import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const SignupDto = z
  .object({
    email: z.string(),
    password: z.string().min(6),
    name: z.string(),
    phone: z.string().optional(),
    organizationId: z.string(),
    role: z.string().optional().default("cashier"),
  })
  .passthrough();
const AuthUserResponseDto = z
  .object({
    id: z.string(),
    email: z.string(),
    name: z.string().optional(),
    phone: z.string().optional(),
    organization_id: z.string().optional(),
    role: z.string().optional(),
    store_id: z.string().optional(),
    active: z.boolean(),
  })
  .passthrough();
const SignupResponseDto = z
  .object({ user: AuthUserResponseDto, message: z.string() })
  .passthrough();
const LoginDto = z
  .object({ email: z.string(), password: z.string() })
  .passthrough();
const SessionResponseDto = z
  .object({
    access_token: z.string(),
    refresh_token: z.string(),
    expires_at: z.number(),
  })
  .partial()
  .passthrough();
const LoginResponseDto = z
  .object({
    user: AuthUserResponseDto,
    session: SessionResponseDto,
    accessToken: z.string(),
    refreshToken: z.string(),
  })
  .passthrough();
const MessageResponseDto = z.object({ message: z.string() }).passthrough();
const RequestPasswordResetDto = z.object({ email: z.string() }).passthrough();
const ResetPasswordDto = z
  .object({ token: z.string(), newPassword: z.string().min(6) })
  .passthrough();
const VerifyEmailDto = z.object({ token: z.string() }).passthrough();
const VerifyEmailResponseDto = z
  .object({ message: z.string(), session: SessionResponseDto.optional() })
  .passthrough();
const ResendVerificationDto = z.object({ email: z.string() }).passthrough();
const CurrencyDto = z
  .object({
    id: z.string(),
    code: z.string(),
    symbol: z.string(),
    shortText: z.string(),
    fullText: z.string(),
    name: z.string(),
    text: z.string(),
    exchangeRate: z.number(),
    currencyDecimalPlaces: z.number(),
    locale: z.string(),
  })
  .partial()
  .passthrough();
const BusinessTypeDto = z
  .object({ id: z.string(), name: z.string(), label: z.string() })
  .partial()
  .passthrough();
const FunnelOptionDto = z
  .object({ id: z.string(), text: z.string() })
  .partial()
  .passthrough();
const FunnelAnswersDto = z
  .object({
    businessSize: z.string(),
    industry: z.string(),
    goals: z.string(),
    referralSource: z.string(),
    orgName: z.string(),
    userName: z.string(),
    phone: z.string(),
    email: z.string(),
    password: z.string(),
    businessType: FunnelOptionDto,
    clientType: FunnelOptionDto,
    monthlySales: FunnelOptionDto,
    inventorySize: FunnelOptionDto,
    currency: CurrencyDto,
    hasBranches: FunnelOptionDto,
    employeeCount: FunnelOptionDto,
    priorExperience: FunnelOptionDto,
    salesReports: z.array(FunnelOptionDto),
    inventoryTrackingNeeds: FunnelOptionDto,
    inventoryOperationNeeds: z.array(FunnelOptionDto),
    barcodeNeeds: FunnelOptionDto,
    wantClientTracking: FunnelOptionDto,
    wantOnlineCatalog: FunnelOptionDto,
    additionalFeatures: z.array(FunnelOptionDto),
    setupOption: FunnelOptionDto,
    trafficSource: FunnelOptionDto,
    loyaltyType: FunnelOptionDto,
    defaultBonusLevel: FunnelOptionDto,
    punchCardCapacity: FunnelOptionDto,
  })
  .partial()
  .passthrough();
const TrafficSourceDto = z
  .object({
    id: z.string(),
    type: z.string(),
    name: z.string(),
    referralCode: z.string(),
  })
  .partial()
  .passthrough();
const SetupOptionDto = z
  .object({ id: z.string(), name: z.string(), value: z.string() })
  .partial()
  .passthrough();
const WhatsappOnboardingConsentDto = z
  .object({
    optIn: z.boolean(),
    optInAt: z.string().optional(),
    locale: z.string().optional(),
    phone: z.string().optional(),
  })
  .passthrough();
const TimezoneDto = z
  .object({
    name: z.string(),
    value: z.string(),
    offset: z.number(),
    abbreviation: z.string(),
    abbr: z.string(),
    isdst: z.boolean(),
    text: z.string(),
    utc: z.array(z.string()),
  })
  .partial()
  .passthrough();
const TimeDto = z
  .object({
    hour: z.number(),
    minute: z.number(),
    id: z.string(),
    text: z.string(),
    value: z.number(),
  })
  .partial()
  .passthrough();
const FiscalOperatorDto = z
  .object({
    id: z.string(),
    text: z.string(),
    name: z.string(),
    path: z.string(),
    type: z.string(),
    enabled: z.boolean(),
  })
  .partial()
  .passthrough();
const StoreSetupDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    timezone: TimezoneDto.optional(),
    autoCloseShiftTime: TimeDto.optional(),
    fiscalOperator: FiscalOperatorDto.optional(),
  })
  .passthrough();
const UserSetupDto = z
  .object({
    firestoreUid: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string().optional(),
    role: z.string(),
    storeFirestoreId: z.string(),
  })
  .passthrough();
const WalletDto = z
  .object({
    firestoreId: z.string(),
    text: z.string(),
    storeFirestoreId: z.string().optional(),
  })
  .passthrough();
const PaymentMethodDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    activeInStore: z.object({}).partial().passthrough().optional(),
    comission: z.number().optional(),
    icon: z.string().optional(),
    iconText: z.string().optional(),
    isCash: z.boolean().optional(),
    destinationWalletForStore: z.object({}).partial().passthrough().optional(),
  })
  .passthrough();
const PurchaseMethodDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    activeInStore: z.object({}).partial().passthrough().optional(),
    icon: z.string().optional(),
    iconText: z.string().optional(),
  })
  .passthrough();
const RoleDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    salaryType: z.string().optional(),
    salary: z.number().optional(),
    permissions: z.object({}).partial().passthrough().optional(),
  })
  .passthrough();
const ProductUnitDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    shortName: z.string().optional(),
  })
  .passthrough();
const DiscountOptionDto = z
  .object({ firestoreId: z.string(), text: z.string() })
  .passthrough();
const SpendingCategoryDto = z
  .object({
    firestoreId: z.string(),
    text: z.string(),
    isAdmin: z.boolean().optional(),
  })
  .passthrough();
const WriteOffReasonDto = z
  .object({ firestoreId: z.string(), text: z.string() })
  .passthrough();
const ReturnReasonDto = z
  .object({ firestoreId: z.string(), text: z.string() })
  .passthrough();
const ProductModifierDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    activeInStore: z.object({}).partial().passthrough().optional(),
    data: z.array(z.object({}).partial().passthrough()).optional(),
  })
  .passthrough();
const LoyaltySettingsDto = z
  .object({
    cashbackEnabled: z.boolean(),
    punchCardEnabled: z.boolean(),
    defaultBonusLevel: z.number(),
    punchCardCapacity: z.number(),
  })
  .partial()
  .passthrough();
const OrganizationSettingsDto = z
  .object({
    defaultCurrency: z.string(),
    language: z.string(),
    dateFormat: z.string(),
    timeFormat: z.string(),
    notificationsEnabled: z.boolean(),
    deliveryPrice: z.number(),
    deliveryPaidByCustomer: z.boolean(),
    deliveryIncludeInReceipt: z.boolean(),
    deliveryPaymentSourceWallet: z.object({}).partial().passthrough(),
    defaultDeliverySpendingCategory: z.object({}).partial().passthrough(),
    showSaveCartForLater: z.boolean(),
    allowMultipleProductSelection: z.boolean(),
    dateFieldVisible: z.boolean(),
    storeFieldVisible: z.boolean(),
    tableNumberFieldVisible: z.boolean(),
    commentFieldVisible: z.boolean(),
    showTotalPurchasePriceInCart: z.boolean(),
    showPurchasePriceInEditCartItem: z.boolean(),
    mergeSimilarItems: z.boolean(),
  })
  .partial()
  .passthrough();
const SaleSettingsDto = z
  .object({
    storeFirestoreId: z.string(),
    settings: OrganizationSettingsDto.optional(),
  })
  .passthrough();
const RecurringSpendingStoreDto = z
  .object({ id: z.string(), name: z.string().optional() })
  .passthrough();
const RecurringSpendingSetupDto = z
  .object({
    firestoreId: z.string().optional(),
    name: z.string(),
    amount: z.number().gte(-99999999.99).lte(99999999.99),
    date: z.string().optional(),
    endDate: z.string().optional(),
    type: z.enum(["daily", "weekly", "monthly"]).optional().default("monthly"),
    store: RecurringSpendingStoreDto.optional(),
    displayOrder: z.number().optional(),
  })
  .passthrough();
const ProductCategoryDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    order: z.number().optional(),
  })
  .passthrough();
const SupplierDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    active: z.boolean().optional(),
  })
  .passthrough();
const ProductDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    activeInStore: z.object({}).partial().passthrough().optional(),
    barcode: z.string().optional(),
    categoryFirestoreId: z.string().optional(),
    supplierFirestoreId: z.string().optional(),
    purchasePrice: z.number().optional(),
    isHiddenInNewSale: z.boolean().optional(),
    salePrices: z.object({}).partial().passthrough().optional(),
    storeStock: z.object({}).partial().passthrough().optional(),
    type: z.boolean().optional(),
  })
  .passthrough();
const ChecklistSetupDto = z
  .object({
    firestoreId: z.string(),
    storeFirestoreId: z.string(),
    checklistType: z.string(),
    content: z.array(z.object({}).partial().passthrough()),
    date: z.string(),
  })
  .partial()
  .passthrough();
const CreateOrganizationDto = z
  .object({
    firestoreId: z.string(),
    name: z.string(),
    locale: z.string().max(35).optional(),
    defaultPaymentMethodSeedVersion: z.number().optional(),
    currency: CurrencyDto.optional(),
    businessType: BusinessTypeDto.optional(),
    pricingModel: z.string().optional(),
    moduleEnabled: z.object({}).partial().passthrough().optional(),
    onboardingChecklist: z
      .array(z.object({}).partial().passthrough())
      .optional(),
    onboardingTasksLeft: z.number().optional(),
    onboardingCompleted: z.boolean().optional(),
    funnelAnswers: FunnelAnswersDto.optional(),
    platform: z.string().optional(),
    trafficSource: TrafficSourceDto.optional(),
    setupOption: SetupOptionDto.optional(),
    whatsappOnboarding: WhatsappOnboardingConsentDto.optional(),
    store: StoreSetupDto,
    user: UserSetupDto,
    wallets: z.array(WalletDto).optional(),
    paymentMethods: z.array(PaymentMethodDto).optional(),
    purchaseMethods: z.array(PurchaseMethodDto).optional(),
    roles: z.array(RoleDto).optional(),
    productUnits: z.array(ProductUnitDto).optional(),
    discountOptions: z.array(DiscountOptionDto).optional(),
    spendingCategories: z.array(SpendingCategoryDto).optional(),
    writeOffReasons: z.array(WriteOffReasonDto).optional(),
    returnReasons: z.array(ReturnReasonDto).optional(),
    productModifiers: z.array(ProductModifierDto).optional(),
    loyaltySettings: LoyaltySettingsDto.optional(),
    saleSettings: z.array(SaleSettingsDto).optional(),
    organizationConstants: z.object({}).partial().passthrough().optional(),
    recurringSpendings: z.array(RecurringSpendingSetupDto).optional(),
    productCategories: z.array(ProductCategoryDto).optional(),
    suppliers: z.array(SupplierDto).optional(),
    products: z.array(ProductDto).optional(),
    checklists: z.array(ChecklistSetupDto).optional(),
  })
  .passthrough();
const CreateOrganizationResponseDto = z.object({}).partial().passthrough();
const OrganizationFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    description: z.string().nullish(),
    currency: z.object({}).partial().passthrough().nullish(),
    phone: z.string().nullish(),
    address: z.string().nullish(),
    businessCategory: z.string().nullish(),
    businessType: z.object({}).partial().passthrough().nullish(),
    linkEnabled: z.boolean().nullish(),
    marketplaceEnabled: z.boolean().nullish(),
    disableNotInStockProducts: z.boolean().nullish(),
    moduleEnabled: z.object({}).partial().passthrough().nullish(),
    storeForMarketplace: z.string().nullish(),
    pricingModel: z.string().nullish(),
    printChecks: z.object({}).partial().passthrough().nullish(),
    onboardingChecklist: z
      .array(z.object({}).partial().passthrough())
      .nullish(),
    onboardingTasksLeft: z.number().nullish(),
    onboardingCompleted: z.boolean().optional(),
    funnelAnswers: z.object({}).partial().passthrough().nullish(),
    platform: z.string().nullish(),
    trafficSource: z.object({}).partial().passthrough().nullish(),
    setupOption: z.object({}).partial().passthrough().nullish(),
    subscriptionStatus: z.string().nullish(),
    subscriptionPlan: z.string().nullish(),
    subscriptionSource: z.string().nullish(),
    subscriptionLastUpdated: z.string().nullish(),
    lastSubscriptionPaymentFailed: z.boolean().nullish(),
    currentMonthSalesUSD: z.number().nullish(),
    liftBlockTill: z.string().nullish(),
    useSupabaseForData: z.boolean().optional(),
    marketplacePriceTypeIds: z.array(z.string()).nullish(),
  })
  .passthrough();
const ResetDataDto = z
  .object({
    cutoffDate: z.string(),
    resetProductStocks: z.boolean().optional().default(false),
    deleteAllProducts: z.boolean().optional().default(false),
  })
  .passthrough();
const ResetDataResponseDto = z
  .object({
    success: z.boolean(),
    message: z.string(),
    counts: z.record(z.number()).optional(),
  })
  .passthrough();
const UpdateOrganizationDto = z
  .object({
    name: z.string(),
    description: z.string().nullable(),
    currency: CurrencyDto.nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    businessCategory: z.string().nullable(),
    businessType: BusinessTypeDto.nullable(),
    moduleEnabled: z.object({}).partial().passthrough().nullable(),
    pricingModel: z.string().nullable(),
    useSupabaseForData: z.boolean(),
  })
  .partial()
  .passthrough();
const UpdateOnboardingDto = z
  .object({
    onboardingChecklist: z.array(z.object({}).partial().passthrough()),
    onboardingTasksLeft: z.number(),
    onboardingCompleted: z.boolean(),
  })
  .partial()
  .passthrough();
const SetSupabaseReadsDto = z.object({ enabled: z.boolean() }).passthrough();
const UpdateBillingUsageDto = z
  .object({ liftBlockTill: z.string().nullable() })
  .partial()
  .passthrough();
const SubscriptionSyncResponseDto = z
  .object({
    applied: z.boolean(),
    supabaseApplied: z.boolean(),
    firestoreApplied: z.boolean(),
    plan: z.string().nullable(),
    status: z.string().nullable(),
    source: z.string().nullable(),
    reason: z.enum([
      "entitlement-applied",
      "entitlement-already-current",
      "free-plan-established",
      "free-plan-already-set",
      "paid-entitlement-on-record",
      "non-production-entitlement-only",
    ]),
  })
  .passthrough();
const OverrideSubscriptionDto = z
  .object({
    plan: z.string().max(128),
    status: z.enum(["active", "past_due", "paused", "deleted"]),
    note: z.string().max(500).optional(),
  })
  .passthrough();
const DualWritePolicyResponseDto = z
  .object({
    dualWriteEnabled: z.string(),
    orgAllowlist: z.string(),
    mirrorIntentOrgAllowlist: z.string(),
    mirrorIntentSbReaders: z.string(),
    templateVersion: z.string().nullable(),
    fetchedAt: z.number(),
  })
  .passthrough();
const ProactiveOptOutDto = z
  .object({
    organizationId: z.string().max(64),
    reason: z.string().max(500).optional(),
  })
  .passthrough();
const WelcomeQuickReplyTapDto = z
  .object({ messageId: z.string(), reply: z.enum(["add_product", "new_sale"]) })
  .passthrough();
const SendMessageDto = z
  .object({ body: z.string(), clientId: z.string() })
  .partial()
  .passthrough();
const RecordAppliedDto = z
  .object({ firestoreId: z.string() })
  .partial()
  .passthrough();
const UpdateConversationDto = z
  .object({
    status: z.enum(["open", "snoozed", "closed"]),
    assigneeType: z.enum(["human_agent", "ai_agent", "unassigned"]),
    assigneeId: z.string(),
  })
  .partial()
  .passthrough();
const ProposeActionDto = z
  .object({
    agentName: z.string(),
    body: z.string(),
    proposal: z.object({}).partial().passthrough(),
  })
  .partial()
  .passthrough();
const TeamReplyDto = z
  .object({
    body: z.string(),
    agentId: z.string(),
    agentName: z.string(),
    authorType: z.enum(["human_agent", "ai_agent"]),
    actionId: z.string(),
  })
  .partial()
  .passthrough();
const WebVisitorMessageDto = z
  .object({
    body: z.string(),
    clientId: z.string().max(100).optional(),
    context: z.object({}).partial().passthrough().optional(),
  })
  .passthrough();
const EnsureWebsiteOrgDto = z.object({ dryRun: z.boolean() }).passthrough();
const WiponSelectionOptionDto = z
  .object({
    id: z.union([z.string(), z.number()]),
    text: z.string(),
    name: z.string(),
  })
  .partial()
  .passthrough();
const WiponCredentialsDto = z
  .object({
    username: z.string(),
    password: z.string(),
    accessToken: z.string(),
    profile: WiponSelectionOptionDto,
    allProfiles: z.array(WiponSelectionOptionDto),
    cashbox: WiponSelectionOptionDto,
    allCashboxes: z.array(WiponSelectionOptionDto),
    salePoint: WiponSelectionOptionDto,
    allSalePoints: z.array(WiponSelectionOptionDto),
    saleSection: WiponSelectionOptionDto,
    allSaleSections: z.array(WiponSelectionOptionDto),
    vatType: z.number(),
    login: z.string(),
    terminalId: z.string(),
    cashRegisterId: z.string(),
  })
  .partial()
  .passthrough();
const LKCredentialsDto = z
  .object({
    cashId: z.number(),
    pin: z.string(),
    username: z.string(),
    password: z.string(),
  })
  .partial()
  .passthrough();
const CreateStoreDto = z
  .object({
    firestoreId: z.string().optional(),
    organizationId: z.string(),
    name: z.string(),
    timezone: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullish()
      .default("Asia/Almaty"),
    businessDayCutoffTime: z.string().optional(),
    inventoryCostingMethod: z
      .enum(["FIFO", "LIFO", "FEFO", "AVERAGE"])
      .optional()
      .default("FIFO"),
    address: z.string().optional(),
    autoCloseShiftTime: TimeDto.optional(),
    showOnTop: z
      .union([z.boolean(), z.object({}).partial().passthrough()])
      .optional(),
    fiscalOperator: FiscalOperatorDto.optional(),
    wiponCredentials: WiponCredentialsDto.optional(),
    LKCredentials: LKCredentialsDto.optional(),
    dailyPercent: z.number().optional(),
    dailyPercentMinValue: z.number().optional(),
  })
  .passthrough();
const StoreFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    text: z.string(),
    address: z.string().nullish(),
    timezone: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullish(),
    businessDayCutoffTime: z.string().nullish(),
    inventoryCostingMethod: z.string().nullish(),
    autoCloseShiftTime: z.object({}).partial().passthrough().nullish(),
    showOnTop: z
      .union([z.boolean(), z.object({}).partial().passthrough()])
      .nullish(),
    fiscalOperator: z.object({}).partial().passthrough().nullish(),
    wiponCredentials: z.object({}).partial().passthrough().nullish(),
    LKCredentials: z.object({}).partial().passthrough().nullish(),
    dailyPercent: z.number().nullish(),
    dailyPercentMinValue: z.number().nullish(),
    isDeleted: z.boolean().nullish(),
    deletedBy: z.string().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
  })
  .passthrough();
const UpdateStoreDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    address: z.string().nullable(),
    timezone: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullable(),
    businessDayCutoffTime: z.string().nullable(),
    inventoryCostingMethod: z.string().nullable(),
    autoCloseShiftTime: TimeDto.nullable(),
    showOnTop: z
      .union([z.boolean(), z.object({}).partial().passthrough()])
      .nullable(),
    fiscalOperator: FiscalOperatorDto.nullable(),
    wiponCredentials: WiponCredentialsDto.nullable(),
    LKCredentials: LKCredentialsDto.nullable(),
    dailyPercent: z.number().nullable(),
    dailyPercentMinValue: z.number().nullable(),
    updatedBy: z.string().nullable(),
  })
  .partial()
  .passthrough();
const DeleteResponseDto = z.object({ message: z.string() }).passthrough();
const ComparisonDifferenceDto = z
  .object({
    field: z.string(),
    supabaseValue: z.object({}).partial().passthrough().nullish(),
    firestoreValue: z.object({}).partial().passthrough().nullish(),
    severity: z.string().optional(),
  })
  .passthrough();
const ComparisonResultDto = z
  .object({
    exists: z.boolean().optional(),
    supabaseData: z.object({}).partial().passthrough().nullish(),
    firestoreData: z.object({}).partial().passthrough().nullish(),
    foundInSupabase: z.boolean(),
    foundInFirestore: z.boolean(),
    matches: z.boolean(),
    differences: z.array(ComparisonDifferenceDto),
  })
  .passthrough();
const CreateProductDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    barcode: z.union([z.string(), z.number()]).nullable(),
    barcodes: z.array(z.string()),
    sku: z.string().nullable(),
    description: z.string(),
    purchasePrice: z.number().nullable(),
    packagePrice: z.number().nullable(),
    salePrices: z.object({}).partial().passthrough(),
    oldPrice: z.number().nullable(),
    priceTypes: z.object({}).partial().passthrough(),
    priceTypeScopes: z.object({}).partial().passthrough(),
    storeStock: z.object({}).partial().passthrough(),
    activeInStore: z.object({}).partial().passthrough(),
    trackInventory: z.boolean(),
    stockThreshold: z.number().nullable(),
    categoryID: z.string().nullable(),
    supplierID: z.string().nullable(),
    unit: z.object({}).partial().passthrough().nullable(),
    productUnit: z.string().nullable(),
    productTags: z.array(z.string()),
    type: z.boolean(),
    isService: z.boolean(),
    isComposite: z.boolean(),
    isHiddenInNewSale: z.boolean(),
    manufacturedBeforeSale: z.boolean(),
    ingredients: z.array(z.object({}).partial().passthrough()),
    hasVariants: z.boolean(),
    isVariant: z.boolean(),
    parentProductId: z.string().nullable(),
    variants: z.array(z.object({}).partial().passthrough()),
    units: z.array(z.object({}).partial().passthrough()),
    activeInEcommerce: z.boolean(),
    defaultQuantity: z.number(),
    punchCardEnabled: z.boolean(),
    imageUrl: z.string().nullable(),
    productImages: z.array(z.object({}).partial().passthrough()),
    importance: z.number(),
    createdDate: z.object({}).partial().passthrough().nullable(),
    lastChangeSource: z.string().nullable(),
    lastChangeType: z.string().nullable(),
  })
  .partial()
  .passthrough();
const ProductFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    barcode: z.union([z.string(), z.number()]).nullish(),
    barcodes: z.array(z.string()).optional(),
    sku: z.string().nullish(),
    description: z.string().optional(),
    purchasePrice: z.number().nullish(),
    packagePrice: z.number().nullish(),
    salePrices: z.object({}).partial().passthrough().optional(),
    oldPrice: z.number().nullish(),
    priceTypes: z.object({}).partial().passthrough().optional(),
    priceTypeScopes: z.object({}).partial().passthrough().optional(),
    storeStock: z.object({}).partial().passthrough().optional(),
    activeInStore: z.object({}).partial().passthrough().optional(),
    trackInventory: z.boolean().optional(),
    stockThreshold: z.number().nullish(),
    categoryID: z.string().nullish(),
    supplierID: z.string().nullish(),
    unit: z.object({}).partial().passthrough().nullish(),
    productUnit: z.string().nullish(),
    productTags: z.array(z.string()).optional(),
    type: z.boolean().optional(),
    isService: z.boolean().optional(),
    isComposite: z.boolean().optional(),
    isHiddenInNewSale: z.boolean().optional(),
    manufacturedBeforeSale: z.boolean().optional(),
    ingredients: z.array(z.object({}).partial().passthrough()).optional(),
    hasVariants: z.boolean().optional(),
    isVariant: z.boolean().optional(),
    parentProductId: z.string().nullish(),
    variants: z.array(z.object({}).partial().passthrough()).optional(),
    units: z.array(z.object({}).partial().passthrough()).optional(),
    activeInEcommerce: z.boolean().optional(),
    defaultQuantity: z.number().optional(),
    punchCardEnabled: z.boolean().optional(),
    imageUrl: z.string().nullish(),
    productImages: z.array(z.object({}).partial().passthrough()).optional(),
    importance: z.number().optional(),
    createdDate: z.object({}).partial().passthrough().nullish(),
    lastChangeSource: z.string().nullish(),
    lastChangeType: z.string().nullish(),
  })
  .passthrough();
const UpdateProductDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    barcode: z.union([z.string(), z.number()]).nullable(),
    barcodes: z.array(z.string()),
    sku: z.string().nullable(),
    description: z.string(),
    purchasePrice: z.number().nullable(),
    packagePrice: z.number().nullable(),
    salePrices: z.object({}).partial().passthrough(),
    oldPrice: z.number().nullable(),
    priceTypes: z.object({}).partial().passthrough(),
    priceTypeScopes: z.object({}).partial().passthrough(),
    storeStock: z.object({}).partial().passthrough(),
    activeInStore: z.object({}).partial().passthrough(),
    trackInventory: z.boolean(),
    stockThreshold: z.number().nullable(),
    categoryID: z.string().nullable(),
    supplierID: z.string().nullable(),
    unit: z.object({}).partial().passthrough().nullable(),
    productUnit: z.string().nullable(),
    productTags: z.array(z.string()),
    type: z.boolean(),
    isService: z.boolean(),
    isComposite: z.boolean(),
    isHiddenInNewSale: z.boolean(),
    manufacturedBeforeSale: z.boolean(),
    ingredients: z.array(z.object({}).partial().passthrough()),
    hasVariants: z.boolean(),
    isVariant: z.boolean(),
    parentProductId: z.string().nullable(),
    variants: z.array(z.object({}).partial().passthrough()),
    units: z.array(z.object({}).partial().passthrough()),
    activeInEcommerce: z.boolean(),
    defaultQuantity: z.number(),
    punchCardEnabled: z.boolean(),
    imageUrl: z.string().nullable(),
    productImages: z.array(z.object({}).partial().passthrough()),
    importance: z.number(),
    createdDate: z.object({}).partial().passthrough().nullable(),
    lastChangeSource: z.string().nullable(),
    lastChangeType: z.string().nullable(),
  })
  .partial()
  .passthrough();
const BulkStockUpdateItemDto = z
  .object({ productId: z.string(), storeId: z.string(), newStock: z.number() })
  .passthrough();
const BulkStockUpdateDto = z
  .object({
    updates: z.array(BulkStockUpdateItemDto),
    reason: z.string().optional(),
  })
  .passthrough();
const BulkStockUpdateResultDto = z
  .object({
    productId: z.string(),
    storeId: z.string(),
    ok: z.boolean(),
    previousStock: z.number().optional(),
    newStock: z.number().optional(),
    error: z.string().optional(),
  })
  .passthrough();
const BulkStockUpdateResponseDto = z
  .object({ results: z.array(BulkStockUpdateResultDto) })
  .passthrough();
const IngredientDto = z
  .object({
    id: z.string(),
    name: z.string(),
    quantity: z.number(),
    purchasePrice: z.number().optional(),
    packagePrice: z.number().optional(),
  })
  .passthrough();
const BulkCostCascadeItemDto = z
  .object({
    productId: z.string(),
    purchasePrice: z.number(),
    ingredients: z.array(IngredientDto).optional(),
  })
  .passthrough();
const BulkCostCascadeDto = z
  .object({
    updates: z.array(BulkCostCascadeItemDto),
    triggeredBy: z.string().optional(),
  })
  .passthrough();
const BulkCostCascadeResultDto = z
  .object({
    productId: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
  })
  .passthrough();
const BulkCostCascadeResponseDto = z
  .object({ results: z.array(BulkCostCascadeResultDto) })
  .passthrough();
const HealMissingProductsDto = z
  .object({
    organizationId: z.string(),
    productFirestoreIds: z.array(z.string().regex(/^[^\/]+$/)),
  })
  .passthrough();
const HealMissingProductResultDto = z
  .object({
    productFirestoreId: z.string(),
    status: z.enum([
      "healed",
      "already-present",
      "soft-deleted",
      "not-in-firestore",
      "refused-missing-store",
      "error",
    ]),
    supabaseId: z.string().optional(),
    error: z.string().optional(),
  })
  .passthrough();
const HealMissingProductsResponseDto = z
  .object({
    results: z.array(HealMissingProductResultDto),
    healed: z.number(),
    alreadyPresent: z.number(),
    softDeleted: z.number(),
    notInFirestore: z.number(),
    refusedMissingStore: z.number(),
    errors: z.number(),
  })
  .passthrough();
const SeedStoreProductSettingsItemDto = z
  .object({
    productId: z.string(),
    salePrice: z.number().optional(),
    active: z.boolean(),
  })
  .passthrough();
const SeedStoreProductSettingsDto = z
  .object({
    storeId: z.string(),
    items: z.array(SeedStoreProductSettingsItemDto),
  })
  .passthrough();
const SeedStoreProductSettingsResultDto = z
  .object({
    productId: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
  })
  .passthrough();
const SeedStoreProductSettingsResponseDto = z
  .object({ results: z.array(SeedStoreProductSettingsResultDto) })
  .passthrough();
const ProductStockUpdateResponseDto = z
  .object({
    message: z.string(),
    previousStock: z.number(),
    newStock: z.number(),
  })
  .passthrough();
const VariantDto = z
  .object({
    name: z.string(),
    value: z.string(),
    price: z.number(),
    sku: z.string(),
  })
  .partial()
  .passthrough();
const CartItemDto = z
  .object({
    productId: z.string().optional(),
    id: z.string().optional(),
    lineType: z.enum(["custom", "delivery"]).optional(),
    name: z.string().optional(),
    quantity: z.number(),
    price: z.number().optional(),
    purchasePrice: z.number().optional(),
    variant: VariantDto.optional(),
    category: z.string().optional(),
    punchCardEnabled: z.boolean().optional(),
    salePrices: z.object({}).partial().passthrough().optional(),
    finalItemPrice: z.number().optional(),
    itemPriceWithoutModifiers: z.number().optional(),
    firestoreId: z.string().optional(),
    supabaseId: z.string().optional(),
    barcode: z.string().optional(),
    unit: z.object({}).partial().passthrough().optional(),
    productUnit: z.string().optional(),
    isService: z.boolean().optional(),
    itemDiscount: z.object({}).partial().passthrough().optional(),
    salePrice: z.number().optional(),
    ref: z.object({}).partial().passthrough().optional(),
    categoryID: z.string().optional(),
    supplierID: z.string().optional(),
    saleID: z.string().optional(),
    cartItemId: z.string().optional(),
    cartItemRef: z.object({}).partial().passthrough().optional(),
    saleDate: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    clientId: z.string().nullish(),
    clientPhone: z.string().nullish(),
    store: z.string().optional(),
    packagePrice: z.number().optional(),
    isComposite: z.boolean().optional(),
    manufacturedBeforeSale: z.boolean().optional(),
    ingredients: z.array(z.object({}).partial().passthrough()).optional(),
    product: z.object({}).partial().passthrough().optional(),
    isReturned: z.boolean().optional(),
    returnedQuantity: z.number().optional(),
    returnedAmount: z.number().optional(),
    returnReason: z.string().optional(),
    organizationId: z.string().optional(),
    description: z.string().optional(),
    priceTypes: z.object({}).partial().passthrough().optional(),
    priceTypeScopes: z.object({}).partial().passthrough().optional(),
    storeStock: z.object({}).partial().passthrough().optional(),
    activeInStore: z.object({}).partial().passthrough().optional(),
    trackInventory: z.boolean().optional(),
    stockThreshold: z.number().optional(),
    barcodes: z.array(z.string()).optional(),
    productTags: z.array(z.object({}).partial().passthrough()).optional(),
    type: z.boolean().optional(),
    isHiddenInNewSale: z.boolean().optional(),
    hasVariants: z.boolean().optional(),
    isVariant: z.boolean().optional(),
    variants: z.array(z.object({}).partial().passthrough()).optional(),
    units: z.array(z.object({}).partial().passthrough()).optional(),
    activeInEcommerce: z.boolean().optional(),
    defaultQuantity: z.number().optional(),
    productImages: z.array(z.object({}).partial().passthrough()).optional(),
    importance: z.number().optional(),
    createdDate: z.object({}).partial().passthrough().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();
const FiscalReceipt = z.object({}).partial().passthrough();
const ReturnedItemDto = z
  .object({
    productId: z.string().optional(),
    name: z.string().optional(),
    quantity: z.number(),
    price: z.number(),
    originalPrice: z.number().optional(),
    refundAmount: z.number().optional(),
    reason: z.string().optional(),
  })
  .passthrough();
const ReturnInfoDto = z
  .object({
    returnId: z.string(),
    returnDate: z.string(),
    date: z.string(),
    returnType: z.number(),
    updateStock: z.boolean(),
    totalReturnedAmount: z.number(),
    returnMethod: z.union([z.string(), z.object({}).partial().passthrough()]),
    returnReason: z.object({}).partial().passthrough(),
    returnComment: z.string(),
    cashier: z.string(),
    store: z.string(),
    organizationId: z.string(),
    comment: z.string(),
    returnedItems: z.array(ReturnedItemDto),
    reason: z.string(),
    totalReturned: z.number(),
    dailyStatID: z.string(),
  })
  .partial()
  .passthrough();
const ModifierDto = z
  .object({ type: z.string(), value: z.number(), name: z.string() })
  .partial()
  .passthrough();
const CreateSaleDto = z
  .object({
    id: z.string().optional(),
    firestoreId: z.string().optional(),
    organizationId: z.string().optional(),
    supabaseId: z.string().optional(),
    ref: z.object({}).partial().passthrough().optional(),
    storeId: z.string().optional(),
    store: z.string().optional(),
    clientId: z.string().optional(),
    cartItems: z.array(CartItemDto),
    totalPrice: z.number().optional(),
    totalCartPrice: z.number().optional(),
    totalPurchasePrice: z.number().optional(),
    totalCartPriceWithPriceModificators: z.number().optional(),
    priceTypes: z.array(z.object({}).partial().passthrough()).optional(),
    totalCartQuantity: z.number().optional(),
    discountPrice: z.number().optional(),
    saleType: z.enum(["product", "service", "mixed"]).optional(),
    paymentMethod: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    secondPaymentMethod: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    secondPaymentMethodValue: z.number().optional(),
    transactionId: z.string().optional(),
    saleDate: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    comment: z.string().optional(),
    tableNumber: z.string().optional(),
    saleSource: z.string().optional(),
    importBatchId: z.string().optional(),
    punchCardReduction: z.number().optional(),
    punchCardEnabled: z.boolean().optional(),
    punchCardCapacity: z.number().optional(),
    dailyStatId: z.string().optional(),
    dailyStatID: z.string().optional(),
    purchaseMethod: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    priceModificators: z.array(z.object({}).partial().passthrough()).optional(),
    finalTotalCartPrice: z.number().optional(),
    discountAmount: z.number().optional(),
    discountPercentage: z.number().optional(),
    bonusAccumulation: z.number().optional(),
    bonusReduction: z.number().optional(),
    deliveryPrice: z.number().optional(),
    deliveryPaidByCustomer: z.boolean().optional(),
    deliveryIncludeInReceipt: z.boolean().optional(),
    priceType: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    fiscalInfo: FiscalReceipt.nullish(),
    returnInfo: ReturnInfoDto.optional(),
    modifier: ModifierDto.optional(),
    modifierComment: z.string().optional(),
    delivery: z.boolean().optional(),
    deliveryPaymentSourceWallet: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    mixedPayment: z.boolean().optional(),
    cashbackEnabled: z.boolean().optional(),
    bonusAction: z.number().optional(),
    returnId: z.string().optional(),
    returnFiscalInfo: FiscalReceipt.nullish(),
    totalDiscount: z.number().optional(),
    totalDiscountPercent: z.number().optional(),
    clientPhone: z.string().optional(),
    cashierUID: z.string().optional(),
    createdDate: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    deliverySpendingId: z.string().optional(),
    firstPaymentMethodValue: z.number().optional(),
    createdBy: z.string().optional(),
    client: z.object({}).partial().passthrough().optional(),
    secondTransactionId: z.string().optional(),
    deliveryAddress: z.string().optional(),
    deliveryComment: z.string().optional(),
    deliveryPhone: z.string().optional(),
    deliveryName: z.string().optional(),
    deliveryTime: z.string().optional(),
    deliveryCourier: z.string().optional(),
  })
  .passthrough();
const CartItemFirestoreResponse = z
  .object({
    id: z.string().nullish(),
    productId: z.string().nullish(),
    name: z.string().nullish(),
    quantity: z.number(),
    price: z.number(),
    salePrice: z.number().nullish(),
    purchasePrice: z.number().nullish(),
    finalItemPrice: z.number().nullish(),
    itemPriceWithoutModifiers: z.number().nullish(),
    packagePrice: z.number().nullish(),
    salePrices: z.object({}).partial().passthrough().nullish(),
    variant: z.object({}).partial().passthrough().nullish(),
    category: z.string().nullish(),
    categoryID: z.string().nullish(),
    supplierID: z.string().nullish(),
    punchCardEnabled: z.boolean().nullish(),
    discountAmount: z.number().nullish(),
    discountPercent: z.number().nullish(),
    barcode: z.string().nullish(),
    productUnit: z.string().nullish(),
    unit: z.object({}).partial().passthrough().nullish(),
    itemDiscount: z.object({}).partial().passthrough().nullish(),
    isComposite: z.boolean().nullish(),
    manufacturedBeforeSale: z.boolean().nullish(),
    ingredients: z.array(z.object({}).partial().passthrough()).nullish(),
    product: z.object({}).partial().passthrough().nullish(),
    isReturned: z.boolean().nullish(),
    returnedQuantity: z.number().nullish(),
    returnedAmount: z.number().nullish(),
    returnReason: z.string().nullish(),
    cartItemId: z.string().nullish(),
    saleID: z.string().nullish(),
    store: z.string().nullish(),
    saleDate: z.object({}).partial().passthrough().nullish(),
    clientId: z.string().nullish(),
    clientPhone: z.string().nullish(),
    lineType: z.enum(["custom", "delivery"]).nullish(),
  })
  .passthrough();
const SaleFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    store: z.string().nullish(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
    clientId: z.string().nullish(),
    client: z.object({}).partial().passthrough().nullish(),
    cashierId: z.string().nullish(),
    cashierUID: z.string().nullish(),
    cashierName: z.string().nullish(),
    cartItems: z.array(CartItemFirestoreResponse),
    totalPrice: z.number(),
    totalCartPrice: z.number().nullish(),
    totalPurchasePrice: z.number().nullish(),
    finalTotalCartPrice: z.number().nullish(),
    profit: z.number().nullish(),
    saleDate: z.string(),
    businessDate: z.string().nullish(),
    saleType: z.enum(["product", "service", "mixed"]).nullish(),
    paymentMethod: z.object({}).partial().passthrough().nullish(),
    paymentMethodId: z.string().nullish(),
    secondPaymentMethod: z.object({}).partial().passthrough().nullish(),
    secondPaymentMethodId: z.string().nullish(),
    secondPaymentMethodValue: z.number().nullish(),
    firstPaymentMethodValue: z.number().nullish(),
    transactionId: z.string().nullish(),
    secondTransactionId: z.string().nullish(),
    discountAmount: z.number().nullish(),
    discountPercentage: z.number().nullish(),
    discountPrice: z.number().nullish(),
    totalCartPriceWithPriceModificators: z.number().nullish(),
    totalDiscount: z.number().nullish(),
    totalDiscountPercent: z.number().nullish(),
    bonusAccumulation: z.number().nullish(),
    bonusReduction: z.number().nullish(),
    bonusAction: z.number().nullish(),
    cashbackEnabled: z.boolean().nullish(),
    punchCardReduction: z.number().nullish(),
    punchCardEnabled: z.boolean().nullish(),
    punchCardCapacity: z.number().nullish(),
    deliveryPrice: z.number().nullish(),
    deliveryPaidByCustomer: z.boolean().nullish(),
    deliveryPaymentSourceWallet: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullish(),
    deliverySpendingId: z.string().nullish(),
    delivery: z.boolean().nullish(),
    deliveryAddress: z.string().nullish(),
    comment: z.string().nullish(),
    tableNumber: z.string().nullish(),
    saleSource: z.string().nullish(),
    importBatchId: z.string().nullish(),
    priceType: z.object({}).partial().passthrough().nullish(),
    priceTypeId: z.string().nullish(),
    purchaseMethod: z.object({}).partial().passthrough().nullish(),
    purchaseMethodId: z.string().nullish(),
    priceModificators: z.array(z.object({}).partial().passthrough()).nullish(),
    clientPhone: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    shiftId: z.string().nullish(),
    modifier: z.object({}).partial().passthrough().nullish(),
    modifierComment: z.string().nullish(),
    fiscalInfo: z.object({}).partial().passthrough().nullish(),
    returnFiscalInfo: z.object({}).partial().passthrough().nullish(),
    returnInfo: z.object({}).partial().passthrough().nullish(),
    returnId: z.string().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
  })
  .passthrough();
const UpdateSaleDto = z
  .object({
    storeName: z.string().nullable(),
    clientId: z.string().nullable(),
    client: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .nullable(),
    cashierName: z.string().nullable(),
    cartItems: z.array(CartItemFirestoreResponse),
    totalPrice: z.number(),
    totalPurchasePrice: z.number().nullable(),
    finalTotalCartPrice: z.number().nullable(),
    saleDate: z.string(),
    paymentMethod: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullable(),
    secondPaymentMethod: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullable(),
    secondPaymentMethodValue: z.number().nullable(),
    firstPaymentMethodValue: z.number().nullable(),
    transactionId: z.string().nullable(),
    secondTransactionId: z.string().nullable(),
    discountAmount: z.number().nullable(),
    discountPercentage: z.number().nullable(),
    discountPrice: z.number().nullable(),
    totalCartPriceWithPriceModificators: z.number().nullable(),
    totalDiscount: z.number().nullable(),
    totalDiscountPercent: z.number().nullable(),
    bonusAccumulation: z.number().nullable(),
    bonusReduction: z.number().nullable(),
    bonusAction: z.number().nullable(),
    cashbackEnabled: z.boolean().nullable(),
    punchCardReduction: z.number().nullable(),
    punchCardEnabled: z.boolean().nullable(),
    punchCardCapacity: z.number().nullable(),
    deliveryPrice: z.number().nullable(),
    deliveryPaidByCustomer: z.boolean().nullable(),
    deliveryPaymentSourceWallet: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullable(),
    deliverySpendingId: z.string().nullable(),
    delivery: z.boolean().nullable(),
    deliveryAddress: z.string().nullable(),
    comment: z.string().nullable(),
    tableNumber: z.string().nullable(),
    saleSource: z.string().nullable(),
    importBatchId: z.string().nullable(),
    priceType: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullable(),
    purchaseMethod: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullable(),
    priceModificators: z.array(z.object({}).partial().passthrough()).nullable(),
    clientPhone: z.string().nullable(),
    modifier: ModifierDto.nullable(),
    modifierComment: z.string().nullable(),
    fiscalInfo: FiscalReceipt.nullable(),
    returnFiscalInfo: FiscalReceipt.nullable(),
    returnInfo: ReturnInfoDto.nullable(),
    returnId: z.string().nullable(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
  })
  .partial()
  .passthrough();
const MarkSalePaidDto = z
  .object({
    transactionId: z.string().max(128),
    paymentMethod: z.union([z.string(), z.object({}).partial().passthrough()]),
    paymentDate: z.string(),
    dailyStatID: z.string().max(128).optional(),
    fiscalInfo: FiscalReceipt.nullish(),
    clientPhone: z.string().optional(),
  })
  .passthrough();
const MarkSalePaidResponseDto = z
  .object({ status: z.enum(["paid", "replayed"]), transactionId: z.string() })
  .passthrough();
const ReturnItemFirestoreResponse = z
  .object({
    productId: z.string().nullish(),
    name: z.string().nullish(),
    quantity: z.number(),
    price: z.number(),
    originalPrice: z.number().nullish(),
    refundAmount: z.number().nullish(),
    reason: z.string().nullish(),
    icon: z.string().nullish(),
    unit: z.string().nullish(),
    category: z.string().nullish(),
    barcode: z.string().nullish(),
    purchasePrice: z.number().nullish(),
    id: z.string().nullish(),
    lineType: z.literal("custom").nullish(),
  })
  .passthrough();
const ReturnFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    saleId: z.string(),
    saleFirestoreId: z.string().nullish(),
    store: z.string().nullish(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
    returnedItems: z.array(ReturnItemFirestoreResponse),
    totalReturnedAmount: z.number(),
    totalPurchaseAmount: z.number().nullish(),
    returnType: z.number().nullish(),
    updateStock: z.boolean().nullish(),
    reason: z.string().nullish(),
    notes: z.string().nullish(),
    refundMethod: z.string().nullish(),
    returnDate: z.string().nullish(),
    cashierId: z.string().nullish(),
    cashierName: z.string().nullish(),
    clientId: z.string().nullish(),
    clientName: z.string().nullish(),
    clientPhone: z.string().nullish(),
    transactionId: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    returnFiscalInfo: z.object({}).partial().passthrough().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
    deletedAt: z.string().nullish(),
    deletedBy: z.string().nullish(),
  })
  .passthrough();
const ReturnItemDto = z
  .object({
    productId: z.string().optional(),
    id: z.string().optional(),
    lineType: z.literal("custom").optional(),
    name: z.string().nullish(),
    quantity: z.number().gte(0),
    price: z.number().gte(0),
    originalPrice: z.number().optional(),
    refundAmount: z.number().gte(0).optional(),
    reason: z.string().nullish(),
    icon: z.string().nullish(),
    unit: z.string().nullish(),
    category: z.string().nullish(),
    barcode: z.string().nullish(),
    purchasePrice: z.number().nullish(),
  })
  .passthrough();
const CreateSaleReturnDto = z
  .object({
    firestoreId: z.string().optional(),
    transactionId: z.string().optional(),
    returnItems: z.array(ReturnItemDto),
    totalRefund: z.number(),
    paymentMethod: z.string().optional(),
    comment: z.string().optional(),
    returnDate: z.string().datetime({ offset: true }).optional(),
    returnFiscalInfo: FiscalReceipt.nullish(),
    returnType: z.number().optional(),
    updateStock: z.boolean().optional(),
    dailyStatID: z.string().optional(),
  })
  .passthrough();
const LostSaleDeletionDto = z
  .object({
    organizationFirestoreId: z
      .string()
      .max(128)
      .regex(/^[A-Za-z0-9_-]+$/),
    saleFirestoreId: z
      .string()
      .max(128)
      .regex(/^[A-Za-z0-9_-]+$/),
    expectedDigest: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
    dryRun: z.boolean(),
  })
  .passthrough();
const StoreRefDto = z
  .object({ id: z.string(), name: z.string(), firestoreId: z.string() })
  .partial()
  .passthrough();
const UserFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    uid: z.string(),
    name: z.string().nullish(),
    text: z.string().nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    role: z.string().nullish(),
    store: StoreRefDto.nullish(),
    active: z.boolean().nullish(),
    pushToken: z.string().nullish(),
    locale: z.string().nullish(),
    salaryCreditedAmount: z.number().nullish(),
    salaryPaidAmount: z.number().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
    deletedAt: z.string().nullish(),
    deletedBy: z.string().nullish(),
  })
  .passthrough();
const CreateUserDto = z
  .object({
    firestoreId: z.string().optional(),
    email: z.string(),
    password: z.string().optional(),
    name: z.string(),
    organizationId: z.string().optional(),
    phone: z.string().optional(),
    role: z.string().optional().default("cashier"),
    storeId: z.string().optional(),
    store: StoreRefDto.optional(),
    active: z.boolean().optional().default(true),
    salaryCreditedAmount: z.number().optional(),
    salaryPaidAmount: z.number().optional(),
    pushToken: z.string().optional(),
    locale: z.string().optional(),
  })
  .passthrough();
const SyncUserResponseDto = z
  .object({
    userId: z.string(),
    organizationId: z.string(),
    storeId: z.string().nullable(),
  })
  .passthrough();
const HealUserFromFirestoreDto = z
  .object({
    firebaseUid: z
      .string()
      .max(128)
      .regex(/^[A-Za-z0-9_-]+$/),
    organizationFirestoreId: z
      .string()
      .max(128)
      .regex(/^[A-Za-z0-9_-]+$/),
    expectedRole: z
      .string()
      .max(128)
      .regex(/^[A-Za-z0-9_-]+$/)
      .optional(),
    expectedPayloadDigest: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
    dryRun: z.boolean(),
  })
  .passthrough();
const HealUserResponseDto = z
  .object({
    dryRun: z.boolean(),
    firebaseUid: z.string(),
    userId: z.string().nullable(),
    organizationId: z.string(),
    storeId: z.string().nullable(),
    payloadDigest: z.string(),
    wouldInsert: z.object({}).partial().passthrough().nullable(),
  })
  .passthrough();
const UserAdminScopeDto = z
  .object({
    organizationId: z.string(),
    organizationFirestoreId: z.string().nullable(),
  })
  .passthrough();
const UserAdminTargetDto = z.object({ firebaseUid: z.string() }).passthrough();
const UpdateUserDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    uid: z.string(),
    name: z.string().nullable(),
    text: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    role: z.string().nullable(),
    store: StoreRefDto.nullable(),
    active: z.boolean().nullable(),
    locale: z.string().nullable(),
    salaryCreditedAmount: z.number().nullable(),
    salaryPaidAmount: z.number().nullable(),
    password: z.string(),
    storeId: z.string(),
  })
  .partial()
  .passthrough();
const UpdatePushTokenDto = z.object({ pushToken: z.string() }).passthrough();
const UpdateEmailDto = z.object({ email: z.string() }).passthrough();
const UpdatePasswordDto = z
  .object({ password: z.string() })
  .partial()
  .passthrough();
const DailyStatsResponseDto = z
  .object({
    id: z.string().nullish(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string().nullish(),
    organization_id: z.string().optional(),
    store_id: z.string().optional(),
    date: z.string(),
    total_sales: z.number().optional(),
    total_revenue: z.number().optional(),
    total_profit: z.number().optional(),
    transaction_count: z.number().optional(),
    average_order_value: z.number().optional(),
    revisionsQuantity: z.number().optional(),
    revisionsAmount: z.number().optional(),
    correctedRevisionsAmount: z.number().optional(),
    negativeRevisionsAmount: z.number().optional(),
    positiveRevisionsAmount: z.number().optional(),
    salaryAmount: z.number().optional(),
    employeeBonusAmount: z.number().optional(),
    workingHoursQuantity: z.number().optional(),
    workingDaysQuantity: z.number().optional(),
    transfersAmount: z.number().optional(),
  })
  .passthrough();
const ShiftReportResponseDto = z
  .object({
    storeId: z.string(),
    supabaseStoreId: z.string(),
    date: z.string(),
    stats: z.record(z.number()),
  })
  .passthrough();
const SalesReportSummaryDto = z
  .object({
    totalSales: z.number(),
    totalRevenue: z.number(),
    totalCost: z.number(),
    totalProfit: z.number(),
    byType: z.record(z.object({}).partial().passthrough()),
    byPaymentMethod: z.record(z.object({}).partial().passthrough()),
    byStore: z.record(z.object({}).partial().passthrough()),
  })
  .passthrough();
const SalesReportResponseDto = z
  .object({
    summary: SalesReportSummaryDto,
    sales: z.array(z.object({}).partial().passthrough()),
  })
  .passthrough();
const InventoryReportSummaryDto = z
  .object({
    totalSupplies: z.number(),
    totalSuppliesAmount: z.number(),
    totalWriteoffs: z.number(),
    totalWriteoffsAmount: z.number(),
    totalRevisions: z.number(),
    totalTransfers: z.number(),
  })
  .passthrough();
const InventoryReportResponseDto = z
  .object({
    summary: InventoryReportSummaryDto,
    supplies: z.array(z.object({}).partial().passthrough()),
    writeoffs: z.array(z.object({}).partial().passthrough()),
    revisions: z.array(z.object({}).partial().passthrough()),
    transfers: z.array(z.object({}).partial().passthrough()),
  })
  .passthrough();
const FinancialReportSummaryDto = z
  .object({
    totalIncome: z.number(),
    totalExpenses: z.number(),
    totalSpendings: z.number(),
    netIncome: z.number(),
    byType: z.record(z.object({}).partial().passthrough()),
    byWallet: z.record(z.object({}).partial().passthrough()),
  })
  .passthrough();
const FinancialReportResponseDto = z
  .object({
    summary: FinancialReportSummaryDto,
    transactions: z.array(z.object({}).partial().passthrough()),
    spendings: z.array(z.object({}).partial().passthrough()),
  })
  .passthrough();
const DualWriteOrganizationDto = z
  .object({
    id: z.string(),
    name: z.string(),
    firestore_id: z.string().optional(),
    use_supabase_for_data: z.boolean(),
    last_active: z.string().optional(),
    stores_count: z.number(),
  })
  .passthrough();
const DualWriteOrganizationsResponseDto = z
  .object({
    organizations: z.array(DualWriteOrganizationDto),
    total: z.number(),
  })
  .passthrough();
const BulkCompareRequestDto = z
  .object({
    storeIds: z.array(z.string()),
    startDate: z.string(),
    endDate: z.string(),
  })
  .passthrough();
const FieldDifferenceDto = z
  .object({
    field: z.string(),
    supabaseValue: z.object({}).partial().passthrough(),
    firestoreValue: z.object({}).partial().passthrough(),
    severity: z.enum(["critical", "warning", "info"]),
    percentDiff: z.number().optional(),
  })
  .passthrough();
const StoreComparisonSummaryDto = z
  .object({
    storeId: z.string(),
    storeName: z.string(),
    date: z.string(),
    status: z.enum([
      "synced",
      "discrepancy",
      "missing_supabase",
      "missing_firestore",
    ]),
    criticalDifferences: z.number(),
    warningDifferences: z.number(),
    infoDifferences: z.number(),
    differences: z.array(FieldDifferenceDto).optional(),
  })
  .passthrough();
const BulkCompareResultDto = z
  .object({
    totalComparisons: z.number(),
    syncedCount: z.number(),
    discrepancyCount: z.number(),
    missingSupabaseCount: z.number(),
    missingFirestoreCount: z.number(),
    comparisons: z.array(StoreComparisonSummaryDto),
  })
  .passthrough();
const SupplyItemDto = z
  .object({
    id: z.string(),
    name: z.string().nullish(),
    quantity: z.number().gte(-999999.9999).lte(999999.9999),
    returnQuantity: z.number().gte(-999999.9999).lte(999999.9999).nullish(),
    purchasePrice: z.number().nullish(),
    salePrice: z.number().nullish(),
    totalPrice: z.number().nullish(),
    unit: z.union([z.string(), z.object({}).partial().passthrough()]).nullish(),
    barcode: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullish(),
    category: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullish(),
    salePrices: z.object({}).partial().passthrough().nullish(),
    priceTypeScopes: z.object({}).partial().passthrough().nullish(),
    type: z.boolean().nullish(),
    productUnit: z.string().nullish(),
    price: z.number().optional(),
    productId: z.string().optional(),
    product: z.object({}).partial().passthrough().optional(),
    ref: z.object({}).partial().passthrough().optional(),
  })
  .passthrough();
const CreateSupplyDto = z
  .object({
    firestoreId: z.string(),
    organizationId: z.string().nullish(),
    storeId: z.string().nullish(),
    store: z.string().nullish(),
    items: z.array(SupplyItemDto),
    totalSum: z.number(),
    totalPrice: z.number().nullish(),
    supplierId: z.string().nullish(),
    supplierID: z.string().nullish(),
    supplierName: z.string().nullish(),
    isPaid: z.boolean().nullish(),
    paymentMethod: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullish(),
    createPaymentTransaction: z.boolean().nullish(),
    supplyDate: z.string().nullish(),
    comment: z.string().nullish(),
    supplyOrderId: z.string().nullish(),
    paymentDate: z.string().nullish(),
    paidBy: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    dailyStatID: z.string().nullish(),
    transactionId: z.string().nullish(),
    cashierUid: z.string().nullish(),
    cashierUID: z.string().nullish(),
    invoiceImage: z.string().nullish(),
    invoiceScanRefs: z.array(z.object({}).partial().passthrough()).nullish(),
    paymentDailyStatId: z.string().nullish(),
    paymentDailyStatID: z.string().nullish(),
    isReturnPresent: z.boolean().nullish(),
    hasPricesUpdated: z.boolean().nullish(),
  })
  .passthrough();
const SupplyItemFirestoreResponse = z
  .object({
    id: z.string().nullish(),
    productId: z.string().nullish(),
    name: z.string().nullish(),
    quantity: z.number(),
    returnQuantity: z.number().nullish(),
    price: z.number().nullish(),
    purchasePrice: z.number().nullish(),
    salePrice: z.number().nullish(),
    totalPrice: z.number().nullish(),
    unit: z.union([z.string(), z.object({}).partial().passthrough()]).nullish(),
    barcode: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullish(),
    category: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .nullish(),
    salePrices: z.record(z.number()).optional(),
    priceTypeScopes: z.record(z.string()).optional(),
    type: z.boolean().optional(),
    productUnit: z.string().optional(),
  })
  .passthrough();
const SupplyFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    store: z.string().nullish(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
    supplierId: z.string().nullish(),
    supplierID: z.string().nullish(),
    supplierName: z.string().nullish(),
    items: z.array(SupplyItemFirestoreResponse),
    totalSum: z.number(),
    totalPrice: z.number().nullish(),
    supplyDate: z.string(),
    paymentDate: z.string().nullish(),
    isPaid: z.boolean().nullish(),
    paymentMethod: z.object({}).partial().passthrough().nullish(),
    createPaymentTransaction: z.boolean().nullish(),
    transactionId: z.string().nullish(),
    paidBy: z.string().nullish(),
    comment: z.string().nullish(),
    invoiceImage: z.string().nullish(),
    invoiceScanRefs: z.array(z.object({}).partial().passthrough()).nullish(),
    cashierUid: z.string().nullish(),
    cashierUID: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    paymentDailyStatId: z.string().nullish(),
    supplyOrderId: z.string().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
    isReturnPresent: z.boolean(),
    hasPricesUpdated: z.boolean(),
  })
  .passthrough();
const SupplyOrderFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string(),
    store: z.string().nullish(),
    supplierId: z.object({}).partial().passthrough().nullish(),
    supplierKey: z.string().nullish(),
    items: z.array(z.object({}).partial().passthrough()),
    totalSum: z.number().nullish(),
    status: z
      .union([z.object({}).partial().passthrough(), z.string()])
      .nullish(),
    isCompleted: z.boolean().nullish(),
    date: z.string().nullish(),
    expectedDeliveryDate: z.string().nullish(),
    supplyDate: z.string().nullish(),
    supplyId: z.string().nullish(),
    notes: z.string().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
  })
  .passthrough();
const SupplyExpectedPaymentDto = z
  .object({
    isPaid: z.boolean(),
    transactionId: z.string().nullable(),
    readFrom: z.enum(["firestore", "supabase"]),
  })
  .passthrough();
const UpdateSupplyDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    store: z.string().nullable(),
    storeId: z.string().nullable(),
    storeName: z.string().nullable(),
    supplierId: z.string().nullable(),
    supplierID: z.string().nullable(),
    supplierName: z.string().nullable(),
    items: z.array(SupplyItemFirestoreResponse),
    totalSum: z.number(),
    totalPrice: z.number().nullable(),
    supplyDate: z.string(),
    paymentDate: z.string().nullable(),
    isPaid: z.boolean().nullable(),
    createPaymentTransaction: z.boolean().nullable(),
    transactionId: z.string().nullable(),
    paidBy: z.string().nullable(),
    comment: z.string().nullable(),
    invoiceImage: z.string().nullable(),
    invoiceScanRefs: z.array(z.object({}).partial().passthrough()).nullable(),
    cashierUid: z.string().nullable(),
    cashierUID: z.string().nullable(),
    dailyStatId: z.string().nullable(),
    paymentDailyStatId: z.string().nullable(),
    supplyOrderId: z.string().nullable(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
    isReturnPresent: z.boolean(),
    hasPricesUpdated: z.boolean(),
    expectedPayment: SupplyExpectedPaymentDto,
    paymentMethod: z.object({}).partial().passthrough(),
  })
  .partial()
  .passthrough();
const CreateSupplyOrderDto = z
  .object({
    firestoreId: z.string().optional(),
    storeId: z.string(),
    supplierId: z.string().optional(),
    items: z.array(z.object({}).partial().passthrough()),
    date: z.string().nullish(),
    status: z
      .union([z.object({}).partial().passthrough(), z.string()])
      .optional(),
    isCompleted: z.boolean().optional(),
    expectedDate: z.string().optional(),
    comment: z.string().optional(),
  })
  .passthrough();
const UpdateSupplyOrderDto = z
  .object({
    items: z.array(z.object({}).partial().passthrough()),
    supplierId: z.string().nullable(),
    date: z.string().nullable(),
    status: z
      .union([z.object({}).partial().passthrough(), z.string()])
      .nullable(),
    isCompleted: z.boolean(),
    expectedDate: z.string(),
    supplyId: z.string(),
    comment: z.string(),
  })
  .partial()
  .passthrough();
const ExternalDestinationDto = z
  .object({
    id: z.string(),
    text: z.string(),
    type: z.string(),
    name: z.string(),
    details: z.string(),
  })
  .partial()
  .passthrough();
const PaymentRangeDto = z
  .object({
    startDate: z.string(),
    endDate: z.string(),
    numberOfPayments: z.number(),
  })
  .partial()
  .passthrough();
const CreateTransactionDto = z
  .object({
    firestoreId: z.string().optional(),
    organizationId: z.string().optional(),
    storeId: z.string().optional(),
    type: z.enum(["income", "expense", "transfer"]),
    value: z.number(),
    sourceWallet: z.string().optional(),
    destinationWallet: z.string().optional(),
    linkedDocId: z.string().optional(),
    linkedDocType: z.string().optional(),
    details: z.string().optional(),
    externalDestination: ExternalDestinationDto.optional(),
    paymentRange: PaymentRangeDto.optional(),
    paidBy: z.string().optional(),
    date: z.string().optional(),
  })
  .passthrough();
const TransactionFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    type: z.string(),
    value: z.number(),
    sourceWallet: z.string().nullish(),
    sourceWalletName: z.string().nullish(),
    destinationWallet: z.string().nullish(),
    destinationWalletName: z.string().nullish(),
    store: z.string().nullish(),
    storeId: z.string().nullish(),
    linkedDocId: z.string().nullish(),
    linkedDocType: z.string().nullish(),
    externalDestination: z.object({}).partial().passthrough().nullish(),
    paymentRange: z.object({}).partial().passthrough().nullish(),
    paidBy: z.object({}).partial().passthrough().nullish(),
    paidByName: z.string().nullish(),
    details: z.string().nullish(),
    date: z.string(),
  })
  .passthrough();
const WalletBalanceResponseDto = z
  .object({ walletId: z.string(), balance: z.number() })
  .passthrough();
const LoyaltyDiscount = z
  .object({
    percentage: z.number(),
    amount: z.number(),
    title: z.string(),
    text: z.string(),
  })
  .partial()
  .passthrough();
const ClientFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    phone: z.string().nullish(),
    phoneNumber: z.string().nullish(),
    email: z.string().nullish(),
    birthday: z.string().nullish(),
    notes: z.string().nullish(),
    address: z.string().nullish(),
    bonusBalance: z.number().nullish(),
    bonusValue: z.number().nullish(),
    punchCardValue: z.number().nullish(),
    punchCardPunches: z.number().nullish(),
    totalPurchases: z.number().nullish(),
    totalPurchasedValue: z.number().nullish(),
    totalVisits: z.number().nullish(),
    lastVisitDate: z.string().nullish(),
    lastPurchaseDate: z.string().nullish(),
    lastPurchaseSource: z.string().nullish(),
    registrationDate: z.string().nullish(),
    registrationSource: z.string().nullish(),
    discountPercent: z.number().nullish(),
    discountType: z.string().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
    deletedAt: z.string().nullish(),
    deletedBy: z.string().nullish(),
    clientType: z.string().nullish(),
    loyaltyDiscount: LoyaltyDiscount.nullish(),
    bonusLevel: z.number().nullish(),
    preferredPriceType: z.string().nullish(),
  })
  .passthrough();
const LoyaltyDiscountDto = z
  .object({
    percentage: z.number(),
    amount: z.number(),
    title: z.string(),
    text: z.string(),
  })
  .partial()
  .passthrough();
const CreateClientDto = z
  .object({
    firestoreId: z.string(),
    organizationId: z.string(),
    name: z.string(),
    displayName: z.string(),
    phone: z.string(),
    phoneNumber: z.string(),
    email: z.string(),
    birthday: z.string(),
    notes: z.string(),
    address: z.string(),
    bonusBalance: z.number(),
    bonusValue: z.number(),
    punchCardValue: z.number(),
    punchCardPunches: z.number(),
    totalPurchases: z.number(),
    totalPurchasedValue: z.number(),
    totalVisits: z.number(),
    lastVisitDate: z.string(),
    lastPurchaseDate: z.string(),
    lastPurchaseSource: z.string(),
    registrationDate: z.string(),
    registrationSource: z.string(),
    discountPercent: z.number(),
    discountType: z.string(),
    dailyStatId: z.string(),
    clientType: z.string(),
    loyaltyDiscount: LoyaltyDiscountDto.nullable(),
    bonusLevel: z.union([z.number(), z.string()]).nullable(),
    preferredPriceType: z.string().nullable(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
    deletedAt: z.string().nullable(),
    deletedBy: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    id: z.string(),
    supabaseId: z.string(),
    ref: z.object({}).partial().passthrough(),
    lastChangeBy: z.string(),
    lastChangeAt: z.object({}).partial().passthrough(),
    lastChangeStore: z.string(),
    dailyStatID: z.string(),
  })
  .partial()
  .passthrough();
const ClientStatsResponseDto = z
  .object({ total: z.number(), new: z.number(), active: z.number() })
  .passthrough();
const ClientComparisonResultDto = z
  .object({
    exists: z.boolean(),
    supabaseData: z.object({}).partial().passthrough().nullish(),
    firestoreData: z.object({}).partial().passthrough().nullish(),
    matches: z.boolean(),
    differences: z.array(FieldDifferenceDto),
    foundInSupabase: z.boolean(),
    foundInFirestore: z.boolean(),
  })
  .passthrough();
const UpdateClientDto = z
  .object({
    firestoreId: z.string(),
    organizationId: z.string(),
    name: z.string(),
    displayName: z.string(),
    phone: z.string(),
    phoneNumber: z.string(),
    email: z.string(),
    birthday: z.string(),
    notes: z.string(),
    address: z.string(),
    bonusBalance: z.number(),
    bonusValue: z.number(),
    punchCardValue: z.number(),
    punchCardPunches: z.number(),
    totalPurchases: z.number(),
    totalPurchasedValue: z.number(),
    totalVisits: z.number(),
    lastVisitDate: z.string(),
    lastPurchaseDate: z.string(),
    lastPurchaseSource: z.string(),
    registrationDate: z.string(),
    registrationSource: z.string(),
    discountPercent: z.number(),
    discountType: z.string(),
    dailyStatId: z.string(),
    clientType: z.string(),
    loyaltyDiscount: LoyaltyDiscountDto.nullable(),
    bonusLevel: z.union([z.number(), z.string()]).nullable(),
    preferredPriceType: z.string().nullable(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
    deletedAt: z.string().nullable(),
    deletedBy: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    id: z.string(),
    supabaseId: z.string(),
    ref: z.object({}).partial().passthrough(),
    lastChangeBy: z.string(),
    lastChangeAt: z.object({}).partial().passthrough(),
    lastChangeStore: z.string(),
    dailyStatID: z.string(),
  })
  .partial()
  .passthrough();
const CreateClientBonusAdjustmentDto = z
  .object({
    operationId: z.string().max(128),
    delta: z.number(),
    date: z.string().optional(),
  })
  .passthrough();
const ClientBonusAdjustmentResponseDto = z
  .object({
    status: z.enum(["applied", "replayed"]),
    operationId: z.string(),
    bonusBefore: z.number().nullable(),
    bonusAfter: z.number().nullable(),
  })
  .passthrough();
const ClientPunchCardResponseDto = z
  .object({
    punchCardValue: z.number(),
    punchCardEnabled: z.boolean(),
    punchCardCapacity: z.number(),
    punchesRemaining: z.number(),
  })
  .passthrough();
const CorrectionReasonDto = z
  .object({ id: z.string(), text: z.string() })
  .partial()
  .passthrough();
const RevisionItemDto = z
  .object({
    productId: z.string(),
    id: z.string(),
    name: z.string(),
    oldStock: z.number(),
    newStock: z.number(),
    inStockOld: z.number(),
    inStock: z.number(),
    previousQuantity: z.number(),
    newQuantity: z.number(),
    purchasePrice: z.number(),
    individualAmount: z.number().nullable(),
    correctedIndividualAmount: z.number().nullable(),
    correctionReason: CorrectionReasonDto.nullable(),
  })
  .partial()
  .passthrough();
const CreateRevisionDto = z
  .object({
    storeId: z.string(),
    firestoreId: z.string().optional(),
    organizationId: z.string().optional(),
    amount: z.number().optional(),
    correctedAmount: z.number().optional(),
    negativeAmount: z.number().optional(),
    positiveAmount: z.number().optional(),
    dailyStatId: z.string().optional(),
    revisionDate: z.string().optional(),
    items: z.array(RevisionItemDto),
  })
  .passthrough();
const RevisionItemFirestoreResponse = z
  .object({
    productId: z.string().nullish(),
    name: z.string().nullish(),
    oldStock: z.number(),
    newStock: z.number(),
    difference: z.number().nullish(),
    purchasePrice: z.number().nullish(),
    unit: z.string().nullish(),
    individualAmount: z.number().nullish(),
    correctedIndividualAmount: z.number().nullish(),
    correctionReason: z.object({}).partial().passthrough().nullish(),
    id: z.string().nullish(),
    previousStock: z.number().optional(),
    actualStock: z.number().optional(),
    inStock: z.number().optional(),
    inStockOld: z.number().optional(),
    ref: z.object({}).partial().passthrough().nullish(),
  })
  .passthrough();
const RevisionFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
    items: z.array(RevisionItemFirestoreResponse),
    amount: z.number().nullish(),
    correctedAmount: z.number().nullish(),
    negativeAmount: z.number().nullish(),
    positiveAmount: z.number().nullish(),
    revisionDate: z.string(),
    createdBy: z.string().nullish(),
    createdByName: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    deletedAt: z.string().nullish(),
    deletedBy: z.string().nullish(),
    store: z.string().nullish(),
    date: z.string(),
    cashierUID: z.string().nullish(),
    dailyStatID: z.string().optional(),
    totalDifferenceAmount: z.number().nullish(),
    totalDifferencePositive: z.number().nullish(),
    totalDifferenceNegative: z.number().nullish(),
  })
  .passthrough();
const UpdateRevisionDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    storeId: z.string().nullable(),
    storeName: z.string().nullable(),
    amount: z.number().nullable(),
    correctedAmount: z.number().nullable(),
    negativeAmount: z.number().nullable(),
    positiveAmount: z.number().nullable(),
    revisionDate: z.string(),
    createdBy: z.string().nullable(),
    createdByName: z.string().nullable(),
    dailyStatId: z.string().nullable(),
    deletedAt: z.string().nullable(),
    deletedBy: z.string().nullable(),
    store: z.string().nullable(),
    date: z.string(),
    cashierUID: z.string().nullable(),
    dailyStatID: z.string(),
    totalDifferenceAmount: z.number().nullable(),
    totalDifferencePositive: z.number().nullable(),
    totalDifferenceNegative: z.number().nullable(),
    items: z.array(RevisionItemFirestoreResponse),
  })
  .partial()
  .passthrough();
const TransferItemDto = z
  .object({
    productId: z.string(),
    quantity: z.number(),
    name: z.string().optional(),
  })
  .passthrough();
const CreateTransferDto = z
  .object({
    firestoreId: z.string().optional(),
    fromStoreId: z.string(),
    toStoreId: z.string(),
    items: z.array(TransferItemDto),
    amount: z.number().optional(),
    transferDate: z.string().optional(),
    fromDailyStatId: z.string().optional(),
    toDailyStatId: z.string().optional(),
    organizationId: z.string().optional(),
  })
  .passthrough();
const TransferStoreRefFirestoreResponse = z
  .object({ id: z.string() })
  .passthrough();
const TransferItemFirestoreResponse = z
  .object({
    productId: z.string().nullish(),
    name: z.string().nullish(),
    quantity: z.number(),
    price: z.number().nullish(),
  })
  .passthrough();
const TransferFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    fromStoreId: z.string().nullish(),
    toStoreId: z.string().nullish(),
    fromStoreName: z.string().nullish(),
    toStoreName: z.string().nullish(),
    fromStore: TransferStoreRefFirestoreResponse.nullish(),
    toStore: TransferStoreRefFirestoreResponse.nullish(),
    items: z.array(TransferItemFirestoreResponse),
    amount: z.number().nullish(),
    transferDate: z.string(),
    createdBy: z.string().nullish(),
    deletedAt: z.string().nullish(),
    deletedBy: z.string().nullish(),
  })
  .passthrough();
const WriteoffItemDto = z
  .object({
    productId: z.string(),
    id: z.string(),
    quantity: z.number().gte(-999999.9999).lte(999999.9999),
    amount: z.number(),
    reason: z.string(),
    name: z.string(),
    purchasePrice: z.number(),
  })
  .partial()
  .passthrough();
const CreateWriteoffDto = z
  .object({
    storeId: z.string(),
    firestoreId: z.string().optional(),
    organizationId: z.string().optional(),
    totalSum: z.number().optional(),
    dailyStatId: z.string().optional(),
    writeoffDate: z.string().optional(),
    items: z.array(WriteoffItemDto),
  })
  .passthrough();
const WriteoffItemFirestoreResponse = z
  .object({
    productId: z.string().nullish(),
    name: z.string().nullish(),
    quantity: z.number(),
    amount: z.number().nullish(),
    reason: z.string().nullish(),
  })
  .passthrough();
const WriteoffProductFirestoreResponse = z
  .object({ id: z.string().nullable(), name: z.string().nullable() })
  .partial()
  .passthrough();
const WriteoffReasonFirestoreResponse = z
  .object({
    id: z.string().nullable(),
    firestoreId: z.string().nullable(),
    name: z.string().nullable(),
    text: z.string().nullable(),
  })
  .partial()
  .passthrough();
const WriteoffFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    store: z.string().nullish(),
    items: z.array(WriteoffItemFirestoreResponse),
    totalAmount: z.number().nullish(),
    product: WriteoffProductFirestoreResponse.nullish(),
    quantity: z.number().nullish(),
    totalSum: z.number().nullish(),
    reason: WriteoffReasonFirestoreResponse.nullish(),
    writeoffDate: z.string(),
    date: z.string().nullish(),
    createdBy: z.string().nullish(),
    cashierUID: z.string().nullish(),
    cashierId: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    storeName: z.string().nullish(),
    reasonName: z.string().nullish(),
    createdByName: z.string().nullish(),
  })
  .passthrough();
const PaymentMethodRefDto = z
  .object({ id: z.string(), name: z.string(), type: z.string() })
  .partial()
  .passthrough();
const CreateSpendingDto = z
  .object({
    storeId: z.string(),
    firestoreId: z.string().optional(),
    organizationId: z.string().optional(),
    name: z.string(),
    amount: z.number(),
    category: z.string().optional(),
    details: z.string().optional(),
    paymentMethod: PaymentMethodRefDto.optional(),
    source: z.object({}).partial().passthrough().optional(),
    isRecurring: z.boolean().optional(),
    recurringPeriod: z.string().optional(),
    spendingDate: z.string().optional(),
    dailyStatId: z.string().optional(),
    transactionId: z.string().optional(),
    spendingType: z.number().optional(),
    type: z.number().optional(),
  })
  .passthrough();
const SpendingFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    name: z.string(),
    details: z.string().nullish(),
    amount: z.number(),
    category: z.object({}).partial().passthrough().nullish(),
    paymentMethod: z.object({}).partial().passthrough().nullish(),
    source: z.object({}).partial().passthrough().nullish(),
    transactionId: z.string().nullish(),
    isRecurring: z.boolean().nullish(),
    recurringPeriod: z.string().nullish(),
    spendingDate: z.string(),
    date: z.string().nullish(),
    store: z.string().nullish(),
    type: z.number().nullish(),
    spendingType: z.number().nullish(),
    createdBy: z.string().nullish(),
    cashierUID: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    storeName: z.string().nullish(),
    categoryName: z.string().nullish(),
    paymentMethodName: z.string().nullish(),
    paymentMethodIcon: z.string().nullish(),
    paymentMethodIsCash: z.boolean().nullish(),
    createdByName: z.string().nullish(),
  })
  .passthrough();
const OrderItemDto = z
  .object({
    productId: z.string(),
    quantity: z.number(),
    price: z.number(),
    name: z.string().optional(),
  })
  .passthrough();
const CreateOrderDto = z
  .object({
    storeId: z.string(),
    firestoreId: z.string().optional(),
    clientName: z.string().optional(),
    clientPhone: z.string().optional(),
    clientEmail: z.string().optional(),
    items: z.array(OrderItemDto),
    totalPrice: z.number(),
    paymentMethod: z.string().optional(),
    deliveryAddress: z.string().optional(),
    notes: z.string().optional(),
  })
  .passthrough();
const OrderItemFirestoreResponse = z
  .object({
    productId: z.string().nullish(),
    name: z.string().nullish(),
    quantity: z.number(),
    price: z.number().nullish(),
    variant: z.object({}).partial().passthrough().nullish(),
  })
  .passthrough();
const OrderFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
    clientName: z.string().nullish(),
    clientEmail: z.string().nullish(),
    clientPhone: z.string().nullish(),
    deliveryAddress: z.string().nullish(),
    name: z.string().nullish(),
    phone: z.string().nullish(),
    date: z.string().nullish(),
    items: z.array(OrderItemFirestoreResponse),
    cart: z.array(z.object({}).partial().passthrough()).nullish(),
    totalPrice: z.number(),
    status: z
      .union([z.object({}).partial().passthrough(), z.string()])
      .nullish(),
    isProcessed: z.boolean().nullish(),
    paymentMethod: z.string().nullish(),
    notes: z.string().nullish(),
  })
  .passthrough();
const UpdateOrderDto = z
  .object({
    status: z.union([z.object({}).partial().passthrough(), z.string()]),
    storeId: z.string(),
    clientName: z.string(),
    clientPhone: z.string(),
    clientEmail: z.string(),
    deliveryAddress: z.string(),
    items: z.array(z.object({}).partial().passthrough()),
    totalPrice: z.number(),
    paymentMethod: z.string(),
    notes: z.string(),
  })
  .partial()
  .passthrough();
const OrderCountResponseDto = z.object({}).partial().passthrough();
const PublicOrderItemDto = z
  .object({
    productId: z.string(),
    quantity: z.number(),
    price: z.number(),
    name: z.string().optional(),
  })
  .passthrough();
const CreatePublicOrderDto = z
  .object({
    organizationId: z.string(),
    suppressNotification: z.boolean().optional(),
    storeId: z.string().optional(),
    clientName: z.string().optional(),
    clientPhone: z.string().optional(),
    name: z.string().optional(),
    phone: z.string().optional(),
    clientEmail: z.string().optional(),
    items: z.array(PublicOrderItemDto),
    totalPrice: z.number().optional(),
    firestoreId: z.string().optional(),
    notes: z.string().optional(),
  })
  .passthrough();
const CreateSupplierDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    contactPerson: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    paymentTerms: z.string().nullable(),
    active: z.boolean().nullable(),
    notes: z.string().nullable(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
  })
  .partial()
  .passthrough();
const SupplierFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    text: z.string(),
    contactPerson: z.string().nullish(),
    phone: z.string().nullish(),
    email: z.string().nullish(),
    address: z.string().nullish(),
    paymentTerms: z.string().nullish(),
    active: z.boolean().nullish(),
    notes: z.string().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
  })
  .passthrough();
const UpdateSupplierDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    contactPerson: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    paymentTerms: z.string().nullable(),
    active: z.boolean().nullable(),
    notes: z.string().nullable(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
  })
  .partial()
  .passthrough();
const CreateCategoryDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    description: z.string().nullable(),
    imageUrl: z.string().nullable(),
    parentCategoryId: z.string().nullable(),
    parentId: z.string().nullable(),
    order: z.number().nullable(),
    displayOrder: z.number().nullable(),
    level: z.number().nullable(),
    path: z.string().nullable(),
    marketplaceDisabled: z.boolean().nullable(),
  })
  .partial()
  .passthrough();
const ProductCategoryFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    text: z.string().optional(),
    description: z.string().nullish(),
    imageUrl: z.string().nullish(),
    parentCategoryId: z.string().nullish(),
    parentId: z.string().nullish(),
    order: z.number().nullish(),
    displayOrder: z.number().nullish(),
    level: z.number().nullish(),
    path: z.string().nullish(),
    marketplaceDisabled: z.boolean().nullish(),
  })
  .passthrough();
const UpdateCategoryDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    description: z.string().nullable(),
    imageUrl: z.string().nullable(),
    parentCategoryId: z.string().nullable(),
    parentId: z.string().nullable(),
    order: z.number().nullable(),
    displayOrder: z.number().nullable(),
    level: z.number().nullable(),
    path: z.string().nullable(),
    marketplaceDisabled: z.boolean().nullable(),
    color: z.string(),
  })
  .partial()
  .passthrough();
const CreateWorkingHoursDto = z
  .object({
    firestoreId: z.string().optional(),
    storeId: z.string().optional(),
    employeeId: z.string(),
    role: z.string().optional(),
    roleDetails: z.object({}).partial().passthrough().optional(),
    salaryType: z.enum(["hour", "day", "month"]).optional(),
    amount: z.number().optional(),
    date: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    startHour: TimeDto.optional(),
    endHour: TimeDto.optional(),
    employeeBonusAmount: z.number().optional(),
    dailyStatId: z.string().optional(),
    monthlyStatId: z.string().optional(),
  })
  .passthrough();
const WorkingHoursFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
    employeeId: z.string().nullish(),
    employeeID: z.string().nullish(),
    store: z.string().nullish(),
    employeeName: z.string().nullish(),
    date: z.string().nullish(),
    startDate: z.string().nullish(),
    endDate: z.string().nullish(),
    startHour: z.object({}).partial().passthrough().nullish(),
    endHour: z.object({}).partial().passthrough().nullish(),
    amount: z.number().nullish(),
    employeeBonusAmount: z.number().nullish(),
    role: z.string().nullish(),
    roleDetails: z.object({}).partial().passthrough().nullish(),
    salaryType: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    monthlyStatId: z.string().nullish(),
  })
  .passthrough();
const ProductMovementFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string() }).partial().passthrough().optional(),
    organizationId: z.string(),
    store: z.string(),
    productID: z.string(),
    date: z.string(),
    type: z.string(),
    previousInStock: z.number(),
    increment: z.number(),
    linkedDocId: z.string().optional(),
    linkedDocType: z.string().optional(),
    linkedDocDate: z.string().optional(),
    clientSaveDate: z.string().optional(),
    comment: z.string().optional(),
  })
  .passthrough();
const ProductVariantDto = z
  .object({
    name: z.string(),
    value: z.string(),
    price: z.number(),
    sku: z.string(),
    barcode: z.string(),
  })
  .partial()
  .passthrough();
const SavedCartItemDto = z
  .object({
    productId: z.string(),
    name: z.string(),
    quantity: z.union([z.string(), z.number()]),
    price: z.union([z.string(), z.number()]),
    salePrices: z.object({}).partial().passthrough(),
    salePrice: z.number(),
    priceTypeScopes: z.object({}).partial().passthrough(),
    variant: ProductVariantDto,
    purchasePrice: z.number(),
    finalItemPrice: z.number(),
    category: z.string(),
    punchCardEnabled: z.boolean(),
    discountAmount: z.number(),
    discountPercent: z.number(),
    isComposite: z.boolean(),
    manufacturedBeforeSale: z.boolean(),
    ingredients: z.array(z.object({}).partial().passthrough()),
    itemDiscount: z.object({}).partial().passthrough(),
    packagePrice: z.number(),
    itemPriceWithoutModifiers: z.number(),
    firestoreId: z.string(),
    supabaseId: z.string(),
    categoryID: z.string(),
    supplierID: z.string(),
    barcode: z.string(),
    unit: z.object({}).partial().passthrough(),
    productUnit: z.string(),
    isService: z.boolean(),
    id: z.string(),
  })
  .partial()
  .passthrough();
const CreateSavedCartDto = z
  .object({
    firestoreId: z.string().optional(),
    organizationId: z.string(),
    storeId: z.string().optional(),
    clientId: z.string().optional(),
    items: z.array(SavedCartItemDto).optional(),
    cartItems: z.array(SavedCartItemDto).optional(),
    name: z.string().optional(),
    tableNumber: z.string().optional(),
    comment: z.string().optional(),
    discountSelectedIndex: z.number().optional(),
    discountAmount: z.number().optional(),
    bonusAction: z.number().optional(),
    selectedPriceType: z.object({}).partial().passthrough().optional(),
    finalTotalCartPrice: z.number().optional(),
    client: z.object({}).partial().passthrough().optional(),
    paymentMethod: z.object({}).partial().passthrough().optional(),
    delivery: z.object({}).partial().passthrough().optional(),
    deliveryPrice: z.number().optional(),
    deliveryPaidByCustomer: z.boolean().optional(),
    deliveryAddress: z.string().optional(),
    deliveryIncludeInReceipt: z.boolean().optional(),
    deliveryPaymentSourceWallet: z
      .object({})
      .partial()
      .passthrough()
      .optional(),
    store: z.string().optional(),
    cashierUID: z.string().optional(),
    dateCreated: z.object({}).partial().passthrough().optional(),
    dateModified: z.object({}).partial().passthrough().optional(),
  })
  .passthrough();
const SavedCartFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    store: z.string().nullish(),
    storeName: z.string().nullish(),
    clientId: z.string().nullish(),
    cashierId: z.string().nullish(),
    cashierUID: z.string().nullish(),
    name: z.string().nullish(),
    items: z.array(CartItemFirestoreResponse),
    cartItems: z.array(CartItemFirestoreResponse).optional(),
    tableNumber: z.string().nullish(),
    comment: z.string().nullish(),
    discountSelectedIndex: z.number().nullish(),
    discountAmount: z.number().nullish(),
    bonusAction: z.number().nullish(),
    selectedPriceType: z.object({}).partial().passthrough().nullish(),
    finalTotalCartPrice: z.number().nullish(),
    client: z.object({}).partial().passthrough().nullish(),
    dateCreated: z.string().nullish(),
    paymentMethod: z.object({}).partial().passthrough().nullish(),
    delivery: z.boolean().nullish(),
    deliveryPrice: z.number().nullish(),
    deliveryPaidByCustomer: z.boolean().nullish(),
    deliveryAddress: z.string().nullish(),
    deliveryIncludeInReceipt: z.boolean().nullish(),
    deliveryPaymentSourceWallet: z
      .union([
        z.string(),
        z.object({ id: z.string(), text: z.string() }).partial().passthrough(),
      ])
      .nullish(),
  })
  .passthrough();
const UpdateSavedCartDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    storeId: z.string().nullable(),
    store: z.string().nullable(),
    storeName: z.string().nullable(),
    clientId: z.string().nullable(),
    cashierId: z.string().nullable(),
    cashierUID: z.string().nullable(),
    name: z.string().nullable(),
    items: z.array(CartItemFirestoreResponse),
    cartItems: z.array(CartItemFirestoreResponse),
    tableNumber: z.string().nullable(),
    comment: z.string().nullable(),
    discountSelectedIndex: z.number().nullable(),
    discountAmount: z.number().nullable(),
    bonusAction: z.number().nullable(),
    selectedPriceType: z.object({}).partial().passthrough().nullable(),
    finalTotalCartPrice: z.number().nullable(),
    client: z.object({}).partial().passthrough().nullable(),
    dateCreated: z.string().nullable(),
    paymentMethod: z.object({}).partial().passthrough().nullable(),
    delivery: z.boolean().nullable(),
    deliveryPrice: z.number().nullable(),
    deliveryPaidByCustomer: z.boolean().nullable(),
    deliveryAddress: z.string().nullable(),
    deliveryIncludeInReceipt: z.boolean().nullable(),
    deliveryPaymentSourceWallet: z
      .union([
        z.string(),
        z.object({ id: z.string(), text: z.string() }).partial().passthrough(),
      ])
      .nullable(),
  })
  .partial()
  .passthrough();
const PaymentMethodResponseDto = z
  .object({
    id: z.string(),
    name: z.string(),
    value: z.string().optional(),
    description: z.string().optional(),
    is_default: z.boolean().optional(),
    order: z.number().optional(),
    icon: z.string().optional(),
    is_cash: z.boolean().optional(),
  })
  .passthrough();
const ConstantItemResponseDto = z
  .object({
    id: z.string(),
    name: z.string(),
    value: z.string().optional(),
    description: z.string().optional(),
    is_default: z.boolean().optional(),
    order: z.number().optional(),
  })
  .passthrough();
const RoleResponseDto = z
  .object({
    id: z.string(),
    name: z.string(),
    value: z.string().optional(),
    description: z.string().optional(),
    is_default: z.boolean().optional(),
    order: z.number().optional(),
    permissions: z.object({}).partial().passthrough().optional(),
    role_bonus_type: z.string().optional(),
    individual_sales_percent: z.number().optional(),
    min_individual_sales: z.number().optional(),
  })
  .passthrough();
const ProductUnitResponseDto = z
  .object({
    id: z.string(),
    name: z.string(),
    value: z.string().optional(),
    description: z.string().optional(),
    is_default: z.boolean().optional(),
    order: z.number().optional(),
    abbreviation: z.string().optional(),
  })
  .passthrough();
const WalletResponseDto = z
  .object({
    id: z.string(),
    name: z.string(),
    value: z.string().optional(),
    description: z.string().optional(),
    is_default: z.boolean().optional(),
    order: z.number().optional(),
    balance: z.number().optional(),
    currency: z.string().optional(),
  })
  .passthrough();
const DiscountOptionResponseDto = z
  .object({
    id: z.string(),
    name: z.string(),
    value: z.string().optional(),
    description: z.string().optional(),
    is_default: z.boolean().optional(),
    order: z.number().optional(),
    type: z.string().optional(),
    amount: z.number().optional(),
  })
  .passthrough();
const SpendingCategoryResponseDto = z
  .object({
    id: z.string(),
    firestore_id: z.string().nullish(),
    text: z.string(),
    is_admin: z.boolean().nullish(),
    organization_id: z.string().nullish(),
    display_order: z.number().nullish(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
  })
  .passthrough();
const SaleSettingsResponseDto = z
  .object({
    allow_negative_stock: z.boolean(),
    require_client: z.boolean(),
    default_payment_method: z.string(),
    tax_rate: z.number(),
    tax_included: z.boolean(),
  })
  .partial()
  .passthrough();
const LoyaltySettingsResponseDto = z
  .object({
    enabled: z.boolean(),
    points_per_amount: z.number(),
    points_value: z.number(),
    min_points_redeem: z.number(),
  })
  .partial()
  .passthrough();
const RecurringSpendingResponseDto = z
  .object({
    id: z.string(),
    name: z.string(),
    amount: z.number().optional(),
    category: z.string().optional(),
    frequency: z.string().optional(),
    next_date: z.string().optional(),
  })
  .passthrough();
const AllConstantsResponseDto = z
  .object({
    paymentMethods: z.array(PaymentMethodResponseDto),
    purchaseMethods: z.array(ConstantItemResponseDto),
    roles: z.array(RoleResponseDto),
    productUnits: z.array(ProductUnitResponseDto),
    wallets: z.array(WalletResponseDto),
    discountOptions: z.array(DiscountOptionResponseDto),
    spendingCategories: z.array(SpendingCategoryResponseDto),
    adminSpendingCategories: z.array(SpendingCategoryResponseDto),
    writeOffReasons: z.array(ConstantItemResponseDto),
    returnReasons: z.array(ConstantItemResponseDto),
    productModifiers: z.array(ConstantItemResponseDto),
    saleSettings: z.record(SaleSettingsResponseDto),
    loyaltySettings: LoyaltySettingsResponseDto.nullish(),
    recurringSpendings: z.array(RecurringSpendingResponseDto),
    printChecks: z.object({}).partial().passthrough().nullish(),
    floatingMenu: z.object({}).partial().passthrough().nullish(),
    stickerHeight: z.object({}).partial().passthrough().nullish(),
  })
  .passthrough();
const UpsertOrganizationConstantDto = z
  .object({ document: z.object({}).partial().passthrough() })
  .passthrough();
const OrganizationConstantResponseDto = z
  .object({
    key: z.enum(["floatingMenu", "printChecks", "stickerHeight"]),
    document: z.object({}).partial().passthrough(),
  })
  .passthrough();
const CreatePaymentMethodDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    icon: z.string().nullable(),
    iconText: z.string().nullable(),
    isCash: z.boolean().nullable(),
    commission: z.number().nullable(),
    comission: z.number().nullable(),
    activeInStore: z.object({}).partial().passthrough().nullable(),
    destinationWalletForStore: z.object({}).partial().passthrough().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const PaymentMethodFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    icon: z.string().nullish(),
    iconText: z.string().nullish(),
    isCash: z.boolean().nullish(),
    commission: z.number().nullish(),
    comission: z.number().nullish(),
    activeInStore: z.object({}).partial().passthrough().nullish(),
    destinationWalletForStore: z.object({}).partial().passthrough().nullish(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdatePaymentMethodDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    icon: z.string().nullable(),
    iconText: z.string().nullable(),
    isCash: z.boolean().nullable(),
    commission: z.number().nullable(),
    comission: z.number().nullable(),
    activeInStore: z.object({}).partial().passthrough().nullable(),
    destinationWalletForStore: z.object({}).partial().passthrough().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeletePaymentMethodResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreatePurchaseMethodDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    icon: z.string().nullable(),
    iconText: z.string().nullable(),
    activeInStore: z.object({}).partial().passthrough().nullable(),
    priceModificators: z.array(z.object({}).partial().passthrough()).nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const PurchaseMethodFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    text: z.string().optional(),
    icon: z.string().nullish(),
    iconText: z.string().nullish(),
    activeInStore: z.object({}).partial().passthrough().nullish(),
    priceModificators: z.array(z.object({}).partial().passthrough()).nullish(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdatePurchaseMethodDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    icon: z.string().nullable(),
    iconText: z.string().nullable(),
    activeInStore: z.object({}).partial().passthrough().nullable(),
    priceModificators: z.array(z.object({}).partial().passthrough()).nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeletePurchaseMethodResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateRoleDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    salary: z.number().nullable(),
    salaryType: z.string().nullable(),
    roleBonusType: z.string().nullable(),
    individualSalesPercent: z.number().nullable(),
    minIndividualSales: z.number().nullable(),
    permissions: z.object({}).partial().passthrough().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const RoleFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    salary: z.number().nullish(),
    salaryType: z.string().nullish(),
    roleBonusType: z.string().nullish(),
    individualSalesPercent: z.number().nullish(),
    minIndividualSales: z.number().nullish(),
    permissions: z.object({}).partial().passthrough().nullish(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdateRoleDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    salary: z.number().nullable(),
    salaryType: z.string().nullable(),
    roleBonusType: z.string().nullable(),
    individualSalesPercent: z.number().nullable(),
    minIndividualSales: z.number().nullable(),
    permissions: z.object({}).partial().passthrough().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeleteRoleResponseDto = z.object({ message: z.string() }).passthrough();
const CreateProductUnitDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    shortName: z.string().nullable(),
    text: z.string().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const ProductUnitFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    shortName: z.string().nullish(),
    text: z.string().nullish(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdateProductUnitDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    shortName: z.string().nullable(),
    text: z.string().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeleteProductUnitResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateWalletDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    type: z.string().nullable(),
    storeId: z.string().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const WalletFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    text: z.string().optional(),
    type: z.string().nullish(),
    storeId: z.string().nullish(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdateWalletDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    type: z.string().nullable(),
    storeId: z.string().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeleteWalletResponseDto = z.object({ message: z.string() }).passthrough();
const CreateDiscountOptionDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    text: z.string(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DiscountOptionFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    text: z.string(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdateDiscountOptionDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    text: z.string(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeleteDiscountOptionResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateSpendingCategoryDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    isAdmin: z.boolean().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const SpendingCategoryFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    text: z.string(),
    isAdmin: z.boolean().nullish(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdateSpendingCategoryDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    isAdmin: z.boolean().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeleteSpendingCategoryResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateWriteOffReasonDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const WriteOffReasonFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    text: z.string(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdateWriteOffReasonDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeleteWriteOffReasonResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateReturnReasonDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const ReturnReasonFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    text: z.string(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdateReturnReasonDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    text: z.string(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeleteReturnReasonResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateProductModifierDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    data: z
      .union([z.array(z.any()), z.object({}).partial().passthrough()])
      .nullable(),
    activeInStore: z.object({}).partial().passthrough().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const ProductModifierFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    data: z
      .union([z.array(z.any()), z.object({}).partial().passthrough()])
      .nullish(),
    activeInStore: z.object({}).partial().passthrough().nullish(),
    displayOrder: z.number().nullish(),
  })
  .passthrough();
const UpdateProductModifierDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    data: z
      .union([z.array(z.any()), z.object({}).partial().passthrough()])
      .nullable(),
    activeInStore: z.object({}).partial().passthrough().nullable(),
    displayOrder: z.number().nullable(),
  })
  .partial()
  .passthrough();
const DeleteProductModifierResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateSaleSettingsDto = z
  .object({
    organizationId: z.string().nullable(),
    storeId: z.string(),
    deliveryPrice: z.number().nullable(),
    deliveryPaidByCustomer: z.boolean().nullable(),
    deliveryIncludeInReceipt: z.boolean().nullable(),
    deliveryPaymentSourceWallet: z
      .union([
        z.string(),
        z.object({ id: z.string(), text: z.string() }).partial().passthrough(),
      ])
      .nullable(),
    defaultDeliverySpendingCategory: z
      .union([
        z.string(),
        z.object({ id: z.string(), text: z.string() }).partial().passthrough(),
      ])
      .nullable(),
    showSaveCartForLater: z.boolean().nullable(),
    allowMultipleProductSelection: z.boolean().nullable(),
    dateFieldVisible: z.boolean().nullable(),
    storeFieldVisible: z.boolean().nullable(),
    tableNumberFieldVisible: z.boolean().nullable(),
    commentFieldVisible: z.boolean().nullable(),
    mergeSimilarItems: z.boolean().nullable(),
    showPurchasePriceInEditCartItem: z.boolean().nullable(),
    showTotalPurchasePriceInCart: z.boolean().nullable(),
    settings: z.object({}).partial().passthrough().nullable(),
  })
  .partial()
  .passthrough();
const SaleSettingsFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string() }).partial().passthrough().nullish(),
    organizationId: z.string().nullish(),
    storeId: z.string(),
    deliveryPrice: z.number().nullish(),
    deliveryPaidByCustomer: z.boolean().nullish(),
    deliveryIncludeInReceipt: z.boolean().nullish(),
    deliveryPaymentSourceWallet: z
      .union([
        z.string(),
        z.object({ id: z.string(), text: z.string() }).partial().passthrough(),
      ])
      .nullish(),
    defaultDeliverySpendingCategory: z
      .union([
        z.string(),
        z.object({ id: z.string(), text: z.string() }).partial().passthrough(),
      ])
      .nullish(),
    showSaveCartForLater: z.boolean().nullish(),
    allowMultipleProductSelection: z.boolean().nullish(),
    dateFieldVisible: z.boolean().nullish(),
    storeFieldVisible: z.boolean().nullish(),
    tableNumberFieldVisible: z.boolean().nullish(),
    commentFieldVisible: z.boolean().nullish(),
    mergeSimilarItems: z.boolean().nullish(),
    showPurchasePriceInEditCartItem: z.boolean().nullish(),
    showTotalPurchasePriceInCart: z.boolean().nullish(),
    settings: z.object({}).partial().passthrough().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
  })
  .passthrough();
const UpdateSaleSettingsDto = z
  .object({
    id: z.string(),
    organizationId: z.string().nullable(),
    deliveryPrice: z.number().nullable(),
    deliveryPaidByCustomer: z.boolean().nullable(),
    deliveryIncludeInReceipt: z.boolean().nullable(),
    deliveryPaymentSourceWallet: z
      .union([
        z.string(),
        z.object({ id: z.string(), text: z.string() }).partial().passthrough(),
      ])
      .nullable(),
    defaultDeliverySpendingCategory: z
      .union([
        z.string(),
        z.object({ id: z.string(), text: z.string() }).partial().passthrough(),
      ])
      .nullable(),
    showSaveCartForLater: z.boolean().nullable(),
    allowMultipleProductSelection: z.boolean().nullable(),
    dateFieldVisible: z.boolean().nullable(),
    storeFieldVisible: z.boolean().nullable(),
    tableNumberFieldVisible: z.boolean().nullable(),
    commentFieldVisible: z.boolean().nullable(),
    mergeSimilarItems: z.boolean().nullable(),
    showPurchasePriceInEditCartItem: z.boolean().nullable(),
    showTotalPurchasePriceInCart: z.boolean().nullable(),
    settings: z.object({}).partial().passthrough().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
  })
  .partial()
  .passthrough();
const DeleteSaleSettingsResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateLoyaltySettingsDto = z
  .object({
    organizationId: z.string().nullable(),
    cashbackEnabled: z.boolean().nullable(),
    bonusPercentage: z.number().nullable(),
    bonusBurnPercentage: z.number().nullable(),
    defaultBonusLevel: z.number().nullable(),
    punchCardEnabled: z.boolean().nullable(),
    punchCardCapacity: z.number().nullable(),
    punchesRequired: z.number().nullable(),
    punchRewardType: z.string().nullable(),
  })
  .partial()
  .passthrough();
const LoyaltySettingsFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string() }).partial().passthrough().nullish(),
    organizationId: z.string().nullish(),
    cashbackEnabled: z.boolean().nullish(),
    bonusPercentage: z.number().nullish(),
    bonusBurnPercentage: z.number().nullish(),
    defaultBonusLevel: z.number().nullish(),
    punchCardEnabled: z.boolean().nullish(),
    punchCardCapacity: z.number().nullish(),
    punchesRequired: z.number().nullish(),
    punchRewardType: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
  })
  .passthrough();
const UpdateLoyaltySettingsDto = z
  .object({
    id: z.string(),
    organizationId: z.string().nullable(),
    cashbackEnabled: z.boolean().nullable(),
    bonusPercentage: z.number().nullable(),
    bonusBurnPercentage: z.number().nullable(),
    defaultBonusLevel: z.number().nullable(),
    punchCardEnabled: z.boolean().nullable(),
    punchCardCapacity: z.number().nullable(),
    punchesRequired: z.number().nullable(),
    punchRewardType: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
  })
  .partial()
  .passthrough();
const DeleteLoyaltySettingsResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateRecurringSpendingDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    amount: z.number(),
    type: z.string().nullable(),
    storeId: z.string().nullable(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    displayOrder: z.number().nullable(),
    date: z.string(),
    store: z.object({}).partial().passthrough().nullable(),
  })
  .partial()
  .passthrough();
const RecurringSpendingFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    amount: z.number(),
    type: z.string().nullish(),
    storeId: z.string().nullish(),
    startDate: z.string(),
    endDate: z.string().nullish(),
    displayOrder: z.number().nullish(),
    date: z.string(),
    store: z.object({}).partial().passthrough().nullish(),
  })
  .passthrough();
const UpdateRecurringSpendingDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    amount: z.number(),
    type: z.string().nullable(),
    storeId: z.string().nullable(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    displayOrder: z.number().nullable(),
    date: z.string(),
    store: z.object({}).partial().passthrough().nullable(),
  })
  .partial()
  .passthrough();
const DeleteRecurringSpendingResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const CreateChecklistDto = z
  .object({
    firestoreId: z.string().optional(),
    storeId: z.string(),
    checklistType: z.string().optional(),
    content: z.array(z.object({}).partial().passthrough()),
    date: z.string().optional(),
  })
  .passthrough();
const ChecklistItemDto = z
  .object({
    id: z.string(),
    text: z.string(),
    checked: z.boolean(),
    order: z.number(),
  })
  .partial()
  .passthrough();
const ChecklistFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
    date: z.string(),
    checklistType: z.string().nullish(),
    content: z.array(ChecklistItemDto).nullish(),
    dailyStatId: z.string().nullish(),
  })
  .passthrough();
const UpdateChecklistDto = z
  .object({
    content: z.array(z.object({}).partial().passthrough()),
    checklistType: z.string(),
  })
  .partial()
  .passthrough();
const ManufactureIngredientDto = z
  .object({
    productId: z.string(),
    quantity: z.number(),
    name: z.string().optional(),
    purchasePrice: z.number().nullish(),
    packagePrice: z.number().nullish(),
    productUnit: z.string().optional(),
  })
  .passthrough();
const CreateManufactureDto = z
  .object({
    firestoreId: z.string().optional(),
    storeId: z.string(),
    productId: z.string(),
    quantity: z.number(),
    totalSum: z.number().optional(),
    ingredients: z.array(ManufactureIngredientDto),
    manufactureDate: z.string().optional(),
    batchNumber: z.string().max(100).optional(),
    manufactureSource: z.literal("import").optional(),
    importBatchId: z.string().max(64).optional(),
    dailyStatId: z.string().optional(),
  })
  .passthrough();
const ManufactureProductFirestoreResponse = z
  .object({ id: z.string().nullable(), name: z.string().nullable() })
  .partial()
  .passthrough();
const ManufactureIngredientFirestoreResponse = z
  .object({
    id: z.string().nullish(),
    productId: z.string().nullish(),
    name: z.string().nullish(),
    quantity: z.number(),
    purchasePrice: z.number().nullish(),
    packagePrice: z.number().nullish(),
    productUnit: z.string().optional(),
    price: z.number().nullish(),
  })
  .passthrough();
const ManufactureFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    store: z.string().nullish(),
    storeName: z.string().nullish(),
    productId: z.string().nullish(),
    product: ManufactureProductFirestoreResponse.nullish(),
    date: z.string(),
    quantity: z.number(),
    totalSum: z.number(),
    ingredients: z.array(ManufactureIngredientFirestoreResponse),
    createdBy: z.string().nullish(),
    cashierUID: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    dailyStatID: z.string().optional(),
    productName: z.string().nullish(),
    createdByName: z.string().nullish(),
    batchNumber: z.string().nullish(),
    manufactureSource: z.string().nullish(),
    importBatchId: z.string().nullish(),
  })
  .passthrough();
const PrintTemplateFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    type: z.string(),
    settings: z.object({}).partial().passthrough().nullish(),
    store: z.string().nullish(),
    content: z.object({}).partial().passthrough().nullish(),
    stores: z.array(z.string()).nullish(),
    isDefault: z.boolean().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
  })
  .passthrough();
const CreatePrintTemplateDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    type: z.string(),
    settings: z.object({}).partial().passthrough().nullable(),
    store: z.string().nullable(),
    content: z.object({}).partial().passthrough().nullable(),
    stores: z.array(z.string()).nullable(),
    isDefault: z.boolean().nullable(),
  })
  .partial()
  .passthrough();
const UpdatePrintTemplateDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    type: z.string(),
    settings: z.object({}).partial().passthrough().nullable(),
    store: z.string().nullable(),
    content: z.object({}).partial().passthrough().nullable(),
    stores: z.array(z.string()).nullable(),
    isDefault: z.boolean().nullable(),
  })
  .partial()
  .passthrough();
const CashboxLinkedTransactionDto = z
  .object({
    firestoreId: z.string(),
    value: z.number(),
    sourceWallet: z.string().optional(),
    destinationWallet: z.string().optional(),
    date: z.string().optional(),
  })
  .passthrough();
const CreateCashboxTransactionDto = z
  .object({
    firestoreId: z.string().optional(),
    storeId: z.string(),
    type: z.enum(["openShift", "closeShift", "deposit", "withdrawal"]),
    amount: z.number().optional(),
    wallet: z
      .union([z.string(), z.object({}).partial().passthrough()])
      .optional(),
    transactionId: z.string().optional(),
    date: z.string().optional(),
    endAmounts: z.object({}).partial().passthrough().optional(),
    systemEndAmounts: z.object({}).partial().passthrough().optional(),
    openId: z.string().optional(),
    transaction: CashboxLinkedTransactionDto.optional(),
  })
  .passthrough();
const CashboxTransactionFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string(),
    store: z.string(),
    storeName: z.string().nullish(),
    type: z.object({}).partial().passthrough().nullable(),
    amount: z.number(),
    wallet: z.object({}).partial().passthrough().nullish(),
    transactionId: z.string().nullish(),
    date: z.string(),
    createdBy: z.object({}).partial().passthrough().nullish(),
    endAmounts: z.object({}).partial().passthrough().nullish(),
    systemEndAmounts: z.object({}).partial().passthrough().nullish(),
    openId: z.string().nullish(),
  })
  .passthrough();
const UpdateCashboxTransactionDto = z
  .object({
    amount: z.number(),
    endAmounts: z.object({}).partial().passthrough(),
    systemEndAmounts: z.object({}).partial().passthrough(),
  })
  .partial()
  .passthrough();
const ComplexSaleTemplateFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    templateName: z.string().nullish(),
    items: z.array(z.object({}).partial().passthrough()).nullish(),
  })
  .passthrough();
const CreateComplexSaleTemplateDto = z
  .object({
    name: z.string(),
    firestoreId: z.string(),
    templateName: z.string(),
    items: z.array(z.object({}).partial().passthrough()),
  })
  .partial()
  .passthrough();
const UpdateComplexSaleTemplateDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    templateName: z.string().nullable(),
    items: z.array(z.object({}).partial().passthrough()).nullable(),
  })
  .partial()
  .passthrough();
const ComplexSaleFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    storeId: z.string().nullish(),
    store: z.string().nullish(),
    name: z.string().nullish(),
    templateName: z.string().nullish(),
    status: z
      .union([z.object({}).partial().passthrough(), z.string()])
      .nullish(),
    isActive: z.boolean().nullish(),
    items: z.array(z.object({}).partial().passthrough()).nullish(),
    payments: z.array(z.object({}).partial().passthrough()).nullish(),
    comments: z.array(z.object({}).partial().passthrough()).nullish(),
    statusUpdates: z.array(z.object({}).partial().passthrough()).nullish(),
    updates: z.array(z.object({}).partial().passthrough()).nullish(),
    finalPrice: z.number().nullish(),
    finalExpensesPrice: z.number().nullish(),
    totalPrice: z.number().nullish(),
    deposit: z.number().nullish(),
    clientId: z.string().nullish(),
    client: z.object({}).partial().passthrough().nullish(),
    createdBy: z.string().nullish(),
    date: z.string().nullish(),
    createdDate: z.string().nullish(),
    dailyStatId: z.string().nullish(),
    transactionId: z.string().nullish(),
    secondTransactionId: z.string().nullish(),
    comment: z.string().nullish(),
  })
  .passthrough();
const ComplexSaleLinkedTransactionDto = z
  .object({
    firestoreId: z.string(),
    value: z.number(),
    destinationWallet: z.string().optional(),
    date: z.string().optional(),
  })
  .passthrough();
const CreateComplexSaleDto = z
  .object({
    storeId: z.string(),
    firestoreId: z.string().optional(),
    organizationId: z.string().optional(),
    clientId: z.string().optional(),
    name: z.string().optional(),
    templateName: z.string().optional(),
    status: z
      .union([z.object({}).partial().passthrough(), z.string()])
      .optional(),
    comment: z.string().optional(),
    createdBy: z.string().optional(),
    finalPrice: z.number().optional(),
    finalExpensesPrice: z.number().optional(),
    deposit: z.number().optional(),
    date: z.string().optional(),
    createdDate: z.string().optional(),
    dailyStatId: z.string().optional(),
    dailyStatID: z.string().optional(),
    totalPrice: z.number().optional(),
    items: z.array(z.object({}).partial().passthrough()),
    transactionId: z.string().optional(),
    secondTransactionId: z.string().optional(),
    payments: z.array(z.object({}).partial().passthrough()).optional(),
    comments: z.array(z.object({}).partial().passthrough()).optional(),
    statusUpdates: z.array(z.object({}).partial().passthrough()).optional(),
    updates: z.array(z.object({}).partial().passthrough()).optional(),
    transactions: z.array(ComplexSaleLinkedTransactionDto).optional(),
  })
  .passthrough();
const UpdateComplexSaleDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    storeId: z.string().nullable(),
    store: z.string().nullable(),
    name: z.string().nullable(),
    templateName: z.string().nullable(),
    status: z
      .union([z.object({}).partial().passthrough(), z.string()])
      .nullable(),
    isActive: z.boolean().nullable(),
    items: z.array(z.object({}).partial().passthrough()).nullable(),
    payments: z.array(z.object({}).partial().passthrough()).nullable(),
    comments: z.array(z.object({}).partial().passthrough()).nullable(),
    statusUpdates: z.array(z.object({}).partial().passthrough()).nullable(),
    updates: z.array(z.object({}).partial().passthrough()).nullable(),
    finalPrice: z.number().nullable(),
    finalExpensesPrice: z.number().nullable(),
    totalPrice: z.number().nullable(),
    deposit: z.number().nullable(),
    clientId: z.string().nullable(),
    client: z.object({}).partial().passthrough().nullable(),
    createdBy: z.string().nullable(),
    date: z.string().nullable(),
    createdDate: z.string().nullable(),
    dailyStatId: z.string().nullable(),
    transactionId: z.string().nullable(),
    secondTransactionId: z.string().nullable(),
    comment: z.string().nullable(),
  })
  .partial()
  .passthrough();
const CreateAuditLogDto = z
  .object({
    tableName: z.string(),
    recordId: z.string(),
    operation: z.string(),
    oldValues: z.object({}).partial().passthrough().optional(),
    newValues: z.object({}).partial().passthrough().optional(),
    metadata: z.object({}).partial().passthrough().optional(),
  })
  .passthrough();
const AuditLogFirestoreResponse = z
  .object({
    id: z.string(),
    organizationId: z.string().nullish(),
    userId: z.string().nullish(),
    tableName: z.string(),
    operation: z.string(),
    recordId: z.string(),
    oldValues: z.object({}).partial().passthrough().nullish(),
    newValues: z.object({}).partial().passthrough().nullish(),
    metadata: z.object({}).partial().passthrough().nullish(),
    createdAt: z.string().nullish(),
  })
  .passthrough();
const ClientOperationFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    clientId: z.string(),
    operationType: z.string(),
    operationValue: z.number().nullish(),
    date: z.string().nullish(),
    userId: z.string().nullish(),
    bonusBefore: z.number().nullish(),
    bonusAfter: z.number().nullish(),
    linkedDocId: z.string().nullish(),
    linkedDocType: z.string().nullish(),
    comment: z.string().nullish(),
  })
  .passthrough();
const CreateClientOperationDto = z
  .object({
    clientId: z.string(),
    operationType: z.enum(["bonus_add", "bonus_use"]),
    amount: z.number().gte(0),
    createdByFirestoreId: z.string().optional(),
  })
  .passthrough();
const LegacyClientOperationResponse = z
  .object({
    id: z.string(),
    client_id: z.string(),
    operation_type: z.string(),
    amount: z.number().nullish(),
    balance_after: z.number().nullish(),
    reference_id: z.string().nullish(),
    reference_type: z.string().nullish(),
    organization_id: z.string().nullish(),
    created_at: z.string().nullish(),
  })
  .passthrough();
const CreateUpdateDto = z
  .object({
    firestoreId: z.string().optional(),
    type: z.string(),
    cashierUid: z.string().optional(),
    cashierUID: z.string().optional(),
    date: z.string().optional(),
    dataBefore: z.object({}).partial().passthrough().nullish(),
    dataAfter: z.object({}).partial().passthrough().nullish(),
  })
  .passthrough();
const UpdateFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    type: z.string(),
    cashierUid: z.string().nullish(),
    date: z.string().nullish(),
    dataBefore: z.object({}).partial().passthrough().nullish(),
    dataAfter: z.object({}).partial().passthrough().nullish(),
  })
  .passthrough();
const CreatePriceTypeDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    scope: z.string().nullable(),
    icon: z.string().nullable(),
    displayOrder: z.number().nullable(),
    autoRules: z.object({}).partial().passthrough().nullable(),
    calculationRules: z.object({}).partial().passthrough().nullable(),
  })
  .partial()
  .passthrough();
const PriceTypeFirestoreResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    ref: z
      .object({ id: z.string(), path: z.string() })
      .partial()
      .passthrough()
      .optional(),
    organizationId: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    name: z.string(),
    scope: z.string().nullish(),
    icon: z.string().nullish(),
    displayOrder: z.number().nullish(),
    autoRules: z.object({}).partial().passthrough().nullish(),
    calculationRules: z.object({}).partial().passthrough().nullish(),
  })
  .passthrough();
const UpdatePriceTypeDto = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullable(),
    supabaseId: z.string(),
    ref: z.object({ id: z.string(), path: z.string() }).partial().passthrough(),
    organizationId: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    name: z.string(),
    scope: z.string().nullable(),
    icon: z.string().nullable(),
    displayOrder: z.number().nullable(),
    autoRules: z.object({}).partial().passthrough().nullable(),
    calculationRules: z.object({}).partial().passthrough().nullable(),
  })
  .partial()
  .passthrough();
const DeletePriceTypeResponseDto = z
  .object({ message: z.string() })
  .passthrough();
const MarketplaceSettingsFirestoreResponse = z
  .object({
    id: z.string(),
    supabaseId: z.string(),
    organizationId: z.string().nullish(),
    marketplaceEnabled: z.boolean().nullish(),
    linkEnabled: z.boolean().nullish(),
    description: z.string().nullish(),
    storeForMarketplace: z.string().nullish(),
    disableNotInStockProducts: z.boolean().nullish(),
    marketplacePriceTypeIds: z.array(z.string()).nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
  })
  .passthrough();
const UpdateMarketplaceSettingsDto = z
  .object({
    id: z.string(),
    organizationId: z.string().nullable(),
    marketplaceEnabled: z.boolean().nullable(),
    linkEnabled: z.boolean().nullable(),
    description: z.string().nullable(),
    storeForMarketplace: z.string().nullable(),
    disableNotInStockProducts: z.boolean().nullable(),
    marketplacePriceTypeIds: z.array(z.string()).nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    enabledCategories: z.object({}).partial().passthrough(),
  })
  .partial()
  .passthrough();
const MarketplaceStorefrontResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    supabaseId: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    currency: z.object({}).partial().passthrough().nullish(),
    storeForMarketplace: z.string().nullish(),
    disableNotInStockProducts: z.boolean().nullish(),
    marketplacePriceTypeIds: z.array(z.string()).nullish(),
    linkEnabled: z.boolean().nullish(),
    useSupabaseForData: z.boolean().optional(),
  })
  .passthrough();
const MarketplaceProductResponse = z
  .object({
    id: z.string(),
    firestoreId: z.string().nullish(),
    name: z.string(),
    categoryID: z.string().nullish(),
    activeInStore: z.object({}).partial().passthrough().optional(),
    storeStock: z.object({}).partial().passthrough().optional(),
    salePrices: z.object({}).partial().passthrough().optional(),
    oldPrice: z.number().nullish(),
    priceTypeScopes: z.object({}).partial().passthrough().optional(),
    productImages: z.array(z.object({}).partial().passthrough()).optional(),
    productUnit: z.string().nullish(),
    defaultQuantity: z.number().optional(),
    type: z.boolean().optional(),
    productTags: z.array(z.string()).optional(),
  })
  .passthrough();
const OrgAuditDto = z
  .object({
    organizationId: z.string(),
    collections: z.array(z.string()).optional(),
    maxRowsPerCollection: z.number().gte(1).lte(5000).optional(),
    cursor: z.string().optional(),
    maxSamples: z.number().gte(1).lte(200).optional(),
  })
  .passthrough();
const FleetAuditDto = z
  .object({
    orgIds: z.array(z.string()),
    collections: z.array(z.string()),
    activeSinceDays: z.number().gte(1).lte(365),
    fullBaseline: z.boolean(),
    maxOrgs: z.number().gte(1).lte(500),
    startAfterOrg: z.string(),
    maxRowsPerCollection: z.number().gte(1).lte(5000),
    budgetMs: z.number().gte(1000).lte(1800000),
    maxSamples: z.number().gte(1).lte(200),
  })
  .partial()
  .passthrough();
const ApiTokensAvailabilityDto = z
  .object({
    available: z.boolean(),
    reason: z
      .enum([
        "organization_not_on_supabase",
        "organization_access_undetermined",
      ])
      .nullish(),
  })
  .passthrough();
const ApiTokenCreatorDto = z
  .object({
    id: z.string(),
    firebaseUid: z.string().nullish(),
    name: z.string().nullish(),
  })
  .passthrough();
const OAuthClientDto = z
  .object({
    name: z.string().nullish(),
    kind: z.enum(["claude", "chatgpt", "local", "other"]),
    host: z.string(),
  })
  .passthrough();
const ApiTokenDto = z
  .object({
    id: z.string(),
    name: z.string(),
    tokenPrefix: z.string(),
    scopes: z.array(
      z.enum([
        "read",
        "read:products",
        "read:stock",
        "read:sales",
        "write:sales",
        "write:products",
        "write:danger",
        "draft:sales",
        "draft:acceptances",
      ])
    ),
    status: z.enum(["active", "expired", "revoked"]),
    createdAt: z.string(),
    expiresAt: z.string(),
    lastUsedAt: z.string().nullish(),
    revokedAt: z.string().nullish(),
    createdBy: ApiTokenCreatorDto,
    revokedBy: ApiTokenCreatorDto.nullish(),
    kind: z.enum(["manual", "oauth"]),
    client: OAuthClientDto.nullish(),
    revokedReason: z
      .enum([
        "authorization_code_reuse",
        "refresh_token_reuse",
        "client_revocation",
      ])
      .nullish(),
  })
  .passthrough();
const ApiTokensListDto = z
  .object({
    availability: ApiTokensAvailabilityDto,
    issuableScopes: z.array(
      z.enum([
        "read",
        "read:products",
        "read:stock",
        "read:sales",
        "write:sales",
        "write:products",
        "write:danger",
        "draft:sales",
        "draft:acceptances",
      ])
    ),
    allScopes: z.array(
      z.enum([
        "read",
        "read:products",
        "read:stock",
        "read:sales",
        "write:sales",
        "write:products",
        "write:danger",
        "draft:sales",
        "draft:acceptances",
      ])
    ),
    tokens: z.array(ApiTokenDto),
  })
  .passthrough();
const CreateApiTokenDto = z
  .object({
    name: z.string().max(100),
    scopes: z.array(
      z.enum([
        "read",
        "read:products",
        "read:stock",
        "read:sales",
        "write:sales",
        "write:products",
        "write:danger",
        "draft:sales",
        "draft:acceptances",
      ])
    ),
    expiresInDays: z.number().gte(1).lte(365),
  })
  .passthrough();
const CreatedApiTokenDto = z
  .object({ token: ApiTokenDto, secret: z.string() })
  .passthrough();
const RevokedApiTokenDto = z
  .object({ token: ApiTokenDto, alreadyRevoked: z.boolean() })
  .passthrough();
const ApiAuditUserRefDto = z
  .object({
    id: z.string(),
    firebaseUid: z.string().nullish(),
    name: z.string().nullish(),
  })
  .passthrough();
const ApiAuditTokenRefDto = z
  .object({ id: z.string(), name: z.string(), tokenPrefix: z.string() })
  .passthrough();
const ApiAuditEventDto = z
  .object({
    id: z.string(),
    occurredAt: z.string(),
    actorType: z.enum(["user", "agent"]),
    channel: z.enum(["app", "api", "mcp"]),
    action: z.string(),
    targetType: z.string().nullish(),
    targetId: z.string().nullish(),
    outcome: z.enum(["success", "denied", "error"]),
    httpStatus: z.number().nullish(),
    user: ApiAuditUserRefDto.nullish(),
    token: ApiAuditTokenRefDto.nullish(),
    details: z.object({}).partial().passthrough(),
  })
  .passthrough();
const ApiAuditEventsDto = z
  .object({ items: z.array(ApiAuditEventDto) })
  .passthrough();
const ApiDraftStoreDto = z
  .object({ id: z.string(), name: z.string().nullish() })
  .passthrough();
const ApiDraftLineDto = z
  .object({
    productId: z.string(),
    name: z.string().nullish(),
    quantity: z.number(),
    purchasePrice: z.number().optional(),
  })
  .passthrough();
const ApiDraftProposerDto = z
  .object({
    tokenId: z.string(),
    tokenName: z.string().nullish(),
    name: z.string().nullish(),
  })
  .passthrough();
const ApiDraftDeciderDto = z
  .object({ name: z.string().nullable() })
  .partial()
  .passthrough();
const ApiDraftAppliedDto = z
  .object({
    storeId: z.string(),
    lineCount: z.number(),
    draftProductsSaved: z.array(z.string()),
    draftProductsNotSaved: z.array(z.string()),
    total: z.number().nullish(),
  })
  .passthrough();
const ApiDraftDto = z
  .object({
    id: z.string(),
    kind: z.enum(["sale", "acceptance"]),
    status: z.enum(["proposed", "expired", "applied", "dismissed"]),
    store: ApiDraftStoreDto,
    items: z.array(ApiDraftLineDto),
    note: z.string().nullish(),
    createdAt: z.string(),
    expiresAt: z.string(),
    proposedBy: ApiDraftProposerDto,
    decidedAt: z.string().nullish(),
    decidedBy: ApiDraftDeciderDto.nullish(),
    appliedRecordId: z.string().nullish(),
    applied: ApiDraftAppliedDto.nullish(),
  })
  .passthrough();
const ApiDraftsDto = z.object({ items: z.array(ApiDraftDto) }).passthrough();
const ApplyDraftDto = z
  .object({ firestoreId: z.string().max(1500) })
  .passthrough();
const ApiV1WhoamiOrganizationDto = z
  .object({ id: z.string(), firestoreId: z.string(), name: z.string() })
  .passthrough();
const ApiV1WhoamiUserDto = z
  .object({
    id: z.string(),
    name: z.string().nullish(),
    email: z.string(),
    role: z.string(),
  })
  .passthrough();
const ApiV1WhoamiTokenDto = z
  .object({
    id: z.string(),
    name: z.string(),
    tokenPrefix: z.string(),
    scopes: z.array(
      z.enum([
        "read",
        "read:products",
        "read:stock",
        "read:sales",
        "write:sales",
        "write:products",
        "write:danger",
        "draft:sales",
        "draft:acceptances",
      ])
    ),
    expiresAt: z.string(),
  })
  .passthrough();
const ApiV1WhoamiDto = z
  .object({
    organization: ApiV1WhoamiOrganizationDto,
    user: ApiV1WhoamiUserDto,
    token: ApiV1WhoamiTokenDto,
  })
  .passthrough();
const ApiV1StoresDto = z
  .object({ items: z.array(StoreFirestoreResponse) })
  .passthrough();
const ApiV1ProductsPageDto = z
  .object({
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
    items: z.array(ProductFirestoreResponse),
  })
  .passthrough();
const ApiV1SalesPageDto = z
  .object({
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
    items: z.array(SaleFirestoreResponse),
  })
  .passthrough();
const ApiV1AcceptancesPageDto = z
  .object({
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
    items: z.array(SupplyFirestoreResponse),
  })
  .passthrough();
const ApiV1RevenueDayDto = z
  .object({
    date: z.string().nullish(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
    saleAmount: z.number(),
    saleQuantity: z.number(),
    salesPurchaseAmount: z.number(),
    salesProfit: z.number(),
    returnsAmount: z.number(),
    returnsQuantity: z.number(),
  })
  .passthrough();
const ApiV1RevenueByDayDto = z
  .object({ items: z.array(ApiV1RevenueDayDto) })
  .passthrough();
const ApiV1SalesTotalsDto = z
  .object({
    saleAmount: z.number(),
    saleCount: z.number(),
    salesPurchaseAmount: z.number(),
    salesProfit: z.number(),
    returnsAmount: z.number(),
    returnsQuantity: z.number(),
  })
  .passthrough();
const ApiV1SalesTotalsByStoreDto = z
  .object({
    saleAmount: z.number(),
    saleCount: z.number(),
    salesPurchaseAmount: z.number(),
    salesProfit: z.number(),
    returnsAmount: z.number(),
    returnsQuantity: z.number(),
    storeId: z.string().nullish(),
    storeName: z.string().nullish(),
  })
  .passthrough();
const ApiV1TopSellerDto = z
  .object({
    productId: z.string(),
    name: z.string(),
    saleQuantity: z.number(),
    saleAmount: z.number(),
    margin: z.number(),
  })
  .passthrough();
const ApiV1BusinessDayWindowDto = z
  .object({
    storeIds: z.array(z.string()),
    timezone: z.string(),
    businessDayCutoffTime: z.string(),
    from: z.string(),
    to: z.string(),
  })
  .passthrough();
const ApiV1SalesSummaryDto = z
  .object({
    startDate: z.string(),
    endDate: z.string(),
    storeId: z.string().nullish(),
    totals: ApiV1SalesTotalsDto,
    byStore: z.array(ApiV1SalesTotalsByStoreDto),
    topProducts: z.array(ApiV1TopSellerDto),
    topProductsWindows: z.array(ApiV1BusinessDayWindowDto),
  })
  .passthrough();
const ApiV1TopProductDto = z
  .object({
    productId: z.string(),
    name: z.string(),
    categoryId: z.string().nullish(),
    supplierId: z.string().nullish(),
    saleQuantity: z.number(),
    saleAmount: z.number(),
    writeOffQuantity: z.number(),
    writeOffAmount: z.number(),
    supplyQuantity: z.number(),
    supplyAmount: z.number(),
    margin: z.number(),
    profit: z.number(),
    checkQuantity: z.number(),
    purchaseAmount: z.number(),
    packageAmount: z.number(),
  })
  .passthrough();
const ApiV1TopProductsDto = z
  .object({ sortBy: z.string(), items: z.array(ApiV1TopProductDto) })
  .passthrough();
const ApiV1StockBalanceDto = z
  .object({
    productId: z.string(),
    firestoreId: z.string().nullish(),
    name: z.string(),
    sku: z.string().nullish(),
    unit: z.string().nullish(),
    isComposite: z.boolean(),
    storeStock: z.record(z.number()),
  })
  .passthrough();
const ApiV1StoreRefDto = z
  .object({ id: z.string(), name: z.string() })
  .passthrough();
const ApiV1StockBalancesPageDto = z
  .object({
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
    items: z.array(ApiV1StockBalanceDto),
    stores: z.array(ApiV1StoreRefDto),
  })
  .passthrough();
const ApiV1StockBalanceItemDto = z
  .object({ item: ApiV1StockBalanceDto, stores: z.array(ApiV1StoreRefDto) })
  .passthrough();
const SaleDraftLineDto = z
  .object({
    productId: z.string(),
    quantity: z.number().gt(0).lte(999999.9999),
  })
  .passthrough();
const SaleDraftRequestDto = z
  .object({
    storeId: z.string(),
    items: z.array(SaleDraftLineDto).min(1).max(50),
    note: z.string().max(1000).optional(),
  })
  .passthrough();
const ApiDraftCreatedDto = z
  .object({ created: z.boolean(), draft: ApiDraftDto })
  .passthrough();
const AcceptanceDraftLineDto = z
  .object({
    productId: z.string(),
    quantity: z.number().gt(0).lte(999999.9999),
    purchasePrice: z.number().gte(0).lte(999999999.99).optional(),
  })
  .passthrough();
const AcceptanceDraftRequestDto = z
  .object({
    storeId: z.string(),
    items: z.array(AcceptanceDraftLineDto).min(1).max(50),
    note: z.string().max(1000).optional(),
  })
  .passthrough();

export const schemas = {
  SignupDto,
  AuthUserResponseDto,
  SignupResponseDto,
  LoginDto,
  SessionResponseDto,
  LoginResponseDto,
  MessageResponseDto,
  RequestPasswordResetDto,
  ResetPasswordDto,
  VerifyEmailDto,
  VerifyEmailResponseDto,
  ResendVerificationDto,
  CurrencyDto,
  BusinessTypeDto,
  FunnelOptionDto,
  FunnelAnswersDto,
  TrafficSourceDto,
  SetupOptionDto,
  WhatsappOnboardingConsentDto,
  TimezoneDto,
  TimeDto,
  FiscalOperatorDto,
  StoreSetupDto,
  UserSetupDto,
  WalletDto,
  PaymentMethodDto,
  PurchaseMethodDto,
  RoleDto,
  ProductUnitDto,
  DiscountOptionDto,
  SpendingCategoryDto,
  WriteOffReasonDto,
  ReturnReasonDto,
  ProductModifierDto,
  LoyaltySettingsDto,
  OrganizationSettingsDto,
  SaleSettingsDto,
  RecurringSpendingStoreDto,
  RecurringSpendingSetupDto,
  ProductCategoryDto,
  SupplierDto,
  ProductDto,
  ChecklistSetupDto,
  CreateOrganizationDto,
  CreateOrganizationResponseDto,
  OrganizationFirestoreResponse,
  ResetDataDto,
  ResetDataResponseDto,
  UpdateOrganizationDto,
  UpdateOnboardingDto,
  SetSupabaseReadsDto,
  UpdateBillingUsageDto,
  SubscriptionSyncResponseDto,
  OverrideSubscriptionDto,
  DualWritePolicyResponseDto,
  ProactiveOptOutDto,
  WelcomeQuickReplyTapDto,
  SendMessageDto,
  RecordAppliedDto,
  UpdateConversationDto,
  ProposeActionDto,
  TeamReplyDto,
  WebVisitorMessageDto,
  EnsureWebsiteOrgDto,
  WiponSelectionOptionDto,
  WiponCredentialsDto,
  LKCredentialsDto,
  CreateStoreDto,
  StoreFirestoreResponse,
  UpdateStoreDto,
  DeleteResponseDto,
  ComparisonDifferenceDto,
  ComparisonResultDto,
  CreateProductDto,
  ProductFirestoreResponse,
  UpdateProductDto,
  BulkStockUpdateItemDto,
  BulkStockUpdateDto,
  BulkStockUpdateResultDto,
  BulkStockUpdateResponseDto,
  IngredientDto,
  BulkCostCascadeItemDto,
  BulkCostCascadeDto,
  BulkCostCascadeResultDto,
  BulkCostCascadeResponseDto,
  HealMissingProductsDto,
  HealMissingProductResultDto,
  HealMissingProductsResponseDto,
  SeedStoreProductSettingsItemDto,
  SeedStoreProductSettingsDto,
  SeedStoreProductSettingsResultDto,
  SeedStoreProductSettingsResponseDto,
  ProductStockUpdateResponseDto,
  VariantDto,
  CartItemDto,
  FiscalReceipt,
  ReturnedItemDto,
  ReturnInfoDto,
  ModifierDto,
  CreateSaleDto,
  CartItemFirestoreResponse,
  SaleFirestoreResponse,
  UpdateSaleDto,
  MarkSalePaidDto,
  MarkSalePaidResponseDto,
  ReturnItemFirestoreResponse,
  ReturnFirestoreResponse,
  ReturnItemDto,
  CreateSaleReturnDto,
  LostSaleDeletionDto,
  StoreRefDto,
  UserFirestoreResponse,
  CreateUserDto,
  SyncUserResponseDto,
  HealUserFromFirestoreDto,
  HealUserResponseDto,
  UserAdminScopeDto,
  UserAdminTargetDto,
  UpdateUserDto,
  UpdatePushTokenDto,
  UpdateEmailDto,
  UpdatePasswordDto,
  DailyStatsResponseDto,
  ShiftReportResponseDto,
  SalesReportSummaryDto,
  SalesReportResponseDto,
  InventoryReportSummaryDto,
  InventoryReportResponseDto,
  FinancialReportSummaryDto,
  FinancialReportResponseDto,
  DualWriteOrganizationDto,
  DualWriteOrganizationsResponseDto,
  BulkCompareRequestDto,
  FieldDifferenceDto,
  StoreComparisonSummaryDto,
  BulkCompareResultDto,
  SupplyItemDto,
  CreateSupplyDto,
  SupplyItemFirestoreResponse,
  SupplyFirestoreResponse,
  SupplyOrderFirestoreResponse,
  SupplyExpectedPaymentDto,
  UpdateSupplyDto,
  CreateSupplyOrderDto,
  UpdateSupplyOrderDto,
  ExternalDestinationDto,
  PaymentRangeDto,
  CreateTransactionDto,
  TransactionFirestoreResponse,
  WalletBalanceResponseDto,
  LoyaltyDiscount,
  ClientFirestoreResponse,
  LoyaltyDiscountDto,
  CreateClientDto,
  ClientStatsResponseDto,
  ClientComparisonResultDto,
  UpdateClientDto,
  CreateClientBonusAdjustmentDto,
  ClientBonusAdjustmentResponseDto,
  ClientPunchCardResponseDto,
  CorrectionReasonDto,
  RevisionItemDto,
  CreateRevisionDto,
  RevisionItemFirestoreResponse,
  RevisionFirestoreResponse,
  UpdateRevisionDto,
  TransferItemDto,
  CreateTransferDto,
  TransferStoreRefFirestoreResponse,
  TransferItemFirestoreResponse,
  TransferFirestoreResponse,
  WriteoffItemDto,
  CreateWriteoffDto,
  WriteoffItemFirestoreResponse,
  WriteoffProductFirestoreResponse,
  WriteoffReasonFirestoreResponse,
  WriteoffFirestoreResponse,
  PaymentMethodRefDto,
  CreateSpendingDto,
  SpendingFirestoreResponse,
  OrderItemDto,
  CreateOrderDto,
  OrderItemFirestoreResponse,
  OrderFirestoreResponse,
  UpdateOrderDto,
  OrderCountResponseDto,
  PublicOrderItemDto,
  CreatePublicOrderDto,
  CreateSupplierDto,
  SupplierFirestoreResponse,
  UpdateSupplierDto,
  CreateCategoryDto,
  ProductCategoryFirestoreResponse,
  UpdateCategoryDto,
  CreateWorkingHoursDto,
  WorkingHoursFirestoreResponse,
  ProductMovementFirestoreResponse,
  ProductVariantDto,
  SavedCartItemDto,
  CreateSavedCartDto,
  SavedCartFirestoreResponse,
  UpdateSavedCartDto,
  PaymentMethodResponseDto,
  ConstantItemResponseDto,
  RoleResponseDto,
  ProductUnitResponseDto,
  WalletResponseDto,
  DiscountOptionResponseDto,
  SpendingCategoryResponseDto,
  SaleSettingsResponseDto,
  LoyaltySettingsResponseDto,
  RecurringSpendingResponseDto,
  AllConstantsResponseDto,
  UpsertOrganizationConstantDto,
  OrganizationConstantResponseDto,
  CreatePaymentMethodDto,
  PaymentMethodFirestoreResponse,
  UpdatePaymentMethodDto,
  DeletePaymentMethodResponseDto,
  CreatePurchaseMethodDto,
  PurchaseMethodFirestoreResponse,
  UpdatePurchaseMethodDto,
  DeletePurchaseMethodResponseDto,
  CreateRoleDto,
  RoleFirestoreResponse,
  UpdateRoleDto,
  DeleteRoleResponseDto,
  CreateProductUnitDto,
  ProductUnitFirestoreResponse,
  UpdateProductUnitDto,
  DeleteProductUnitResponseDto,
  CreateWalletDto,
  WalletFirestoreResponse,
  UpdateWalletDto,
  DeleteWalletResponseDto,
  CreateDiscountOptionDto,
  DiscountOptionFirestoreResponse,
  UpdateDiscountOptionDto,
  DeleteDiscountOptionResponseDto,
  CreateSpendingCategoryDto,
  SpendingCategoryFirestoreResponse,
  UpdateSpendingCategoryDto,
  DeleteSpendingCategoryResponseDto,
  CreateWriteOffReasonDto,
  WriteOffReasonFirestoreResponse,
  UpdateWriteOffReasonDto,
  DeleteWriteOffReasonResponseDto,
  CreateReturnReasonDto,
  ReturnReasonFirestoreResponse,
  UpdateReturnReasonDto,
  DeleteReturnReasonResponseDto,
  CreateProductModifierDto,
  ProductModifierFirestoreResponse,
  UpdateProductModifierDto,
  DeleteProductModifierResponseDto,
  CreateSaleSettingsDto,
  SaleSettingsFirestoreResponse,
  UpdateSaleSettingsDto,
  DeleteSaleSettingsResponseDto,
  CreateLoyaltySettingsDto,
  LoyaltySettingsFirestoreResponse,
  UpdateLoyaltySettingsDto,
  DeleteLoyaltySettingsResponseDto,
  CreateRecurringSpendingDto,
  RecurringSpendingFirestoreResponse,
  UpdateRecurringSpendingDto,
  DeleteRecurringSpendingResponseDto,
  CreateChecklistDto,
  ChecklistItemDto,
  ChecklistFirestoreResponse,
  UpdateChecklistDto,
  ManufactureIngredientDto,
  CreateManufactureDto,
  ManufactureProductFirestoreResponse,
  ManufactureIngredientFirestoreResponse,
  ManufactureFirestoreResponse,
  PrintTemplateFirestoreResponse,
  CreatePrintTemplateDto,
  UpdatePrintTemplateDto,
  CashboxLinkedTransactionDto,
  CreateCashboxTransactionDto,
  CashboxTransactionFirestoreResponse,
  UpdateCashboxTransactionDto,
  ComplexSaleTemplateFirestoreResponse,
  CreateComplexSaleTemplateDto,
  UpdateComplexSaleTemplateDto,
  ComplexSaleFirestoreResponse,
  ComplexSaleLinkedTransactionDto,
  CreateComplexSaleDto,
  UpdateComplexSaleDto,
  CreateAuditLogDto,
  AuditLogFirestoreResponse,
  ClientOperationFirestoreResponse,
  CreateClientOperationDto,
  LegacyClientOperationResponse,
  CreateUpdateDto,
  UpdateFirestoreResponse,
  CreatePriceTypeDto,
  PriceTypeFirestoreResponse,
  UpdatePriceTypeDto,
  DeletePriceTypeResponseDto,
  MarketplaceSettingsFirestoreResponse,
  UpdateMarketplaceSettingsDto,
  MarketplaceStorefrontResponse,
  MarketplaceProductResponse,
  OrgAuditDto,
  FleetAuditDto,
  ApiTokensAvailabilityDto,
  ApiTokenCreatorDto,
  OAuthClientDto,
  ApiTokenDto,
  ApiTokensListDto,
  CreateApiTokenDto,
  CreatedApiTokenDto,
  RevokedApiTokenDto,
  ApiAuditUserRefDto,
  ApiAuditTokenRefDto,
  ApiAuditEventDto,
  ApiAuditEventsDto,
  ApiDraftStoreDto,
  ApiDraftLineDto,
  ApiDraftProposerDto,
  ApiDraftDeciderDto,
  ApiDraftAppliedDto,
  ApiDraftDto,
  ApiDraftsDto,
  ApplyDraftDto,
  ApiV1WhoamiOrganizationDto,
  ApiV1WhoamiUserDto,
  ApiV1WhoamiTokenDto,
  ApiV1WhoamiDto,
  ApiV1StoresDto,
  ApiV1ProductsPageDto,
  ApiV1SalesPageDto,
  ApiV1AcceptancesPageDto,
  ApiV1RevenueDayDto,
  ApiV1RevenueByDayDto,
  ApiV1SalesTotalsDto,
  ApiV1SalesTotalsByStoreDto,
  ApiV1TopSellerDto,
  ApiV1BusinessDayWindowDto,
  ApiV1SalesSummaryDto,
  ApiV1TopProductDto,
  ApiV1TopProductsDto,
  ApiV1StockBalanceDto,
  ApiV1StoreRefDto,
  ApiV1StockBalancesPageDto,
  ApiV1StockBalanceItemDto,
  SaleDraftLineDto,
  SaleDraftRequestDto,
  ApiDraftCreatedDto,
  AcceptanceDraftLineDto,
  AcceptanceDraftRequestDto,
};

const endpoints = makeApi([
  {
    method: "get",
    path: "/api",
    alias: "AppController_getHello",
    requestFormat: "json",
    response: z.string(),
  },
  {
    method: "get",
    path: "/api/api-drafts",
    alias: "ApiDraftsController_list",
    requestFormat: "json",
    parameters: [
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["proposed", "expired", "applied", "dismissed"])
          .optional(),
      },
      {
        name: "kind",
        type: "Query",
        schema: z.enum(["sale", "acceptance"]).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().gte(1).lte(200).optional().default(50),
      },
    ],
    response: ApiDraftsDto,
  },
  {
    method: "post",
    path: "/api/api-drafts/:id/applied",
    alias: "ApiDraftsController_applied",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ firestoreId: z.string().max(1500) }).passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ApiDraftDto,
    errors: [
      {
        status: 409,
        description: `&#x60;already_decided&#x60; or &#x60;record_already_claimed&#x60;`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `&#x60;&lt;kind&gt;_not_found&#x60;, &#x60;&lt;kind&gt;_predates_proposal&#x60;, &#x60;&lt;kind&gt;_in_other_organization&#x60;, &#x60;store_mismatch&#x60; or &#x60;no_draft_products&#x60;`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Firestore could not be read; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/api-drafts/:id/dismiss",
    alias: "ApiDraftsController_dismiss",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ApiDraftDto,
    errors: [
      {
        status: 409,
        description: `&#x60;already_decided&#x60;`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/api-tokens",
    alias: "ApiTokensController_list",
    requestFormat: "json",
    response: ApiTokensListDto,
  },
  {
    method: "post",
    path: "/api/api-tokens",
    alias: "ApiTokensController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateApiTokenDto,
      },
    ],
    response: CreatedApiTokenDto,
    errors: [
      {
        status: 409,
        description: `&#x60;organization_not_on_supabase&#x60; or &#x60;too_many_active_tokens&#x60;; or, when the standing changed after the route admitted the caller, &#x60;not_an_active_admin&#x60;, &#x60;organization_not_found&#x60; or &#x60;creator_has_no_firebase_identity&#x60;`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/api-tokens/:id/revoke",
    alias: "ApiTokensController_revoke",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RevokedApiTokenDto,
    errors: [
      {
        status: 404,
        description: `No such token in this organization`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `&#x60;not_an_active_admin&#x60;: the caller is not an active admin of this organization`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/api-tokens/audit",
    alias: "ApiTokensController_auditEvents",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().gte(1).lte(200).optional().default(50),
      },
      {
        name: "before",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "beforeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "tokenId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: ApiAuditEventsDto,
  },
  {
    method: "post",
    path: "/api/audit-log",
    alias: "AuditLogController_createAuditLog",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateAuditLogDto,
      },
    ],
    response: z.object({ id: z.string() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/audit-log",
    alias: "AuditLogController_getAuditLogs",
    requestFormat: "json",
    parameters: [
      {
        name: "tableName",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "recordId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(AuditLogFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/auth/email-verification/resend",
    alias: "AuthController_resendVerification",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string() }).passthrough(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "post",
    path: "/api/auth/email-verification/verify",
    alias: "AuthController_verifyEmail",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ token: z.string() }).passthrough(),
      },
    ],
    response: VerifyEmailResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid or expired token`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/auth/login",
    alias: "AuthController_login",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LoginDto,
      },
    ],
    response: LoginResponseDto,
    errors: [
      {
        status: 401,
        description: `Invalid credentials`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/auth/logout",
    alias: "AuthController_logout",
    requestFormat: "json",
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/auth/me",
    alias: "AuthController_getCurrentUser",
    requestFormat: "json",
    response: AuthUserResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/auth/password-reset/confirm",
    alias: "AuthController_resetPassword",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ResetPasswordDto,
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid or expired token`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/auth/password-reset/request",
    alias: "AuthController_requestPasswordReset",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string() }).passthrough(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "post",
    path: "/api/auth/signup",
    alias: "AuthController_signup",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SignupDto,
      },
    ],
    response: SignupResponseDto,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `User already exists`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/cashbox-transactions",
    alias: "CashboxTransactionsController_createCashboxTransaction",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateCashboxTransactionDto,
      },
    ],
    response: CashboxTransactionFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/cashbox-transactions",
    alias: "CashboxTransactionsController_getCashboxTransactions",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(CashboxTransactionFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/cashbox-transactions/:id",
    alias: "CashboxTransactionsController_getCashboxTransactionById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CashboxTransactionFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/cashbox-transactions/:id",
    alias: "CashboxTransactionsController_updateCashboxTransaction",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateCashboxTransactionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CashboxTransactionFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/cashbox-transactions/:id",
    alias: "CashboxTransactionsController_deleteCashboxTransaction",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/cashbox-transactions/compare/:firestoreId",
    alias: "CashboxTransactionsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        exists: z.boolean(),
        supabaseData: z.object({}).partial().passthrough().nullable(),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/cashbox-transactions/firestore/:firestoreId",
    alias: "CashboxTransactionsController_deleteByFirestoreId",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/cashbox-transactions/window",
    alias: "CashboxTransactionsController_getCashboxTransactionsWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/checklists",
    alias: "ChecklistsController_createChecklist",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateChecklistDto,
      },
    ],
    response: ChecklistFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/checklists/:id",
    alias: "ChecklistsController_updateChecklist",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateChecklistDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ChecklistFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/checklists/:id",
    alias: "ChecklistsController_getChecklist",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ChecklistFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/checklists/compare/:firestoreId",
    alias: "ChecklistsController_compareWithFirestore",
    description: `Fetches checklist from both databases and compares all fields to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        firestoreId: z.string(),
        supabaseId: z.string().nullable(),
        foundInSupabase: z.boolean(),
        foundInFirestore: z.boolean(),
        matches: z.boolean(),
        differences: z.array(z.any()),
        warnings: z.array(z.any()),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/checklists/latest/before-date",
    alias: "ChecklistsController_getLatestChecklistBeforeDate",
    requestFormat: "json",
    parameters: [
      {
        name: "date",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: ChecklistFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/client-operations",
    alias: "ClientOperationsController_list",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "clientId",
        type: "Query",
        schema: z.unknown(),
      },
    ],
    response: z.array(ClientOperationFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/client-operations",
    alias: "ClientOperationsController_createLegacy",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateClientOperationDto,
      },
    ],
    response: LegacyClientOperationResponse,
  },
  {
    method: "get",
    path: "/api/clients",
    alias: "ClientsController_getClients",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "registrationDateFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "registrationDateTo",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "lastPurchaseDateFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "lastPurchaseDateTo",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "includeDeleted",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(ClientFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/clients",
    alias: "ClientsController_createClient",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateClientDto,
      },
    ],
    response: ClientFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/clients/:id",
    alias: "ClientsController_getClientById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ClientFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/clients/:id",
    alias: "ClientsController_updateClient",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateClientDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ClientFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/clients/:id",
    alias: "ClientsController_deleteClient",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/clients/:id/bonus-adjustments",
    alias: "ClientsController_applyBonusAdjustment",
    description: `ATL-1445 stage 2c. Adds &#x60;delta&#x60; to the client bonus balance under a row lock and records the operation once per &#x60;operationId&#x60; (the Firestore operation document id). A replay returns status &#x60;replayed&#x60; and applies nothing; the same operationId with another client or delta is 409.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateClientBonusAdjustmentDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ClientBonusAdjustmentResponseDto,
    errors: [
      {
        status: 404,
        description: `No such client in this organization`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `operationId already used for a different client or delta`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/clients/:id/punch-card",
    alias: "ClientsController_getClientPunchCardInfo",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ClientPunchCardResponseDto,
  },
  {
    method: "get",
    path: "/api/clients/:id/purchase-history",
    alias: "ClientsController_getClientPurchaseHistory",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(SaleFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/clients/compare/:firestoreId",
    alias: "ClientsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: ClientComparisonResultDto,
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/clients/search",
    alias: "ClientsController_searchClients",
    requestFormat: "json",
    parameters: [
      {
        name: "q",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(ClientFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/clients/stats",
    alias: "ClientsController_getClientStats",
    requestFormat: "json",
    parameters: [
      {
        name: "registrationDateFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "registrationDateTo",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "lastPurchaseDateFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "lastPurchaseDateTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: ClientStatsResponseDto,
  },
  {
    method: "get",
    path: "/api/clients/window",
    alias: "ClientsController_getClientsWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "registrationDateFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "registrationDateTo",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "lastPurchaseDateFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "lastPurchaseDateTo",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "includeDeleted",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/complex-sale-templates",
    alias: "ComplexSaleTemplatesController_getTemplates",
    requestFormat: "json",
    response: z.array(ComplexSaleTemplateFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/complex-sale-templates",
    alias: "ComplexSaleTemplatesController_createTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateComplexSaleTemplateDto,
      },
    ],
    response: ComplexSaleTemplateFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/complex-sale-templates/:id",
    alias: "ComplexSaleTemplatesController_getTemplateById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ComplexSaleTemplateFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/complex-sale-templates/:id",
    alias: "ComplexSaleTemplatesController_updateTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateComplexSaleTemplateDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ComplexSaleTemplateFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/complex-sale-templates/:id",
    alias: "ComplexSaleTemplatesController_deleteTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/complex-sales",
    alias: "ComplexSalesController_getComplexSales",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "clientId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(ComplexSaleFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/complex-sales",
    alias: "ComplexSalesController_createComplexSale",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateComplexSaleDto,
      },
    ],
    response: ComplexSaleFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/complex-sales/:id",
    alias: "ComplexSalesController_getComplexSaleById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ComplexSaleFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/complex-sales/:id",
    alias: "ComplexSalesController_updateComplexSale",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateComplexSaleDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ComplexSaleFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/complex-sales/:id",
    alias: "ComplexSalesController_deleteComplexSale",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/complex-sales/window",
    alias: "ComplexSalesController_getComplexSalesWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "clientId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/config/dual-write-policy",
    alias: "ConfigController_getDualWritePolicy",
    description: `Fallback policy source for clients whose Firebase Remote Config SDK reports a successful fetch but reads empty values (ATL-911). Returns the SAME template the client reads, so an operator still changes policy in one place. Raw strings — the client parses.`,
    requestFormat: "json",
    response: DualWritePolicyResponseDto,
    errors: [
      {
        status: 503,
        description: `Remote Config could not be read. Deliberately an error, not an empty policy: an empty policy would read to the client as an operator decision to allow nothing.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/discount-options",
    alias: "DiscountOptionsController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateDiscountOptionDto,
      },
    ],
    response: DiscountOptionFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/discount-options",
    alias: "DiscountOptionsController_findAll",
    requestFormat: "json",
    response: z.array(DiscountOptionFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/discount-options/:id",
    alias: "DiscountOptionsController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DiscountOptionFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/discount-options/:id",
    alias: "DiscountOptionsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateDiscountOptionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DiscountOptionFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/discount-options/:id",
    alias: "DiscountOptionsController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/discount-options/compare/:firestoreId",
    alias: "DiscountOptionsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/health",
    alias: "AppController_getHealth",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/logs/sessions/count",
    alias: "LogsController_getSessionCount",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/logs/stream",
    alias: "LogsController_stream",
    description: `Establishes an SSE connection for real-time log streaming. Use x-log-session header on other requests to route logs here.`,
    requestFormat: "json",
    parameters: [
      {
        name: "session",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/loyalty-settings",
    alias: "LoyaltySettingsController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateLoyaltySettingsDto,
      },
    ],
    response: LoyaltySettingsFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/loyalty-settings",
    alias: "LoyaltySettingsController_findOne",
    requestFormat: "json",
    response: LoyaltySettingsFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/loyalty-settings",
    alias: "LoyaltySettingsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateLoyaltySettingsDto,
      },
    ],
    response: LoyaltySettingsFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/loyalty-settings",
    alias: "LoyaltySettingsController_remove",
    requestFormat: "json",
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/loyalty-settings/compare/:organizationId",
    alias: "LoyaltySettingsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/manufactures",
    alias: "ManufacturesController_createManufacture",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateManufactureDto,
      },
    ],
    response: ManufactureFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/manufactures",
    alias: "ManufacturesController_getManufactures",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(ManufactureFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/manufactures/:id",
    alias: "ManufacturesController_getManufactureById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ManufactureFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/manufactures/:id",
    alias: "ManufacturesController_deleteManufacture",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/manufactures/compare/:firestoreId",
    alias: "ManufacturesController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: ComparisonResultDto,
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/manufactures/window",
    alias: "ManufacturesController_getManufacturesWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/marketplace-settings",
    alias: "MarketplaceSettingsController_findOne",
    requestFormat: "json",
    response: MarketplaceSettingsFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/marketplace-settings",
    alias: "MarketplaceSettingsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateMarketplaceSettingsDto,
      },
    ],
    response: MarketplaceSettingsFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/marketplace/organizations",
    alias: "MarketplacePublicController_listOrganizations",
    requestFormat: "json",
    response: z.array(MarketplaceStorefrontResponse),
  },
  {
    method: "get",
    path: "/api/marketplace/organizations/:organizationId",
    alias: "MarketplacePublicController_getStorefront",
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: MarketplaceStorefrontResponse,
  },
  {
    method: "get",
    path: "/api/marketplace/price-types",
    alias: "MarketplacePublicController_getPriceTypes",
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(PriceTypeFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/marketplace/product-categories",
    alias: "MarketplacePublicController_getCategories",
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(ProductCategoryFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/marketplace/product-categories/:categoryId",
    alias: "MarketplacePublicController_getCategory",
    requestFormat: "json",
    parameters: [
      {
        name: "categoryId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: ProductCategoryFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/marketplace/product-units",
    alias: "MarketplacePublicController_getProductUnits",
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(ProductUnitFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/marketplace/products",
    alias: "MarketplacePublicController_getProducts",
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "categoryId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(MarketplaceProductResponse),
  },
  {
    method: "post",
    path: "/api/migration/cleanup/:organizationId",
    alias: "MigrationController_cleanupOrganization",
    description: `Cleanup migrated data for a test organization
POST /migration/cleanup/:organizationId`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/migration/organization",
    alias: "MigrationController_migrateOrganization",
    description: `Migrate one or more organizations (full migration)
POST /migration/organization

Body:
  - organizationIds: string[] (required) - List of Firestore organization IDs to migrate
  - exportFilePath: string (optional) - Path to Firebase Auth export JSON file
    If provided, auth.users are migrated FIRST with preserved passwords,
    then all collections including public.users are migrated.
  - dryRun: boolean (optional) - If true, report without writing
  - skipCollections: string[] (optional) - Collections to skip (mutually exclusive with onlyCollections)
  - onlyCollections: string[] (optional) - If provided, ONLY these collections are migrated.
    The organization document is always migrated regardless.
    Mutually exclusive with skipCollections.
  - cleanBeforeMigrate: boolean (optional, default false) - Clear the org&#x27;s existing Supabase
    data (organization row + UUID preserved) before reinserting. REQUIRED to re-migrate an
    already-migrated org (refresh its baseline); without it the upsert collides on the
    firestore_id unique index. Disable the org&#x27;s dual-write first.
  - acknowledgeStockRewriteOnLiveReads: boolean (optional, default false) - REQUIRED for any org
    whose reads are already cut over to Supabase (use_supabase_for_data&#x3D;true) when the run
    includes the &#x60;products&#x60; task. Such a run re-anchors product_store_stock.quantity onto the
    CURRENT Firestore storeStock — the on-hand number that org&#x27;s cashier is reading right now —
    so it is refused unless this records the decision. Prefer rolling reads back first.

Examples:
  # Full migration
  curl -X POST http://localhost:3000/api/migration/organization \
    -H &quot;Content-Type: application/json&quot; \
    -d &#x27;{&quot;organizationIds&quot;: [&quot;org1&quot;, &quot;org2&quot;], &quot;exportFilePath&quot;: &quot;/path/to/accounts.json&quot;}&#x27;

  # Migrate only organization + stores + users
  curl -X POST http://localhost:3000/api/migration/organization \
    -H &quot;Content-Type: application/json&quot; \
    -d &#x27;{&quot;organizationIds&quot;: [&quot;org1&quot;], &quot;onlyCollections&quot;: [&quot;stores&quot;, &quot;users&quot;]}&#x27;`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/migration/paginated/:organizationId/:collectionName",
    alias: "MigrationController_migrateCollectionPaginated",
    description: `Paginated migration for large organizations
POST /migration/paginated/:organizationId/:collectionName
Query params:
  - limit: number (default: 500) - documents per page
  - cursor: string - base64 encoded cursor for pagination
  - dryRun: boolean (default: false)

Response includes:
  - cursor: use this for next request to continue
  - hasMore: true if more documents exist

Example: Keep calling until hasMore&#x3D;false:
  POST /migration/paginated/ORG_ID/products?limit&#x3D;500
  POST /migration/paginated/ORG_ID/products?limit&#x3D;500&amp;cursor&#x3D;RETURNED_CURSOR`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "collectionName",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "dryRun",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/migration/parity/:organizationId",
    alias: "MigrationController_parity",
    description: `Full-count dual-write parity report for an org (ATL-469): Firestore-total vs Supabase-total
per collection. The cutover-readiness signal (recent-sample checks are insufficient).
GET /migration/parity/:organizationId`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/migration/run",
    alias: "MigrationController_createRun",
    description: `Create a resumable migration run for a large org (ATL-590).
POST /migration/run  { organizationId, options }
Returns { runId, plan, status }. Then drive it by repeatedly POSTing
/migration/run/:runId/advance until { done: true }.

REFUSES (before any run row is created) when the org&#x27;s reads are cut over to Supabase and the
plan would re-anchor its on-hand stock, unless &#x60;options.acknowledgeStockRewriteOnLiveReads&#x60; is
set — see &#x60;live-read-stock-rewrite-admission.util.ts&#x60;.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/migration/run/:runId",
    alias: "MigrationController_getRun",
    description: `Get run status/progress (ATL-590). GET /migration/run/:runId`,
    requestFormat: "json",
    parameters: [
      {
        name: "runId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/migration/run/:runId/advance",
    alias: "MigrationController_advanceRun",
    description: `Execute exactly one bounded unit of a run (ATL-590). POST /migration/run/:runId/advance`,
    requestFormat: "json",
    parameters: [
      {
        name: "runId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/migration/run/:runId/restore",
    alias: "MigrationController_restoreRun",
    description: `All-or-nothing row CAS restore for a sealed terminal full run (ATL-1359).`,
    requestFormat: "json",
    parameters: [
      {
        name: "runId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/migration/setdiff/:organizationId",
    alias: "MigrationController_setDiff",
    description: `ATL-592 (ATL-469 increment): id-level set-diff parity — the RIGOROUS read-cutover gate count
parity can&#x27;t be (it catches missing rows masked by offsetting orphans). Per collection, diffs
the full firestore_id set on both sides. Scope big orgs with ?collections&#x3D;sales,orders.
GET /migration/setdiff/:organizationId`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "collections",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "maxIds",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/migration/setdiff/:organizationId/reconcile",
    alias: "MigrationController_setDiffReconcile",
    description: `ATL-592: TARGETED set-diff backfill — fetches ONLY the missing docs by id (no full scan) and
inserts them via the shared insert path. Error-isolated per collection. Optional pruneOrphans
(default off) deletes stale Supabase rows whose firestore_id is gone from Firestore.
POST /migration/setdiff/:organizationId/reconcile

ATL-1036: &#x60;dryRun&#x60; (default false) reports what a live run would do and writes nothing —
no insert, no prune, no &#x60;recompute_org_caches&#x60;. The option had been implemented in
&#x60;reconcileOrgBySetDiff&#x60; all along and was reachable only from the fleet sweep, so this
endpoint — the one the read-cutover gate&#x27;s remedy names, and the one an operator actually
runs — could only be run for real. Every other recovery tool in this campaign is dry-run BY
DEFAULT; this one could not dry-run at all.

Read &#x60;attempted&#x60; (documents still present in Firestore, i.e. what a live run would submit)
rather than &#x60;inserted&#x60;, which is 0 on a dry run by construction. &#x60;attempted&#x60; is an UPPER
bound: the natural-key dedup and the FK-orphan skip live inside the insert a dry run does not
perform, so a dry run cannot predict ATL-1027&#x27;s terminal skip.`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/migration/users/:organizationId",
    alias: "MigrationController_migrateAuthUsers",
    description: `Migrate auth.users only (with passwords)
POST /migration/users/:organizationId

Creates auth.users entries with preserved password hashes from Firebase export.
Does NOT migrate public.users - use POST /organization for full migration.

Body:
  - exportFilePath: string (required) - Path to Firebase Auth export JSON file
  - dryRun: boolean (optional, default: false) - If true, report without writing

Prerequisites:
  Run Firebase Auth export: &#x60;firebase auth:export accounts.json --format&#x3D;json&#x60;`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/migration/validate/:organizationId",
    alias: "MigrationController_validateMigration",
    description: `Validate migration for an organization
POST /migration/validate/:organizationId
Query params:
  - sampleSize: number (default: 10) - number of random samples per collection

Compares random samples from Supabase with Firestore to verify data integrity.
Checks ALL collections (not just key ones).

Example:
  POST /migration/validate/OYvDhoihyH3f2YCFPtxD?sampleSize&#x3D;20`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "sampleSize",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/migration/write-evidence/:organizationId",
    alias: "MigrationController_getWriteEvidence",
    description: `ATL-1239 — the cutover gate&#x27;s write-integrity verdict for one organization. READ-ONLY.
GET /migration/write-evidence/:organizationId

The ATL-1206 evidence pass calls this before every organization it would re-run. The clause
reads only the NEWEST completed run, so re-running an organization that already PASSES can
demote it (an &#x60;unverified&#x60; chunk, a task error) with no refusal anywhere — the one hazard
&#x60;cohort-evidence-plan.util.ts&#x60; declared and could not close, because there was no HTTP surface
for &quot;the latest run of org X&quot; and a database credential for the driver would have been a wider
blast radius than the hazard itself.

A read failure is returned as &#x60;{ success: false }&#x60; and NOT as a verdict. The caller reads
&#x60;unknown&#x60; as permission to write, so an outage must not be able to spell itself that way.

The path does not start with &#x60;run/&#x60;, so it cannot be confused with &#x60;run/:runId&#x60; by a future
refactor of either route.`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/onboarding/proactive-opt-out",
    alias: "OnboardingController_proactiveOptOut",
    description: `ACT-NUDGE-2026-001 — stop the proactive in-app second touch to one organization, permanently.
For the support team / support agent when a merchant asks, in any channel, not to be written
to. The welcome (touch 1) is sent ~60s after registration and does not read the opt-out.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProactiveOptOutDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/onboarding/second-touch-sweep",
    alias: "OnboardingController_secondTouchSweep",
    description: `ACT-NUDGE-2026-001 — drive one second-touch sweep from outside the process (Cloud Scheduler),
for the same reason as &#x60;welcome-sweep&#x60;. Every gate still applies: with any of them closed this
returns an &#x60;idleReason&#x60; and sends nothing. Same guard, same &quot;no global auth guard&quot; warning.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/onboarding/welcome-quick-reply-taps",
    alias: "OnboardingController_welcomeQuickReplyTap",
    description: `ACT-QUICK-REPLY-001 — the merchant tapped a quick-reply button under their welcome.

Customer-facing, so &#x60;FirebaseAuthGuard&#x60; (organization and user come from the token, never the
body). ⛔ Like every route here, the decorator is the only access control —
&#x60;onboarding-route-guarded.spec.ts&#x60; asserts it. Sends nothing to anybody.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WelcomeQuickReplyTapDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/onboarding/welcome-sweep",
    alias: "OnboardingController_welcomeSweep",
    description: `Drive one in-app welcome sweep from OUTSIDE the process.

The same reasoning as &#x60;POST /support/team/notify-sweep&#x60; (ATL-995): an in-process timer cannot
be trusted to be the guarantee on Cloud Run, whereas a scheduler HTTP call IS a request and is
therefore allocated CPU for as long as it runs. Wire it to Cloud Scheduler at &#x60;* * * * *&#x60;.

Safe to call as often as you like, and safe to call concurrently with the cron: the claim is
atomic and leased, so two callers cannot both send the same welcome.

⛔ &#x60;SupportApiKeyGuard&#x60; is the ONLY access control on this route — this backend has no global
auth guard, so an undecorated route here would be an open trigger for real customer messages.
&#x60;onboarding-route-guarded.spec.ts&#x60; asserts the decorator is still present.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/orders",
    alias: "OrdersController_createOrder",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateOrderDto,
      },
    ],
    response: OrderFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/orders",
    alias: "OrdersController_getOrders",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "isProcessed",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(OrderFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/orders/:id",
    alias: "OrdersController_getOrderById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrderFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/orders/:id",
    alias: "OrdersController_updateOrder",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateOrderDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrderFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/orders/:id/status",
    alias: "OrdersController_updateOrderStatus",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrderFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/orders/count/unprocessed",
    alias: "OrdersController_countUnprocessedOrders",
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/orders/public",
    alias: "PublicOrdersController_createPublicOrder",
    description: `ATL-953 — &#x60;suppressNotification&#x60; is admin-only and this is where that is enforced.

The route is &#x60;@Public()&#x60;, so the flag cannot be trusted from the body alone: without this
check anyone able to reach the backend could create orders a merchant is never told about.
The request is REFUSED (403) rather than having the flag quietly ignored — a suppression
request from a caller without the authority to make it is a thing to surface.

Fail-closed: if &#x60;MIGRATION_ADMIN_TOKEN&#x60; is not configured, no token can match, so suppression
is impossible rather than universally allowed.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePublicOrderDto,
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: OrderFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/orders/public/compare/:firestoreId",
    alias: "PublicOrdersController_compareOrder",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/organizations",
    alias: "OrganizationsController_createOrganization",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateOrganizationDto,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/organizations",
    alias: "OrganizationsController_getCurrentOrganization",
    requestFormat: "json",
    response: OrganizationFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/organizations/:id",
    alias: "OrganizationsController_getOrganization",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrganizationFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/organizations/:id",
    alias: "OrganizationsController_updateOrganization",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateOrganizationDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrganizationFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/organizations/:id",
    alias: "OrganizationsController_deleteOrganization",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "patch",
    path: "/api/organizations/:id/billing-usage",
    alias: "OrganizationsController_updateBillingUsage",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ liftBlockTill: z.string().nullable() })
          .partial()
          .passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrganizationFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/organizations/:id/onboarding",
    alias: "OrganizationsController_updateOnboarding",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateOnboardingDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrganizationFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/organizations/:id/print-checks",
    alias: "OrganizationsController_updatePrintChecks",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrganizationFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/organizations/:id/subscription/override",
    alias: "OrganizationsController_overrideSubscription",
    description: `ATL-855 — Atlas-staff subscription override.

&#x60;SuperAdminGuard&#x60;, NOT &#x60;@Roles(&#x27;admin&#x27;)&#x60;. &#x60;role&#x60; is a per-organization role, so gating a
plan-setting endpoint on it would hand every merchant the ability to grant themselves a paid
plan. Cross-org by design: support corrects other people&#x27;s organizations.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OverrideSubscriptionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SubscriptionSyncResponseDto,
  },
  {
    method: "post",
    path: "/api/organizations/:id/subscription/sync",
    alias: "OrganizationsController_syncSubscription",
    description: `ATL-855 — the ONLY client-callable subscription write, and it takes no plan.

Both customer-initiated call sites (the free-plan choice / Android mount effect, and
&quot;restore purchases&quot;) post here with an empty body. The server derives the plan from provider
evidence it holds — &#x60;revenuecat_events&#x60; / &#x60;paddle_subscriptions&#x60; — and refuses anything that
would downgrade a live paid entitlement. Deliberately NOT &#x60;@Roles(&#x27;admin&#x27;)&#x60;: choosing the free
plan is something any user of the org does, and &#x60;admin&#x60; would not have been a meaningful gate
anyway (the merchant IS the admin of their own org).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SubscriptionSyncResponseDto,
  },
  {
    method: "patch",
    path: "/api/organizations/:id/supabase-reads",
    alias: "OrganizationsController_setSupabaseReads",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ enabled: z.boolean() }).passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OrganizationFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/organizations/compare/:firestoreId",
    alias: "OrganizationsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/organizations/reset-data",
    alias: "OrganizationsController_resetData",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ResetDataDto,
      },
    ],
    response: ResetDataResponseDto,
  },
  {
    method: "get",
    path: "/api/parity-audit/collections",
    alias: "ParityAuditController_collections",
    description: `The collections this audit knows how to field-compare (registry keys).`,
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/parity-audit/fleet",
    alias: "ParityAuditController_auditFleet",
    description: `Fleet-wide, bounded, resumable field-level audit. POST /api/parity-audit/fleet`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: FleetAuditDto,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/parity-audit/org",
    alias: "ParityAuditController_auditOrg",
    description: `Single-org, bounded, resumable field-level audit. POST /api/parity-audit/org`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OrgAuditDto,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/payment-methods",
    alias: "PaymentMethodsController_createPaymentMethod",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePaymentMethodDto,
      },
    ],
    response: PaymentMethodFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/payment-methods",
    alias: "PaymentMethodsController_getPaymentMethods",
    requestFormat: "json",
    response: z.array(PaymentMethodFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/payment-methods/:id",
    alias: "PaymentMethodsController_getPaymentMethodById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PaymentMethodFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/payment-methods/:id",
    alias: "PaymentMethodsController_updatePaymentMethod",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdatePaymentMethodDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PaymentMethodFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/payment-methods/:id",
    alias: "PaymentMethodsController_deletePaymentMethod",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/payment-methods/compare/:firestoreId",
    alias: "PaymentMethodsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/price-types",
    alias: "PriceTypesController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePriceTypeDto,
      },
    ],
    response: PriceTypeFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/price-types",
    alias: "PriceTypesController_findAll",
    requestFormat: "json",
    response: z.array(PriceTypeFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/price-types/:id",
    alias: "PriceTypesController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PriceTypeFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/price-types/:id",
    alias: "PriceTypesController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdatePriceTypeDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PriceTypeFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/price-types/:id",
    alias: "PriceTypesController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/price-types/compare/:firestoreId",
    alias: "PriceTypesController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/print-templates",
    alias: "PrintTemplatesController_getPrintTemplates",
    requestFormat: "json",
    parameters: [
      {
        name: "type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(PrintTemplateFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/print-templates",
    alias: "PrintTemplatesController_createPrintTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePrintTemplateDto,
      },
    ],
    response: PrintTemplateFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/print-templates/:id",
    alias: "PrintTemplatesController_getPrintTemplateById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PrintTemplateFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/print-templates/:id",
    alias: "PrintTemplatesController_updatePrintTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdatePrintTemplateDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PrintTemplateFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/print-templates/:id",
    alias: "PrintTemplatesController_deletePrintTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/print-templates/by-type-and-store",
    alias: "PrintTemplatesController_getPrintTemplatesByTypeAndStore",
    requestFormat: "json",
    parameters: [
      {
        name: "type",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(PrintTemplateFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/print-templates/compare/:firestoreId",
    alias: "PrintTemplatesController_compareWithFirestore",
    description: `Fetches print template from both databases and compares all fields to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "firestoreOrgId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        exists: z.boolean(),
        supabaseData: z.object({}).partial().passthrough().nullable(),
        firestoreData: z.object({}).partial().passthrough().nullable(),
        foundInSupabase: z.boolean(),
        foundInFirestore: z.boolean(),
        matches: z.boolean(),
        differences: z.array(z.any()),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/product-categories",
    alias: "ProductCategoriesController_createCategory",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateCategoryDto,
      },
    ],
    response: ProductCategoryFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/product-categories",
    alias: "ProductCategoriesController_getCategories",
    requestFormat: "json",
    parameters: [
      {
        name: "parentCategoryId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(ProductCategoryFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/product-categories/:id",
    alias: "ProductCategoriesController_getCategoryById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductCategoryFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/product-categories/:id",
    alias: "ProductCategoriesController_updateCategory",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateCategoryDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductCategoryFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/product-categories/:id",
    alias: "ProductCategoriesController_deleteCategory",
    description: `ATL-1009: pass &#x60;moveToCategoryId&#x60; (a Firestore id, or empty for &quot;no category&quot;) to re-point this category’s products in the SAME call. The app’s deleteProductCategory() re-points them in Firestore and previously told the backend only to delete, so Supabase kept every product pointing at a soft-deleted category.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "moveToCategoryId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/product-categories/compare/:firestoreId",
    alias: "ProductCategoriesController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/product-categories/tree",
    alias: "ProductCategoriesController_getCategoryTree",
    requestFormat: "json",
    response: z.array(ProductCategoryFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/product-modifiers",
    alias: "ProductModifiersController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateProductModifierDto,
      },
    ],
    response: ProductModifierFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/product-modifiers",
    alias: "ProductModifiersController_findAll",
    requestFormat: "json",
    response: z.array(ProductModifierFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/product-modifiers/:id",
    alias: "ProductModifiersController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductModifierFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/product-modifiers/:id",
    alias: "ProductModifiersController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateProductModifierDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductModifierFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/product-modifiers/:id",
    alias: "ProductModifiersController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/product-modifiers/compare/:firestoreId",
    alias: "ProductModifiersController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/product-movements",
    alias: "ProductMovementsController_getProductMovements",
    requestFormat: "json",
    parameters: [
      {
        name: "productId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z
          .enum(["sale", "supply", "revision", "transfer", "writeoff"])
          .optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "linkedDocId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "linkedDocType",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(ProductMovementFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/product-movements/window",
    alias: "ProductMovementsController_getProductMovementsWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "productId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "linkedDocId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "linkedDocType",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/product-units",
    alias: "ProductUnitsController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateProductUnitDto,
      },
    ],
    response: ProductUnitFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/product-units",
    alias: "ProductUnitsController_findAll",
    requestFormat: "json",
    response: z.array(ProductUnitFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/product-units/:id",
    alias: "ProductUnitsController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductUnitFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/product-units/:id",
    alias: "ProductUnitsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateProductUnitDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductUnitFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/product-units/:id",
    alias: "ProductUnitsController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/product-units/compare/:firestoreId",
    alias: "ProductUnitsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: ComparisonResultDto,
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/products",
    alias: "ProductsController_createProduct",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateProductDto,
      },
    ],
    response: ProductFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/products",
    alias: "ProductsController_getProducts",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "categoryId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "supplierId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(ProductFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/products/:id",
    alias: "ProductsController_getProductById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/products/:id",
    alias: "ProductsController_updateProduct",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateProductDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/products/:id",
    alias: "ProductsController_deleteProduct",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "patch",
    path: "/api/products/:id/stock",
    alias: "ProductsController_updateStock",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "userId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: ProductStockUpdateResponseDto,
  },
  {
    method: "get",
    path: "/api/products/barcode/:barcode",
    alias: "ProductsController_getProductByBarcode",
    requestFormat: "json",
    parameters: [
      {
        name: "barcode",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ProductFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/products/compare/:firestoreId",
    alias: "ProductsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/products/cost-cascade/bulk",
    alias: "ProductsController_updateCostCascadeBulk",
    description: `ATL-950 — the Supabase leg of the composite cost cascade.

POST, not PATCH, for the same reason as &#x60;stock/bulk&#x60; above: the frontend dual-write outbox
derives a fallback document id from the URL tail of PATCH/PUT/DELETE requests, and
&#x27;bulk&#x27; must never be adopted as a document identity.

One call per edited product, never one per composite — production fan-out reaches 158
composites for a single edit (p90 12, p99 49, measured 2026-08-24), and 158 sequential
round trips from a till on mobile data is not a write path.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkCostCascadeDto,
      },
    ],
    response: BulkCostCascadeResponseDto,
  },
  {
    method: "post",
    path: "/api/products/heal-missing",
    alias: "ProductsController_healMissingProducts",
    description: `ATL-794 — operator bulk heal for products present in Firestore but missing in Supabase.

NOT a user-facing route: @Public() bypasses the class-level FirebaseAuthGuard, and
MigrationTokenGuard locks it behind the &#x60;x-migration-token&#x60; header (fail-closed when the
token env is unset — same posture as the /migration routes, ATL-590). The migration
engine&#x27;s &#x60;reconcile: true&#x60; refusal for &#x60;products&#x60; is deliberate and stays; this endpoint
is the sanctioned path because every insert goes through the ATL-792 heal, which
ledger-anchors opening stock. Driven by scripts/backfill-missing-products-atl794.ts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: HealMissingProductsDto,
      },
    ],
    response: HealMissingProductsResponseDto,
  },
  {
    method: "get",
    path: "/api/products/search",
    alias: "ProductsController_searchProducts",
    requestFormat: "json",
    parameters: [
      {
        name: "q",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(ProductFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/products/stock-snapshots/:id",
    alias: "ProductsController_getProductStockSnapshot",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/products/stock/bulk",
    alias: "ProductsController_updateStockBulk",
    description: `ATL-765 — bulk absolute-stock endpoint. POST (not PATCH) is deliberate: the frontend
dual-write outbox derives a fallback document id from the URL tail of PATCH/PUT/DELETE
requests, and &#x27;bulk&#x27; must never be adopted as a document identity — a POST with no
&#x60;firestoreId&#x60; in body/context is refused loudly instead (the outbox admission invariant).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkStockUpdateDto,
      },
    ],
    response: BulkStockUpdateResponseDto,
  },
  {
    method: "post",
    path: "/api/products/store-settings/seed",
    alias: "ProductsController_seedStoreProductSettings",
    description: `ATL-790 — seed a NEW store&#x27;s per-product settings (copied retail price + visibility),
the Supabase counterpart of saveNewStore&#x27;s Firestore product backfill. POST for the same
outbox-identity reason as &#x60;stock/bulk&#x60;.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SeedStoreProductSettingsDto,
      },
    ],
    response: SeedStoreProductSettingsResponseDto,
  },
  {
    method: "post",
    path: "/api/purchase-methods",
    alias: "PurchaseMethodsController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePurchaseMethodDto,
      },
    ],
    response: PurchaseMethodFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/purchase-methods",
    alias: "PurchaseMethodsController_findAll",
    requestFormat: "json",
    response: z.array(PurchaseMethodFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/purchase-methods/:id",
    alias: "PurchaseMethodsController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PurchaseMethodFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/purchase-methods/:id",
    alias: "PurchaseMethodsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdatePurchaseMethodDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PurchaseMethodFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/purchase-methods/:id",
    alias: "PurchaseMethodsController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/purchase-methods/compare/:firestoreId",
    alias: "PurchaseMethodsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/reconciliation/sweep",
    alias: "ReconciliationController_runSweep",
    description: `Manually trigger a reconciliation sweep.
POST /api/reconciliation/sweep

Guarded by &#x60;MigrationTokenGuard&#x60; (x-migration-token header must match
MIGRATION_ADMIN_TOKEN). The &#x60;@Public()&#x60; decorator bypasses the global
FirebaseAuthGuard — token-guard is the sole access control here.

Body accepts &#x60;SweepOptions&#x60; (all fields optional, defaults apply).

READING THE RESULT (ATL-703): a default run only reconciles orgs active within
RECONCILE_ACTIVE_SINCE_DAYS (7) — on prod that is ~117 of ~268 live migrated orgs. Therefore
&#x60;orgsErrored &#x3D;&#x3D;&#x3D; 0&#x60; does NOT mean the fleet is clean. Read &#x60;coverage&#x60;:
  - &#x60;coverage.fleetComplete&#x60; — the ONLY field that may be read as &quot;the whole fleet is clean&quot;.
  - &#x60;coverage.fleetCompleteBlockers[]&#x60; — when it is false, every named reason why. Empty ⇔ true.
  - &#x60;coverage.orgsLiveTotal / orgsEnumerated / orgsAttempted / orgsSucceeded&#x60; — the funnel.
    &#x60;orgsLiveTotal&#x60; EXCLUDES migration-exempt orgs (&#x60;orgsExempt&#x60; counts them separately) and
    soft-deleted orgs (each probed against Firestore and reported in &#x60;skipped[]&#x60; — an org
    Supabase deleted but Firestore still has BLOCKS the gate rather than vanishing from it).
  - &#x60;coverage.skipped[]&#x60; — every org NOT covered, with the named mechanism that excluded it
    (inactive-window, null-firestore-id, not-in-requested-org-filter, budget-exhausted,
    halted-before-attempt, migration-exempt, org-deleted-both-sides,
    org-soft-deleted-but-live-in-firestore — migration-exempt carrying each org&#x27;s stated reason).
    Only &#x60;migration-exempt&#x60; and &#x60;org-deleted-both-sides&#x60; can coexist with a green &#x60;fleetComplete&#x60;;
    each states a FACT that removes the reconciliation obligation (a written human decision; an
    org the product has deleted on BOTH sides, its Firestore organizations document verified
    absent this run — which does NOT mean the org holds no data: deleted Firestore documents
    leave their subcollections orphaned, and those are not inspected here). Every other entry
    blocks.

THE PRE-CUTOVER FLEET AUDIT is
&#x60;{&quot;fullBaseline&quot;: true, &quot;pruneOrphans&quot;: true, &quot;ledgerCheck&quot;: true}&#x60; — a LIVE, UNFILTERED run
that also opens the books. Precisely: &#x60;fullBaseline&#x60; and &#x60;ledgerCheck&#x60; are required
unconditionally (without them &#x60;not-full-baseline&#x60; / &#x60;ledger-not-checked&#x60; block the gate no matter
what the data says), while &#x60;pruneOrphans&#x60; blocks only if the run actually FINDS prunable orphans
(&#x60;unpruned-orphans&#x60; counts &#x60;totalPrunableUnprunedOrphans&#x60; — ATL-822) — which you cannot know before running, so the
audit call always includes it. Note what each option costs you, because each was a way the gate
used to read green while data was wrong:
  - &#x60;&quot;dryRun&quot;: true&#x60; heals nothing and suppresses the ATL-664 insert accounting entirely, so it
    can only ever REPORT drift (&#x60;totalWouldBackfill&#x60;), never certify its absence → blocked as
    &#x60;dry-run&#x60;. It is the right call for &quot;what would this change?&quot;, not for &quot;are we clean?&quot;.
  - omitting &#x60;&quot;pruneOrphans&quot;&#x60; (the default) leaves confirmed phantom Supabase rows in place →
    blocked as &#x60;unpruned-orphans&#x60;. ATL-796: two by-design classes are EXCLUDED from that count
    and never pruned — converged deletions (FS doc hard-deleted, SB row soft-deleted: the
    designed delete propagation; reported as &#x60;convergedDeletes&#x60;) and &#x60;placeholder_*&#x60;
    FK-recovery stubs (FS-absent by construction, often FK-load-bearing; reported as
    &#x60;fkPlaceholders&#x60;). Before ATL-796 they were ~95% of &#x60;wouldPrune&#x60; (1,639 on 2026-07-29) and
    the prune&#x27;s hard-delete errored on their FK children.

WHICH ROWS A PRUNE WOULD DELETE (ATL-814). &#x60;wouldPrune&#x60; was a bare integer, so &#x60;pruneOrphans&#x60;
— which the nightly passes unconditionally and which hard-&#x60;delete()&#x60;s — was a destructive
operation whose targets could not be inspected before authorising it. The number was 1,639 and
~95% wrong a week before it was 43; the response now names the rows. Four fields, on every run:
  - &#x60;totalWouldPruneByCollection&#x60; (and per org, &#x60;wouldPruneByCollection&#x60;) — the count split by
    Firestore collection. Only NON-ZERO entries appear, so an absent/empty map means the same
    thing a zero count means. Which collection a row is in is the first fact that decides whether
    deleting it is safe. &#x60;totalConvergedDeletesByCollection&#x60; / &#x60;totalFkPlaceholdersByCollection&#x60;
    do the same for the two EXCLUDED classes: why a row was spared is as reviewable as why one
    would be deleted.
  - &#x60;pruneItemisation&#x60; — the rows themselves (&#x60;collection&#x60;, &#x60;table&#x60;, &#x60;id&#x60;, &#x60;firestoreId&#x60;,
    &#x60;prunable&#x60;), per org and rolled up on the summary (which adds &#x60;byOrg&#x60; and &#x60;orgFirestoreId&#x60;
    per row).
  - &#x60;pruneItemisation.refusedCount&#x60; — HOW MANY OF &#x60;totalCount&#x60; A LIVE PRUNE WOULD NOT DELETE.
    The prune skips the report-only collections (&#x60;products&#x60;, &#x60;complexSales&#x60;, &#x60;users&#x60;) and the
    ATL-707 stock-affecting parents (&#x60;sales&#x60;, &#x60;supplies&#x60;, &#x60;writeOffs&#x60;, &#x60;revisions&#x60;, &#x60;transfers&#x60;,
    &#x60;manufactures&#x60;, &#x60;returns&#x60;), but &#x60;wouldPrune&#x60; counts them anyway. On prod 2026-08-02 that was
    18 of 44 fleet-wide — 13 of them live &#x60;products&#x60; rows still carrying stock.
    &#x60;totalCount - refusedCount&#x60; is what a prune would actually attempt; each row also says
    &#x60;prunable&#x60; and, when false, why. This is reported, not netted out of &#x60;wouldPrune&#x60;:
    redefining the count would redefine the gate.

THE BLOCKER VOCABULARY FOR DRIFT SPLITS ON WHO CAN CLEAR IT (ATL-822). Refused-by-design drift
used to block under &#x60;unpruned-orphans&#x60; / &#x60;unhealed-missing-rows&#x60; / &#x60;success-gap&#x60;, which made
those three blockers unclearable by any correct work (their only remedy was a delete/insert the
reconciler itself forbids) — and a gate that cannot be satisfied gets overridden, at which point
every blocker it reports becomes noise. The split:
  - &#x60;unpruned-orphans&#x60; now means PRUNABLE orphans left unpruned (&#x60;totalPrunableUnprunedOrphans&#x60;)
    — running the prune clears it. &#x60;unhealed-missing-rows&#x60; means HEALABLE rows still missing
    (&#x60;totalUnhealedMissing&#x60;) — running a live backfill clears it. NOTE: refused missing rows
    were never counted by &#x60;unhealed-missing-rows&#x60;; before ATL-822 they were &#x60;continue&#x60;d into
    &#x60;reportOnlyDrift&#x60; and blocked via &#x60;success-gap&#x60;. &#x60;totalUnhealedMissing&#x60; is therefore
    UNCHANGED across ATL-822 — the split quantified a hidden population, it did not move a count.
  - &#x60;refused-orphans&#x60; / &#x60;refused-missing-rows&#x60; are the refused halves
    (&#x60;totalRefusedOrphans&#x60; / &#x60;totalRefusedMissing&#x60;, each with a &#x60;...ByCollection&#x60; map). BOTH
    STILL BLOCK THE GATE — a refusal says who may fix a row, never that the row is right, and
    every one is read-visible divergence at cutover. Their remedies are the collection&#x27;s own
    heal paths: the ATL-707 movement-aware operator backfill for refused missing rows,
    deleted_at propagation (future work) for refused orphans.
    ⚠️ &#x60;totalRefusedMissing&#x60; COUNTS CLASSIFIED REFUSALS ONLY — see the ATL-829 block below
    before sizing anything from it.
    ATL-836 added a third cause with a different remedy and a different shape: &#x60;transactions&#x60;
    in an org holding live rows with &#x60;firestore_id IS NULL&#x60; is refused per ORGANIZATION (the
    row is unaddressable, not missing — ATL-821) and healed by the identity remediation, which
    clears MOST of it with no code change but NOT all: 367 live rows fleet-wide stay unkeyed
    after it runs by design (ATL-823 &#x60;holder-disagrees-on-money&#x60; has a forward fix and no
    backfill script), bounding up to 28 orgs that stay refused until those rows are decided.
    It is also the only INSERT-ONLY refusal: the same collection&#x27;s orphans stay in the prunable
    half, so an org can report &#x60;refused-missing-rows&#x60; with &#x60;refusedOrphans: 0&#x60;. Each
    collection&#x27;s &#x60;backfillRefused&#x60; string names its own remedy — read it, don&#x27;t infer it.
  - &#x60;success-gap&#x60; no longer fires for an org whose ONLY shortfall is refusal
    (&#x60;coverage.orgsShortfallRefusalOnly&#x60; counts those; &#x60;orgsSucceeded&#x60; still excludes them — the
    org is measured-and-refused, not clean). A big-drift skip is NOT a refusal (it was abandoned
    BEFORE the orphan confirm — nothing measured; per-org &#x60;bigDriftCollections&#x60; vs
    &#x60;refusedCollections&#x60; carry the distinction) and keeps producing &#x60;success-gap&#x60;, as does any
    insert failure, collection error, unhealed row or prunable orphan alongside a refusal.
  - &#x60;totalUnprunedOrphans&#x60; is FAIL-CLOSED: an org result that reports &#x60;unprunedOrphans&#x60; without
    the split fields counts wholly as prunable — an unmeasured split must never read as &quot;all
    refused&quot;. It is NOT reliably the arithmetic sum of the two halves: &#x60;unprunedOrphans&#x60; and
    &#x60;prunableUnprunedOrphans&#x60; are floored at zero independently and org-wide, so an org that
    over-prunes one collection (duplicate &#x60;firestore_id&#x60;s — ATL-799) can show
    &#x60;totalUnprunedOrphans: 0&#x60; while &#x60;totalRefusedOrphans&#x60; is non-zero. Read the halves when
    they disagree; &#x60;refused-orphans&#x60; still fires, so the gate stays red.
  - The classifier keys on the migration layer&#x27;s &#x60;backfillRefused&#x60; / &#x60;orphanPruneRefusal&#x60;
    fields, never on &#x60;skipped&#x60; alone (&#x60;skipped: &#x27;big-drift&#x27;&#x60; is an abandonment, and an
    unclassifiable &#x60;skipped&#x60; fails closed into the big-drift bucket). A refused collection that
    ALSO has missing rows keeps its full orphan itemisation (it was measured; the refusal is
    about writing, not measuring).

⚠️⚠️ THE DRIFT COUNTERS ARE LOWER BOUNDS, AND THE RESPONSE NOW SAYS WHEN AND WHY (ATL-829).
&#x60;totalRefusedMissing&#x60;, &#x60;totalUnhealedMissing&#x60;, &#x60;totalWouldBackfill&#x60;, &#x60;totalUnmeasuredMissing&#x60; and
&#x60;totalWouldPrune&#x60; are totals only over what this run actually opened. TWO FIELDS GOVERN ALL OF
THEM, and they come FIRST:
  - &#x60;totalRefusedMissingIsLowerBound&#x60; — READ THIS BEFORE ANY DRIFT NUMBER. &#x60;false&#x60; is a real
    completeness claim: no mechanism this run can observe bounded its counters.
  - &#x60;totalRefusedMissingLowerBoundReasons[]&#x60; — every such mechanism behind a &#x60;true&#x60;. EMPTY ⇔
    the flag is false. MOSTLY DERIVED, NOT HAND-WRITTEN: every &#x60;fleetCompleteBlockers&#x60; entry is
    emitted verbatim as a reason unless &#x60;DRIFT_BOUND_BY_BLOCKER&#x60; classifies it as not bounding a
    row count, with a stated justification (&#x60;dry-run&#x60; measures everything it does not write;
    &#x60;success-gap&#x60; means an org was measured and is merely unclean; &#x60;unhealed-missing-rows&#x60; IS a
    counter; the ledger/constants blockers are other surfaces). Three review rounds each found
    one more blocker missing from a hand-maintained list, so the compiler maintains it now.
    Plus the mechanisms no blocker models: &#x60;orgs-errored&#x60;/&#x60;orgs-timed-out&#x60;/&#x60;orgs-deferred&#x60; (those
    branches emit NO drift fields — an absence, not a zero), &#x60;orgs-not-reached&#x60;,
    &#x60;orgs-accounting-absent&#x60; (a reconciled org that reported no refused/unmeasured split at all),
    and &#x60;coverage-skipped:&lt;reason&gt;&#x60;.
    ⚠️ ON A DEFAULT RUN THIS IS ALWAYS NON-EMPTY, and that is the point. A non-&#x60;fullBaseline&#x60;
    sweep enumerates only the active window — on prod ~116 of 306 live orgs — so it reports
    &#x60;coverage-skipped:inactive-window&#x60; and every drift number it prints is a floor over 38% of
    the fleet.
    ⚠️ A REASON IS NOT A BLOCKER, and &#x60;org-deleted-both-sides&#x60; is the case that shows why. It
    does NOT block the gate — deleting an org on both sides removes the reconciliation
    obligation — and it IS a reason, because it does not remove the ROWS: all four soft-deleted
    prod orgs hold 44 documents in diffable collections between them (RIZA FLOWERS 26, Underwood
    Decor 6, two test orgs 6 each), so a &#x60;fullBaseline&#x60; run&#x27;s &#x60;totalWouldBackfill&#x60; is short by at
    least that. Only &#x60;migration-exempt&#x60; is exempt from BOTH, because a written exemption
    redefines the denominator (&#x60;orgsLiveTotal&#x60; excludes it) rather than excusing an action.
    ⚠️ AND &#x60;[]&#x60; IS NOT &quot;EXACT&quot;. Four bounds have no mechanism in the gate: the fixed
    &#x60;PARITY_COLLECTIONS&#x60; list (disclosed permanently in &#x60;coverage.notCovered[]&#x60; — see below);
    soft-deleted Supabase rows returned as PRESENT by the id fetch, so a live Firestore doc whose
    Supabase row is soft-deleted never reads as missing; the &#x60;fetchAllFirestoreIds&#x60; &#x60;documentId&#x60;
    paging race (the missing side has no re-confirmation pass, the orphan side does); and any
    other drop INSIDE a cleanly-diffed collection. The last is what &#x60;fk-orphan-skipped&#x60; was until
    it became a blocker — which is the intended way to add one: teach the gate, and the reason
    follows. &#x60;[]&#x60; means &quot;nothing this run can observe bounded these numbers&quot;.

⛔ &#x60;totalUnhealedMissing&#x60; IS IDENTICALLY ZERO ON A LIVE RUN, BY CONSTRUCTION (round 5). Per
collection, with &#x60;X &#x3D; attempted − migrated − skippedExisting − skippedDuplicate − skippedOrphan&#x60;,
the migration layer sets &#x60;failedToInsert &#x3D; max(0, X)&#x60;, and
&#x60;wouldBackfill − backfilled − benignSkipped − insertFailures&#x60; reduces to &#x60;max(0, X − max(0, X))&#x60;
— zero for every X. &#x60;failedToInsert&#x60; absorbs the whole residual, so &#x60;unhealed-missing-rows&#x60; can
only fire on a DRY run, where &#x60;dry-run&#x60; blocks the gate regardless.
  READ THE CONSEQUENCE, not the algebra: the only run that can ever be green is a live one, and
  on a live run the sole thing between a missing row and a green gate is WHICH NAMED BUCKET the
  row lands in. A row in no bucket is invisible. That is why the list below exists and keeps
  growing, and why &quot;is this population benign?&quot; is the highest-stakes question in this module.

ROWS THE BACKFILL DROPS ARE NOT &quot;SKIPPED&quot; (round 4, corrected in round 5). Two counters, both
sitting inside &#x60;benignSkipped&#x60; and therefore both subtracted out of &#x60;totalUnhealedMissing&#x60;:
  - &#x60;totalNaturalKeyDuplicatesDropped&#x60; (per org &#x60;naturalKeyDuplicatesDropped&#x60;, with
    &#x60;...ByCollection&#x60;) — rows dropped to satisfy a NATURAL-KEY unique constraint. THIS IS THE ONE
    THAT FIRES. Three of &#x60;skippedDuplicate&#x60;&#x27;s four producers drop a DISTINCT Firestore document
    whose id is never written: &#x60;transactions&#x60;/&#x60;unique_transaction&#x60; (in-batch and cross-page), and
    the &#x60;suppliers&#x60; (&#x60;name&#x60;) / &#x60;product_categories&#x60; (&#x60;parent_category_id, name&#x60;) dedup. The next
    sweep finds the document missing and the dedup drops it again — permanent. Live on prod:
    &#x60;Aks__Bro&#x60; has three Firestore supplier documents and one Supabase row. The fourth producer
    (two rows sharing one &#x60;firestore_id&#x60;) IS benign and cannot occur here — the missing set is a
    &#x60;Set&#x60; of ids and &#x60;getAll&#x60; returns one snapshot per id.
    REMEDY: reconcile the duplicate IN FIRESTORE (merge or rename). Never &quot;re-run the backfill&quot; —
    the app&#x27;s own uniqueness invariant is what drops the row, so a backfill re-drops it.
  - &#x60;totalFkOrphanSkipped&#x60; (per org &#x60;fkOrphanSkipped&#x60;) — rows dropped because a foreign key had
    no parent (ATL-588). ⚠️ STRUCTURALLY ZERO ON A SWEEP TODAY: its only producer is gated to
    &#x60;returns&#x60;, which ATL-707 refuses, so &#x60;insertDocsBatch&#x60; is never reached for it. Kept as a
    live guard because the refusal set is data, not structure — do not read it as load-bearing.
Neither is folded into &#x60;unhealed-missing-rows&#x60;, deliberately: that blocker promises a live
backfill clears it, and a live backfill re-drops both. &#x60;skippedExisting&#x60; and
&#x60;skippedDeletedFromSource&#x60; are genuinely not-missing and stay uncounted.

WHAT THE VALVE SPECIFICALLY DOES, since it is the mechanism this ticket is named for. The
big-drift valve (&#x60;maxMissing&#x60;, &#x60;RECONCILE_MAX_ORG_MISSING&#x60;, default 5,000) abandons a collection
as soon as the set-diff finds more missing rows than the cap — and it does so BEFORE the refusal
classification runs. So those rows enter NO classified counter. Марракеш&#x27;s 27,325 refused
&#x60;writeOffs&#x60; contributed ZERO to a number operators were sizing the ATL-707 backfill from. Four
more fields, on every run:
  - &#x60;totalUnmeasuredMissing&#x60; (per org, &#x60;unmeasuredMissing&#x60;) — the abandoned rows, counted. The
    COUNT is exact: the id-set diff completed in full before the valve fired. What is unmeasured
    is the CLASSIFICATION — whether a backfill would insert them or refuse them was never
    resolved. That is why they are not folded into &#x60;totalRefusedMissing&#x60;: the run did not earn
    that claim, and filing an unclassified population into a classified counter would rebuild
    the same false total one level down.
  - &#x60;totalUnmeasuredMissingCountUnknown&#x60; — abandonments where even the count was not recorded.
    Non-zero ⇒ &#x60;totalUnmeasuredMissing&#x60; is itself a lower bound. Unknown is not zero, here too;
    an org reporting &#x60;bigDriftCollections&#x60; with no ATL-829 accounting counts ALL of them here.
  - &#x60;bigDriftAbandonments&#x60; — org + collection + observed missing count + orphan candidates + the
    &#x60;maxMissing&#x60; in force. Read &#x60;reason&#x60; before &#x60;orphanCandidates&#x60;: on &#x60;&#x27;big-drift&#x27;&#x60; it is the
    RAW unconfirmed candidate count (an upper bound), on the fail-closed branch it is whatever
    that producer recorded, which for a refusal-shaped skip is the CONFIRMED count. Sorted
    unknown-count first, then largest first, capped at 200 with &#x60;bigDriftAbandonmentsTruncated&#x60;.
    The counters above are exact regardless of that cap — but the CAP IS NOT A PAYLOAD BOUND:
    &#x60;results[]&#x60; carries an uncapped per-org array for every swept org (~3.2 MB fleet worst case,
    the same stacking the prune itemisation discloses). Narrow with &#x60;orgIds&#x60;.
  - &#x60;totalBigDriftCollections&#x60; — how many collections were abandoned fleet-wide.

TO SIZE THE ATL-707 BACKFILL: read &#x60;totalRefusedMissing + totalUnmeasuredMissing&#x60; as a RANGE —
and check &#x60;totalRefusedMissingLowerBoundReasons&#x60; before trusting even the range, because a
default run&#x27;s range is a floor over the active window only. To collapse the range for an org,
take the measurement DRY:

    {&quot;fullBaseline&quot;: true, &quot;dryRun&quot;: true, &quot;maxMissing&quot;: 300000, &quot;orgIds&quot;: [&quot;&lt;org&gt;&quot;]}

&#x60;maxMissing&#x60; is a request option (ATL-829) precisely so this does not require a Cloud Run
revision — which would raise the valve GLOBALLY, including for the nightly, which passes
&#x60;pruneOrphans: true&#x60;. It must be a FINITE NON-NEGATIVE INTEGER (&#x60;null&#x60;/&#x60;NaN&#x60;/&#x60;Infinity&#x60;/negative/
fractional are 400s, checked before the sweep lock is taken). The ENV path
(&#x60;RECONCILE_MAX_ORG_MISSING&#x60;) is validated too and fails SOFT to the 5,000 default with a logged
error: &#x60;ConfigModule&#x60; has no &#x60;validationSchema&#x60;, so it hands back the raw env STRING, and
&#x60;RECONCILE_MAX_ORG_MISSING&#x3D;&quot;off&quot;&#x60; used to reach the set-diff as &#x60;&#x27;off&#x27;&#x60; — where &#x60;27325 &gt; &#x27;off&#x27;&#x60;
is false, silently disabling the valve on the nightly&#x27;s own path. There is no SENTINEL that turns the
valve off — but there is no ceiling either, and any value above a collection&#x27;s size is
EQUIVALENT to disabling it, so on a run without &#x60;dryRun&#x60; a large cap authorises every insert the
per-org timeout allows. The validation buys auditability (the cap is always a number you can
read back), not safety. The orgs the completeness census says dominate (4 orgs &#x3D; 94% of 317,415
missing docs) are exactly the ones large enough to trip the cap.
  ⚠️ NOTE THE CALL ABOVE SETS &#x60;collections&#x60;, so it reports &#x60;collections-filtered&#x60; and its own
  numbers are floors over that one collection. That is correct and intended for sizing ONE
  collection — just do not carry the resulting number back as a fleet total.

GATE EFFECT OF &#x60;big-drift-abandoned&#x60;, stated honestly in both directions. It does NOT make the
gate stricter: an abandoned collection already increments &#x60;reportOnlyDrift&#x60;, which excludes the
org from &#x60;orgsSucceeded&#x60; and from &#x60;orgsShortfallRefusalOnly&#x60;, so &#x60;success-gap&#x60; was already firing
on every such run and &#x60;fleetComplete&#x60; is byte-identical across ATL-829. What changes is that the
gate NAMES the abandonment and the work that clears it.
  ⚠️ AND THE DRY MEASUREMENT ABOVE DOES NOT CLEAR IT. &#x60;dry-run&#x60; is itself a blocker, so the call
  that safely learns the number can never green the gate; clearing this blocker in a certifying
  run needs a LIVE run with the cap raised, and on the ATL-707 collections that is exactly the
  write the reconciler refuses to make. Raise the cap to LEARN the population. Do not read &quot;the
  gate went green&quot; as the goal — the remedy for those rows is still the movement-aware operator
  backfill, and the blocker is naming a measurement you can take, not a delete you must risk.

READ &#x60;pruneItemisation.complete&#x60; BEFORE &#x60;pruneItemisation.rows&#x60;. It is the only field that
licenses reading &#x60;rows&#x60; as the whole delete target:
  - &#x60;totalCount: 0, complete: true, rows: [], gaps: []&#x60; — an affirmative &quot;nothing to prune&quot;.
  - &#x60;complete: true&#x60; with rows — that IS the full target list.
  - &#x60;complete: false&#x60; — &#x60;rows&#x60; is a SAMPLE, and &#x60;gaps[]&#x60; names every reason it is short: a cap,
    a sample read that failed, a collection whose set-diff threw (orphans never computed), a
    collection skipped as big-drift (candidates never Firestore-confirmed), an org that timed
    out or errored, or — at the fleet level — a COVERAGE hole: orgs this run never reached
    (&#x60;budget-exhausted&#x60;, &#x60;halted-before-attempt&#x60;, &#x60;inactive-window&#x60;, &#x60;orgIds&#x60; narrowing, …) and
    a &#x60;collections&#x60; filter. Coverage counts because &#x60;results[]&#x60; holds only the orgs that were
    swept: without it, a run truncated before its first org returned the affirmative zero above
    while measuring nothing. Only the two skips that may coexist with a green &#x60;fleetComplete&#x60;
    (&#x60;migration-exempt&#x60;, &#x60;org-deleted-both-sides&#x60;) are exempt. A short list that reads as
    complete is the failure mode this module keeps hitting (ATL-723), so &#x60;gaps&#x60; is exhaustive by
    construction and &#x60;complete&#x60; is derived from it.
The itemisation is ABSENT only when the sweep produced no result object for that org at all —
&#x60;{&quot;totalCount&quot;: 0, &quot;complete&quot;: true}&#x60; and &quot;not measured&quot; are different payloads, never the same
zero.

WHAT IT DOES NOT GUARANTEE:
  - It is a SNAPSHOT taken during the diff, not a lock. A later prune may hit a different set —
    rows re-created by a live dual-write, or removed by something else. &#x60;id: null&#x60; on a sample
    row already records one such case: counted by the diff, gone by the time the sample read ran.
  - It lists the PRUNABLE class only. Converged soft-deletes and &#x60;placeholder_*&#x60; stubs are
    counted, never listed, because they are never delete targets.
  - It counts differently from the prune. &#x60;wouldPrune&#x60; counts distinct &#x60;firestore_id&#x60;s; the
    prune deletes BY &#x60;firestore_id&#x60;, so where a table holds duplicates (ATL-799 has produced
    them in prod) one counted orphan removes several ROWS. The sample lists rows, so two rows
    sharing one &#x60;firestoreId&#x60; is that discrepancy made visible rather than a bug in the sample.
  - It changes nothing. Itemisation is detection-only: it runs after the classification, reads
    only primary keys, and a failure is recorded as a gap — it can never alter what is pruned.
  - BOUNDS: 20 rows per collection, 50 per org, 500 per fleet run. The fleet numbers bound
    &#x60;summary.pruneItemisation&#x60; ONLY (≤ ~850 KB): the response also carries &#x60;results[]&#x60;, one
    per swept org, each with its OWN ≤50 rows and ≤50 gaps — so the response as a whole grows
    with the fleet, not with the fleet cap (measured worst case: 268 orgs × 50 refused rows ≈
    12.6 MB, because a refused row also carries its ~340-char &#x60;pruneRefusalReason&#x60;). Today&#x27;s
    population is 44 rows fleet-wide. Narrow with &#x60;orgIds&#x60; to itemise past a cap. The same
    numbers cap &#x60;gaps&#x60;. Everything is also emitted to PostHog
    (&#x60;reconciliation_org.prune_sample&#x60;, &#x60;reconciliation_sweep.prune_by_org&#x60;), because a fleet
    sweep outlives its HTTP client and a review nobody receives fixes nothing.
  - &#x60;&quot;collections&quot;: [...]&#x60; diffs a subset — the other collections are UNKNOWN, not clean →
    blocked as &#x60;collections-filtered&#x60;. An unknown collection name is rejected outright (400)
    rather than silently diffing nothing.
  - &#x60;&quot;orgIds&quot;: [...]&#x60; narrows the fleet → blocked as &#x60;coverage-skipped:not-in-requested-org-filter&#x60;.
  - omitting &#x60;&quot;ledgerCheck&quot;&#x60; (the default) leaves the STOCK LEDGER unread → blocked as
    &#x60;ledger-not-checked&#x60;. &#x60;product_movements&#x60; is excluded from the id-level set-diff by design
    (live dual-write rows carry a NULL &#x60;firestore_id&#x60;, so an id backfill would duplicate every
    live movement and DOUBLE the org&#x27;s stock), and its Firestore side costs one document read
    per movement — ~2.37M fleet-wide. The check is detection-only and can never write. Per-(
    product,store) stock IS &#x60;SUM(increment)&#x60; over that table, so a run without it says nothing
    about the books, and &#x60;movementLedgerCoverage&#x60; states that in numbers.

WHAT &#x60;fleetComplete&#x60; STILL DOES NOT COVER: &#x60;coverage.notCovered[]&#x60;, present on every response,
names the surfaces THIS RUN did not compare — read it before promoting &quot;clean&quot; to &quot;everything
agrees&quot;. ATL-797 gave the &#x60;constants&#x60; fan-out (wallets, payment methods, roles, spending
categories, units, modifiers, recurring spendings) an always-on per-constant check —
&#x60;constantsCoverage&#x60; states its numbers, gaps block as &#x60;constants-not-checked&#x60; and found drift as
&#x60;constants-drift&#x60;. ATL-798 removed the last PERMANENTLY uncovered pair —
&#x60;constants/saleSettings&#x60; and &#x60;constants/loyaltySettings&#x60; were excluded because
&#x60;migrateConstants&#x60; had NEVER written them (its &#x60;Array.isArray(data)&#x60; gate skipped saleSettings&#x27;
per-store object and loyaltySettings&#x27; flat doc), so comparing them would have reported drift no
heal could clear; the write path is now real and both are compared (&#x60;doc-per-store&#x60;, matched on
the STORE, with the delivery wallet and spending category compared after &#x60;.id&#x60; extraction). One
uncovered surface remains and &#x60;fleetComplete&#x60; can be true while it exists:
  - &#x60;firestore subcollections outside PARITY_COLLECTIONS&#x60; (ATL-829 round 4). The set-diff
    targets a FIXED list — 24 parity collections plus &#x60;users&#x60; — so any other Firestore
    subcollection is never queried, contributes 0 to every drift counter on every run, and its
    absence is indistinguishable from being in sync. Known unmapped on prod: &#x60;subscriptions&#x60;,
    &#x60;cancelSurveys&#x60; (which HAS a Supabase table). &#x60;revenueCatEvents&#x60; was on this list until
    ATL-862 added it to &#x60;PARITY_COLLECTIONS&#x60;. THIS IS THE ATL-723 MECHANISM:
    &#x60;writeOffs&#x60; misspelled &#x60;writeoffs&#x60; in that same list read zero for months while every counter
    said clean, and the fleet turned out to be short 317,415 documents. Today&#x27;s volume outside
    the list is tens of documents; the mechanism is the part that has been expensive.
Additionally, a compared constant may be only PARTIALLY id-verified: id-less Firestore items get
a random synthetic &#x60;firestore_id&#x60; per migration run (ATL-799), so they are covered by the exact
count alone, never by the id diff. Such constants carry &#x60;partialCoverageReason&#x60; and are
disclosed in &#x60;notCovered&#x60;.

HOW TO READ &#x60;ledger-drift&#x60; (ATL-806). Every drifted org now carries
&#x60;movementLedger.deletedParents&#x60;: the operations named by a Firestore movement that are absent
from Supabase AND VERIFIED absent from Firestore by a document probe. It exists because &quot;that is
just deleted operations&quot; was the standing, unfalsifiable explanation for ledger drift. It is an
ATTRIBUTION and it deliberately does NOT move &#x60;inSync&#x60; — compare &#x60;firestoreMovements&#x60; against
&#x60;supabaseCounterpart&#x60;: on prod (2026-08-02) 4,460 of Charge lab 2.0&#x27;s 4,492 deleted-parent
movements are PRESENT in Supabase as ATL-717 dangling rows, so that population contributes zero
drift, and excluding it from the Firestore side alone would have INVENTED 3,873 movements of
drift.

Four things to check before drawing a conclusion from those two numbers, all of them fields:
  - &#x60;status: &#x27;not-computed&#x27;&#x60; means the probe did not run. It is never a zero.
  - &#x60;supabaseCounterpart: null&#x60; means UNMEASURED. Also never a zero — and a zero counterpart is
    precisely the reading that makes a benign population look like loss.
  - &#x60;counterpartMatchesComparedPopulation: false&#x60; ⇒ the counterpart was measured without the
    comparison&#x27;s holdouts, so it can only be too LARGE: read the overlap as an upper bound.
  - &#x60;unprobedParents &gt; 0&#x60; ⇒ the numbers are a FLOOR (&#x60;stoppedEarly&#x60; says why). Candidates are
    probed heaviest-first, so what is missing is the long tail.

&#x60;aliveParents&#x60; is the OPPOSITE finding and is NOT noise: the operation exists in Firestore and
is missing from Supabase — a completeness gap this ledger check does not cover (it compares
movements, not operations).

NEGATIVE STOCK (ATL-818) — &#x60;negativeStockCoverage&#x60;, ALWAYS-ON, AND DELIBERATELY NOT A GATE.
A &#x60;(product, store)&#x60; cell whose &#x60;SUM(increment)&#x60; over &#x60;product_movements&#x60; is below zero holds
stock that cannot exist. This is the one finding in the whole response that needs no
interpretation: &#x60;ledger-drift&#x60; above is a Firestore↔Supabase FIDELITY comparison, and per
ATL-708 &quot;differs from Firestore&quot; is genuinely arguable because Firestore&#x27;s own stock is
client-side and non-transactional. &quot;Is below zero&quot; is not arguable, and it needs no Firestore
side at all — it is a Supabase-internal question, one aggregate query per org.

It exists because &#x60;ledger-drift&#x60; was measured failing as a cutover screen in BOTH directions
(ATL-818, 2026-08-02): 58 orgs held negative Supabase stock while not being ledger-drifted at
all, and 20 drifted orgs held none. Prod baseline 2026-08-03: 109 orgs, 5,195 cells, worst
−340,533 (ресторан Марракеш).

  ⛔ IT DOES NOT AFFECT &#x60;fleetComplete&#x60;, AND THAT IS A DECISION, NOT AN OVERSIGHT. No value in
  &#x60;negativeStockCoverage&#x60; can produce a &#x60;fleetCompleteBlockers&#x60; entry, and negative stock is
  also excluded from &#x60;orgReconciledCleanly&#x60; (which feeds &#x60;orgsSucceeded&#x60; → &#x60;success-gap&#x60;, the
  non-obvious back door). Both are pinned by tests. The reason is ATL-822: the gate already
  carries blockers no correct work can clear — 41% of &#x60;wouldPrune&#x60; is refused-but-counted
  (ATL-814) and 587 refused-by-design rows are counted into &#x60;unhealedMissing&#x60; (ATL-820) — and a
  gate that cannot go green through legitimate work gets OVERRIDDEN rather than satisfied, at
  which point its REAL blockers become noise too. Fix what the existing blockers mean first;
  then decide whether this one should gate, on the numbers this metric exists to produce.

READ &#x60;orgsMeasured&#x60; BEFORE ANY OTHER FIELD HERE. It is the only denominator the rest are true
over, and &#x60;orgsWithNegativeStock: 0&#x60; looks identical for a clean fleet and for a run that
measured nothing. The buckets are exhaustive:
&#x60;orgsEnumerated &#x3D; orgsMeasured + orgsNotMeasured + orgsNotReached&#x60;.
  - &#x60;gaps[]&#x60; names every reason the metric covers less than the whole fleet (orgs not reached,
    orgs not measured, each sample cap). Empty ⇔ every enumerated org was measured and the
    sample is whole.
  - &#x60;worstStock&#x60; is &#x60;null&#x60;, never &#x60;0&#x60;, when no measured org has a negative cell — &#x60;0&#x60; is a value
    the aggregate cannot return, so it would be a fabricated measurement. Per org, likewise:
    &#x60;status: &#x27;checked&#x27;, cellCount: 0&#x60; is an affirmative &quot;no negative stock&quot;, while
    &#x60;&#x27;not-measured&#x27;&#x60; / &#x60;&#x27;errored&#x27;&#x60; / &#x60;&#x27;not-migrated&#x27;&#x60; carry NO &#x60;cellCount&#x60; at all. The two are
    different SHAPES, not the same shape with a different number (ATL-723; and PR #460, where
    &#x60;complete: true, gaps: []&#x60; was emitted for a run that measured nothing).
  - &#x60;sample&#x60; is bounded — 20 cells per org (applied inside the aggregate) and 200 per fleet run,
    sorted WORST-FIRST across the whole fleet before the cut, so truncation removes the least
    severe cells. &#x60;sampleTruncated&#x60; and a &#x60;gaps&#x60; entry disclose it. Every cell carries BOTH id
    spaces (Supabase uuid + Firestore id) plus the product name, so it is actionable without a
    lookup. &#x60;storeId: null&#x60; is a real cell whose movements carry no store (44 fleet-wide).
  - &#x60;byOrg&#x60; and &#x60;totalNegativeCells&#x60; are exact regardless of any sample cap: they come from the
    aggregate, not from the listed rows.

WHY ALWAYS-ON WHERE &#x60;ledgerCheck&#x60; IS OPT-IN: one aggregate per org, measured at 132 ms on the
fleet&#x27;s worst org (175k movements) and 3.2 s for the entire fleet — against the ledger check&#x27;s
~2.37M Firestore document reads. There is no cadence worth designing around a tenth of a second.

⛔ DETECTION ONLY, STRUCTURALLY. It issues one &#x60;rpc()&#x60; to a &#x60;STABLE&#x60; SQL function, inside which
Postgres refuses to execute any DML. Nothing here may id-diff or id-backfill &#x60;product_movements&#x60;
— live dual-write rows carry a NULL &#x60;firestore_id&#x60;, so an id-level backfill duplicates every
live movement and DOUBLES the org&#x27;s stock (ATL-707).

WHAT IT COUNTS: everything in the table. Soft-deleted movements, rows under soft-deleted
products/stores, and ATL-719 synthetic opening balances are all INCLUDED — this is the stock
Supabase actually holds, the same definition &#x60;product_stock_current&#x60; serves the product. The
&#x60;get_org_negative_stock&#x60; migration header records each exclusion considered and its measured
effect; excluding opening balances alone would take the fleet from 109 negative orgs to 114.

⚠️ AND A VERIFIED-DELETED PARENT IS NOT AUTOMATICALLY BENIGN. On the two measured orgs the
population was pre-migration deletions, which both stores hold. An operation deleted AFTER its
org was migrated, in an org that was not yet dual-writing (fleet-wide only from 2026-06-19),
lands in the same bucket and IS real loss — the frontend&#x27;s delete paths write reversal movement
docs rather than removing the originals. That is why the counterpart is measured per org rather
than assumed. See &#x60;movement-deleted-parent.util.ts&#x60;.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/recurring-spendings",
    alias: "RecurringSpendingsController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateRecurringSpendingDto,
      },
    ],
    response: RecurringSpendingFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/recurring-spendings",
    alias: "RecurringSpendingsController_findAll",
    requestFormat: "json",
    response: z.array(RecurringSpendingFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/recurring-spendings/:id",
    alias: "RecurringSpendingsController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RecurringSpendingFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/recurring-spendings/:id",
    alias: "RecurringSpendingsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateRecurringSpendingDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RecurringSpendingFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/recurring-spendings/:id",
    alias: "RecurringSpendingsController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/recurring-spendings/compare/:firestoreId",
    alias: "RecurringSpendingsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/reports/compare/daily-stats",
    alias: "ReportsController_compareDailyStats",
    description: `Fetches daily stats from both databases and compares to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "date",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/reports/compare/product-stats",
    alias: "ReportsController_compareProductStats",
    description: `Fetches product stats from both databases for a specific product and date to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "productId",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "date",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/reports/daily-stats",
    alias: "ReportsController_getDailyStats",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(DailyStatsResponseDto),
  },
  {
    method: "get",
    path: "/api/reports/financial",
    alias: "ReportsController_getFinancialReport",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: FinancialReportResponseDto,
  },
  {
    method: "get",
    path: "/api/reports/inventory",
    alias: "ReportsController_getInventoryReport",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: InventoryReportResponseDto,
  },
  {
    method: "post",
    path: "/api/reports/migration/bulk-compare",
    alias: "ReportsController_bulkCompareDailyStats",
    description: `Compares daily stats between Firestore and Supabase for a date range across multiple stores`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkCompareRequestDto,
      },
    ],
    response: BulkCompareResultDto,
  },
  {
    method: "get",
    path: "/api/reports/migration/dual-write-organizations",
    alias: "ReportsController_getDualWriteOrganizations",
    description: `Lists all organizations that have dual-write mode enabled or have migration activity`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "searchTerm",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: DualWriteOrganizationsResponseDto,
  },
  {
    method: "get",
    path: "/api/reports/migration/health/:organizationId",
    alias: "ReportsController_getOrganizationSyncHealth",
    description: `Compares all stores in an organization across a date range to assess overall sync health`,
    requestFormat: "json",
    parameters: [
      {
        name: "organizationId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: BulkCompareResultDto,
  },
  {
    method: "get",
    path: "/api/reports/product-report",
    alias: "ReportsController_getProductReport",
    description: `Array of flat per-date docs { ...productsKeyedById, date, store } mirroring the Firestore dailyProductStats productReport docs. Built from sales/writeoffs/supplies source rows, so custom/delivery cart lines are included (unlike /reports/product-stats).`,
    requestFormat: "json",
    parameters: [
      {
        name: "startDate",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(z.object({}).partial().passthrough()),
  },
  {
    method: "get",
    path: "/api/reports/product-stats",
    alias: "ReportsController_getProductStatsForDays",
    requestFormat: "json",
    parameters: [
      {
        name: "startDate",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/reports/sales",
    alias: "ReportsController_getSalesReport",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: SalesReportResponseDto,
  },
  {
    method: "get",
    path: "/api/reports/shift",
    alias: "ReportsController_getShiftReport",
    description: `ATL-652: reconstructs the legacy Firestore dailyStats flat shift keys (&lt;wallet&gt;StartAmount, &lt;pm&gt;SalesAmount, &lt;key&gt;EndAmount, &lt;key&gt;SystemEndAmount, …) from Supabase source tables so the Shift/Смена screen works in Supabase read mode.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(ShiftReportResponseDto),
  },
  {
    method: "post",
    path: "/api/return-reasons",
    alias: "ReturnReasonsController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateReturnReasonDto,
      },
    ],
    response: ReturnReasonFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/return-reasons",
    alias: "ReturnReasonsController_findAll",
    requestFormat: "json",
    response: z.array(ReturnReasonFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/return-reasons/:id",
    alias: "ReturnReasonsController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ReturnReasonFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/return-reasons/:id",
    alias: "ReturnReasonsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateReturnReasonDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ReturnReasonFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/return-reasons/:id",
    alias: "ReturnReasonsController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/return-reasons/compare/:firestoreId",
    alias: "ReturnReasonsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/revisions",
    alias: "RevisionsController_createRevision",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateRevisionDto,
      },
    ],
    response: RevisionFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/revisions",
    alias: "RevisionsController_getRevisions",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "includeDeleted",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(RevisionFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/revisions/:id",
    alias: "RevisionsController_getRevisionById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RevisionFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/revisions/:id",
    alias: "RevisionsController_updateRevision",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateRevisionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RevisionFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/revisions/:id",
    alias: "RevisionsController_deleteRevision",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/revisions/compare/:firestoreId",
    alias: "RevisionsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        exists: z.boolean(),
        supabaseData: z.object({}).partial().passthrough().nullable(),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/revisions/window",
    alias: "RevisionsController_getRevisionsWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "includeDeleted",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/roles",
    alias: "RolesController_createRole",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateRoleDto,
      },
    ],
    response: RoleFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/roles",
    alias: "RolesController_getRoles",
    requestFormat: "json",
    response: z.array(RoleFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/roles/:id",
    alias: "RolesController_getRoleById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RoleFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/roles/:id",
    alias: "RolesController_updateRole",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateRoleDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RoleFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/roles/:id",
    alias: "RolesController_deleteRole",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/roles/compare/:firestoreId",
    alias: "RolesController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/sale-settings",
    alias: "SaleSettingsController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSaleSettingsDto,
      },
    ],
    response: SaleSettingsFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/sale-settings",
    alias: "SaleSettingsController_findAll",
    requestFormat: "json",
    response: z.array(SaleSettingsFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/sale-settings/:id",
    alias: "SaleSettingsController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SaleSettingsFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/sale-settings/:id",
    alias: "SaleSettingsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSaleSettingsDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SaleSettingsFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/sale-settings/:id",
    alias: "SaleSettingsController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/sale-settings/compare/:storeId",
    alias: "SaleSettingsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/sales",
    alias: "SalesController_createSale",
    description: `Creates a sale for products, services, or mixed. Sale type is auto-detected if not provided.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSaleDto,
      },
    ],
    response: SaleFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/sales",
    alias: "SalesController_getSales",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "saleType",
        type: "Query",
        schema: z.enum(["product", "service", "mixed"]).optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "includeDeleted",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "clientId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(SaleFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/sales-operator/lost-delete",
    alias: "SalesOperatorController_deleteLostSale",
    description: `For a sale Firestore deleted (document gone, sale + deleteSale in its movement ledger) whose Supabase delete never arrived. Buries its money legs and reverses its client effects exactly as the normal delete, but writes NO stock movement; refused whole when, on any cell, the normal reversal would bring Supabase closer to Firestore. Send &#x60;dryRun: true&#x60; first, then apply with its &#x60;digest&#x60; as &#x60;expectedDigest&#x60;.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LostSaleDeletionDto,
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/sales/:id",
    alias: "SalesController_getSaleById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SaleFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/sales/:id",
    alias: "SalesController_updateSale",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSaleDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SaleFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/sales/:id",
    alias: "SalesController_deleteSale",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "post",
    path: "/api/sales/:id/payment",
    alias: "SalesController_markSalePaid",
    description: `ATL-1445 stage 2b. Books the income transaction keyed by &#x60;transactionId&#x60; (the Firestore transactions document id), links it to the sale and writes the payment method, in one database transaction. A replay returns status &#x60;replayed&#x60; and writes nothing. Client effects are NOT applied: the sale create already applied them.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MarkSalePaidDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: MarkSalePaidResponseDto,
    errors: [
      {
        status: 404,
        description: `No such sale in this organization (yet)`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The sale is deleted or already paid by another payment, or the transaction id names a deleted leg or another document&#x27;s leg`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/sales/:id/returns",
    alias: "SalesController_getSaleReturns",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(ReturnFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/sales/:id/returns",
    alias: "SalesController_createSaleReturn",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSaleReturnDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ReturnFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/sales/:id/returns/:returnId",
    alias: "SalesController_getSaleReturn",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "returnId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ReturnFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/sales/:id/returns/:returnId",
    alias: "SalesController_deleteSaleReturn",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "returnId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/sales/compare/:firestoreId",
    alias: "SalesController_compareSale",
    description: `Fetches sale from both databases and compares all fields to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-log-session",
        type: "Header",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/sales/returns/compare/:firestoreId",
    alias: "SalesController_compareReturn",
    description: `Fetches return from both databases and compares all fields to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/sales/window",
    alias: "SalesController_getSalesWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "saleType",
        type: "Query",
        schema: z.enum(["product", "service", "mixed"]).optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "includeDeleted",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "clientId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/saved-carts",
    alias: "SavedCartsController_createSavedCart",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSavedCartDto,
      },
    ],
    response: SavedCartFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/saved-carts",
    alias: "SavedCartsController_getSavedCarts",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(SavedCartFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/saved-carts/:id",
    alias: "SavedCartsController_getSavedCartById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SavedCartFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/saved-carts/:id",
    alias: "SavedCartsController_deleteSavedCart",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "patch",
    path: "/api/saved-carts/:id",
    alias: "SavedCartsController_updateSavedCart",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSavedCartDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SavedCartFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/saved-carts/compare/:firestoreId",
    alias: "SavedCartsController_compareWithFirestore",
    description: `Fetches saved cart from both databases and compares all fields to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        firestoreId: z.string(),
        supabaseId: z.string().nullable(),
        foundInSupabase: z.boolean(),
        foundInFirestore: z.boolean(),
        matches: z.boolean(),
        differences: z.array(z.any()),
        warnings: z.array(z.any()),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/saved-carts/firestore/:firestoreId",
    alias: "SavedCartsController_deleteByFirestoreId",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/scheduled-tasks/sales-count-check",
    alias: "ScheduledTasksController_manualSalesCountCheck",
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/settings",
    alias: "SettingsController_getAllSettings",
    requestFormat: "json",
    response: AllConstantsResponseDto,
  },
  {
    method: "put",
    path: "/api/settings/constants/:key",
    alias: "SettingsController_upsertOrganizationConstant",
    description: `ATL-1038 — the Supabase leg of the flat &#x60;constants/&lt;key&gt;&#x60; documents.

&#x60;printChecks&#x60; had no write path here at all: &#x60;updatePrintChecks&#x60; wrote Firestore and returned,
so a merchant toggling receipt printing changed one store and not the other, and a fleet-wide
correction on 2026-08-23 grew back within two months. The row lives on &#x60;organization_constants&#x60;
and NOT on &#x60;organizations.print_checks&#x60; — that column does not exist, which is why the
&#x60;PATCH /organizations/:id/print-checks&#x60; route next door has never once written anything
(ATL-861).

PUT, not PATCH: the body is the complete document, stored verbatim.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ document: z.object({}).partial().passthrough() })
          .passthrough(),
      },
      {
        name: "key",
        type: "Path",
        schema: z.enum(["floatingMenu", "printChecks", "stickerHeight"]),
      },
    ],
    response: OrganizationConstantResponseDto,
  },
  {
    method: "post",
    path: "/api/spending-categories",
    alias: "SpendingCategoriesController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSpendingCategoryDto,
      },
    ],
    response: SpendingCategoryFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/spending-categories",
    alias: "SpendingCategoriesController_findAll",
    requestFormat: "json",
    response: z.array(SpendingCategoryFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/spending-categories/:id",
    alias: "SpendingCategoriesController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SpendingCategoryFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/spending-categories/:id",
    alias: "SpendingCategoriesController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSpendingCategoryDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SpendingCategoryFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/spending-categories/:id",
    alias: "SpendingCategoriesController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/spending-categories/compare/:firestoreId",
    alias: "SpendingCategoriesController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/spendings",
    alias: "SpendingsController_createSpending",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSpendingDto,
      },
    ],
    response: SpendingFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/spendings",
    alias: "SpendingsController_getSpendings",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(SpendingFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/spendings/:id",
    alias: "SpendingsController_getSpendingById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SpendingFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/spendings/:id",
    alias: "SpendingsController_deleteSpending",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/spendings/compare/:firestoreId",
    alias: "SpendingsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        firestoreId: z.string(),
        supabaseId: z.string().nullable(),
        foundInSupabase: z.boolean(),
        foundInFirestore: z.boolean(),
        matches: z.boolean(),
        differences: z.array(z.any()),
        warnings: z.array(z.any()),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/spendings/window",
    alias: "SpendingsController_getSpendingsWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/stores",
    alias: "StoresController_createStore",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateStoreDto,
      },
    ],
    response: StoreFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/stores",
    alias: "StoresController_getStores",
    requestFormat: "json",
    response: z.array(StoreFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/stores/:id",
    alias: "StoresController_getStoreById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StoreFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/stores/:id",
    alias: "StoresController_updateStore",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateStoreDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StoreFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/stores/:id",
    alias: "StoresController_patchStore",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateStoreDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StoreFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/stores/:id",
    alias: "StoresController_deleteStore",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/stores/compare/:firestoreId",
    alias: "StoresController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: ComparisonResultDto,
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/suppliers",
    alias: "SuppliersController_createSupplier",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSupplierDto,
      },
    ],
    response: SupplierFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/suppliers",
    alias: "SuppliersController_getSuppliers",
    requestFormat: "json",
    response: z.array(SupplierFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/suppliers/:id",
    alias: "SuppliersController_getSupplierById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SupplierFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/suppliers/:id",
    alias: "SuppliersController_updateSupplier",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSupplierDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SupplierFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/suppliers/:id",
    alias: "SuppliersController_deleteSupplier",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/suppliers/compare/:firestoreId",
    alias: "SuppliersController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: ComparisonResultDto,
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/supplies",
    alias: "SuppliesController_createSupply",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSupplyDto,
      },
    ],
    response: SupplyFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/supplies",
    alias: "SuppliesController_getSupplies",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "firestoreId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "supplierId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(SupplyFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/supplies/:id",
    alias: "SuppliesController_getSupplyById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SupplyFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/supplies/:id",
    alias: "SuppliesController_updateSupply",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSupplyDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SupplyFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/supplies/:id",
    alias: "SuppliesController_deleteSupply",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/supplies/compare/:firestoreId",
    alias: "SuppliesController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "logSessionId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/supplies/orders/active",
    alias: "SuppliesController_getActiveSupplyOrders",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(SupplyOrderFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/supplies/window",
    alias: "SuppliesController_getSuppliesWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "firestoreId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "supplierId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/supply-orders",
    alias: "SupplyOrdersController_createSupplyOrder",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSupplyOrderDto,
      },
    ],
    response: SupplyOrderFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/supply-orders",
    alias: "SupplyOrdersController_getSupplyOrders",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "supplierId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "isCompleted",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(SupplyOrderFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/supply-orders/:id",
    alias: "SupplyOrdersController_getSupplyOrderById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SupplyOrderFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/supply-orders/:id",
    alias: "SupplyOrdersController_updateSupplyOrder",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSupplyOrderDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SupplyOrderFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/supply-orders/:id",
    alias: "SupplyOrdersController_deleteSupplyOrder",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/supply-orders/compare/:firestoreId",
    alias: "SupplyOrdersController_compareWithFirestore",
    description: `Fetches supply order from both databases and compares all fields to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        firestoreId: z.string(),
        supabaseId: z.string().nullable(),
        foundInSupabase: z.boolean(),
        foundInFirestore: z.boolean(),
        matches: z.boolean(),
        differences: z.array(z.any()),
        warnings: z.array(z.any()),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/support/actions/:id",
    alias: "SupportController_getAction",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/support/actions/:id/dismiss",
    alias: "SupportController_dismissAction",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/support/actions/:id/items/:index/applied",
    alias: "SupportController_recordActionItemApplied",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ firestoreId: z.string() }).partial().passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "index",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/support/conversations",
    alias: "SupportController_startConversation",
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/support/conversations/:id/messages",
    alias: "SupportController_getMessages",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "before",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/support/conversations/:id/messages",
    alias: "SupportController_sendMessage",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendMessageDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/support/conversations/:id/read",
    alias: "SupportController_markRead",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/support/operator/website-org",
    alias: "SupportOperatorController_ensureWebsiteOrg",
    description: `Idempotent. Supabase-side only, no owner user, no signup side effects (growth milestones are recorded as skipped). Send &#x60;dryRun: true&#x60; first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ dryRun: z.boolean() }).passthrough(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/support/public/web/messages",
    alias: "SupportPublicController_send",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WebVisitorMessageDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/support/public/web/messages",
    alias: "SupportPublicController_list",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/support/team/contacts/:id",
    alias: "SupportController_teamContact",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/support/team/conversations",
    alias: "SupportController_teamList",
    requestFormat: "json",
    parameters: [
      {
        name: "status",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "assignee",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "org",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "contact",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/support/team/conversations/:id",
    alias: "SupportController_teamThread",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/api/support/team/conversations/:id",
    alias: "SupportController_teamUpdate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateConversationDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/support/team/conversations/:id/actions",
    alias: "SupportController_teamProposeAction",
    description: `ATL-1323 — propose products to the customer as a card they save one by one in the app.

Creates a PROPOSAL only. Nothing in the organization&#x27;s catalogue, stock or money is written by
this route or by anything it calls; see &#x60;SupportActionsService&#x60;.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProposeActionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/support/team/conversations/:id/actions",
    alias: "SupportController_teamListActions",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/support/team/conversations/:id/reply",
    alias: "SupportController_teamReply",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TeamReplyDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/support/team/diagnostics",
    alias: "SupportController_triggerDiagnostics",
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/support/team/notify-sweep",
    alias: "SupportController_notifySweep",
    description: `Drive one notify sweep from OUTSIDE the process.

ATL-995 — this is the reliable trigger for the whole support notification pipeline, and it
exists because an in-process timer cannot be trusted here. &#x60;atlas-backend&#x60; runs on Cloud Run
with the default CPU allocation, so CPU is withdrawn between requests and the &#x60;@Cron&#x60; sweep
stops ticking while the service is idle; measured, that delayed 13 of 42 customer messages by
over 30 minutes and one by 20.8 hours. A scheduler HTTP call IS a request, so the sweep is
guaranteed CPU for as long as it runs.

Wire it to Cloud Scheduler at &#x60;* * * * *&#x60; (see docs/technical/support-notifications.md).
Safe to call as often as you like: it recovers missed arms, then fires only what the atomic
claim hands it, so concurrent callers cannot double-send.`,
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/support/unread",
    alias: "SupportController_unread",
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/transactions",
    alias: "TransactionsController_createTransaction",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateTransactionDto,
      },
    ],
    response: TransactionFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/transactions",
    alias: "TransactionsController_getTransactions",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z.enum(["income", "expense", "transfer"]).optional(),
      },
      {
        name: "walletId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "linkedDocId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "linkedDocType",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "externalDestinationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(TransactionFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/transactions/:id",
    alias: "TransactionsController_getTransactionById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: TransactionFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/transactions/:id",
    alias: "TransactionsController_deleteTransaction",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/transactions/compare/:firestoreId",
    alias: "TransactionsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/transactions/wallet/:walletId/balance",
    alias: "TransactionsController_getWalletBalance",
    requestFormat: "json",
    parameters: [
      {
        name: "walletId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: WalletBalanceResponseDto,
  },
  {
    method: "get",
    path: "/api/transactions/window",
    alias: "TransactionsController_getTransactionsWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "walletId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "linkedDocId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "linkedDocType",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "externalDestinationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/transfers",
    alias: "TransfersController_createTransfer",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateTransferDto,
      },
    ],
    response: TransferFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/transfers",
    alias: "TransfersController_getTransfers",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(TransferFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/transfers/:id",
    alias: "TransfersController_getTransferById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: TransferFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/transfers/:id",
    alias: "TransfersController_deleteTransfer",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/transfers/compare/:firestoreId",
    alias: "TransfersController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        exists: z.boolean(),
        supabaseData: z.object({}).partial().passthrough().nullable(),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/transfers/window",
    alias: "TransfersController_getTransfersWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/updates",
    alias: "UpdatesController_createUpdate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateUpdateDto,
      },
    ],
    response: UpdateFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/updates",
    alias: "UpdatesController_getUpdates",
    requestFormat: "json",
    parameters: [
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(UpdateFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/users",
    alias: "UsersController_getUsers",
    requestFormat: "json",
    parameters: [
      {
        name: "active",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(UserFirestoreResponse),
  },
  {
    method: "post",
    path: "/api/users",
    alias: "UsersController_createUser",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateUserDto,
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/users/:id",
    alias: "UsersController_getUserById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/users/:id",
    alias: "UsersController_updateUser",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateUserDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/users/:id",
    alias: "UsersController_deleteUser",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/users/:id/email",
    alias: "UsersController_updateEmail",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string() }).passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/users/:id/enable",
    alias: "UsersController_enableUser",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/users/:id/password",
    alias: "UsersController_updatePassword",
    description: `ATL-1951 — records that the user&#x27;s password was changed (audit stamp). It does NOT set the
password: the &#x60;updateUserInAuth&#x60; callable does, live, and this route is that write&#x27;s mirror,
which a device outbox may replay hours later. The body is ignored (see &#x60;UpdatePasswordDto&#x60;).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ password: z.string() }).partial().passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "put",
    path: "/api/users/:id/push-token",
    alias: "UsersController_updatePushToken",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ pushToken: z.string() }).passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/users/:id/push-token/remove",
    alias: "UsersController_removePushToken",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/users/:id/user-admin-authorization/:action",
    alias: "UsersController_authorizeUserAdminAction",
    description: `ATL-1138 — the user-admin callables (&#x60;updateUserInAuth&#x60;, &#x60;disableUser&#x60;, &#x60;enableUser&#x60;) ask this
before they write Firebase Auth, and act on the uid it returns. The refusals are THE SAME ones
the mutation route for that action makes — &#x60;PATCH :id/email|password&#x60;, &#x60;DELETE :id&#x60;,
&#x60;PATCH :id/enable&#x60; — through the same helpers, so the callable and the dual-write behind it
cannot disagree about who may do what. Read-only: it changes nothing, and the app&#x27;s dual-write
remains the single backend write (performing the write here would make the app&#x27;s follow-up
dual-write a second one, and a repeated soft-delete is a 404).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "action",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ firebaseUid: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/users/compare/:firestoreId",
    alias: "UsersController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: ComparisonResultDto,
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/users/heal-from-firestore",
    alias: "UsersController_healUserFromFirestore",
    description: `Authenticated by the &#x60;x-migration-token&#x60; header, NOT by the bearer token this API otherwise uses — a Firebase session is not a credential here. Creates only; refuses if any row already exists for the uid. Send &#x60;dryRun: true&#x60; first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: HealUserFromFirestoreDto,
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: HealUserResponseDto,
  },
  {
    method: "post",
    path: "/api/users/me/sync-from-firestore",
    alias: "UsersController_syncSelfFromFirestore",
    requestFormat: "json",
    response: SyncUserResponseDto,
  },
  {
    method: "get",
    path: "/api/users/me/user-admin-scope",
    alias: "UsersController_getUserAdminScope",
    description: `ATL-1138 — &#x60;createNewInternalUser&#x60; asks this before creating a Firebase Auth user: it answers
only to an &#x60;editUsers&#x60; holder, and the callable refuses an &#x60;organizationId&#x60; that is not one of
these two ids.`,
    requestFormat: "json",
    response: UserAdminScopeDto,
  },
  {
    method: "get",
    path: "/api/v1/acceptances",
    alias: "ApiV1Controller_listAcceptances",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().gte(1).lte(200).optional().default(50),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().gte(0).optional().default(0),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z
          .string()
          .regex(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})$/
          )
          .optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z
          .string()
          .regex(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})$/
          )
          .optional(),
      },
      {
        name: "supplierId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
    ],
    response: ApiV1AcceptancesPageDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/acceptances/drafts",
    alias: "ApiV1Controller_createAcceptanceDraft",
    description: `Lines name products, quantities and optionally a purchase price. An acceptance is recorded against ONE supplier, taken from the products (&#x60;supplierID&#x60; in GET /v1/products): every line must share one supplier, else 422 &#x60;mixed_suppliers&#x60; / &#x60;no_supplier&#x60; — propose one draft per supplier. Same Idempotency-Key rules as sale drafts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AcceptanceDraftRequestDto,
      },
      {
        name: "Idempotency-Key",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: ApiDraftCreatedDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `&#x60;invalid_draft&#x60; (every problem listed) or &#x60;idempotency_key_reused&#x60;`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/drafts",
    alias: "ApiV1Controller_listDrafts",
    requestFormat: "json",
    parameters: [
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["proposed", "expired", "applied", "dismissed"])
          .optional(),
      },
      {
        name: "kind",
        type: "Query",
        schema: z.enum(["sale", "acceptance"]).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().gte(1).lte(200).optional().default(50),
      },
    ],
    response: ApiDraftsDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/drafts/:id",
    alias: "ApiV1Controller_getDraft",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ApiDraftDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No such draft in this organization`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/products",
    alias: "ApiV1Controller_listProducts",
    description: `&#x60;storeStock&#x60; (quantity on hand) is included only for a token that may read stock (&#x60;read:stock&#x60; or &#x60;read&#x60;); a &#x60;read:products&#x60; token gets the catalogue without it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().gte(1).lte(200).optional().default(50),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().gte(0).optional().default(0),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().max(100).optional(),
      },
      {
        name: "categoryId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
    ],
    response: ApiV1ProductsPageDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/products/:id",
    alias: "ApiV1Controller_getProduct",
    description: `&#x60;storeStock&#x60; follows the same scope rule as GET /v1/products.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
      },
    ],
    response: ProductFirestoreResponse,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No such live product in this organization`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/reports/revenue-by-day",
    alias: "ApiV1Controller_revenueByDay",
    requestFormat: "json",
    parameters: [
      {
        name: "startDate",
        type: "Query",
        schema: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
    ],
    response: ApiV1RevenueByDayDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/reports/sales-summary",
    alias: "ApiV1Controller_salesSummary",
    description: `Totals are revenue-by-day summed (the app’s stats screens). Best sellers come from the product report over the instants of the same trading days, per group of stores sharing a timezone and cutoff (&#x60;topProductsWindows&#x60;), with the sales columns only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "startDate",
        type: "Query",
        schema: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
      {
        name: "topLimit",
        type: "Query",
        schema: z.number().gte(1).lte(50).optional().default(10),
      },
    ],
    response: ApiV1SalesSummaryDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `&#x60;store_not_found&#x60;: storeId names no store`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/reports/stock-balances",
    alias: "ApiV1Controller_stockBalances",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().gte(1).lte(200).optional().default(50),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().gte(0).optional().default(0),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().max(100).optional(),
      },
    ],
    response: ApiV1StockBalancesPageDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `&#x60;store_not_found&#x60;: storeId names no store`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/reports/stock-balances/:id",
    alias: "ApiV1Controller_stockBalance",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
    ],
    response: ApiV1StockBalanceItemDto,
    errors: [
      {
        status: 400,
        description: `&#x60;product_is_service&#x60;: a service carries no stock`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No such live product, or &#x60;store_not_found&#x60;: storeId names no store`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/reports/top-products",
    alias: "ApiV1Controller_topProducts",
    requestFormat: "json",
    parameters: [
      {
        name: "from",
        type: "Query",
        schema: z
          .string()
          .regex(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})$/
          ),
      },
      {
        name: "to",
        type: "Query",
        schema: z
          .string()
          .regex(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})$/
          ),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
      {
        name: "sortBy",
        type: "Query",
        schema: z
          .enum([
            "saleQuantity",
            "saleAmount",
            "writeOffQuantity",
            "writeOffAmount",
            "supplyQuantity",
            "supplyAmount",
            "margin",
            "profit",
            "checkQuantity",
            "purchaseAmount",
            "packageAmount",
          ])
          .optional()
          .default("saleAmount"),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().gte(1).lte(100).optional().default(20),
      },
    ],
    response: ApiV1TopProductsDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/sales",
    alias: "ApiV1Controller_listSales",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().gte(1).lte(200).optional().default(50),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().gte(0).optional().default(0),
      },
      {
        name: "storeId",
        type: "Query",
        schema: z.string().max(64).optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z
          .string()
          .regex(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})$/
          )
          .optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z
          .string()
          .regex(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})$/
          )
          .optional(),
      },
    ],
    response: ApiV1SalesPageDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/sales/:id",
    alias: "ApiV1Controller_getSale",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
      },
    ],
    response: SaleFirestoreResponse,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No such live sale in this organization`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/sales/drafts",
    alias: "ApiV1Controller_createSaleDraft",
    description: `Lines name products and quantities only; prices, discounts, client and payment are the merchant’s, at checkout. Fields outside the contract are refused, with a reason, not ignored. Requires an Idempotency-Key: the same key and body return the same draft (200), a different body with a used key is 422 idempotency_key_reused.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SaleDraftRequestDto,
      },
      {
        name: "Idempotency-Key",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: ApiDraftCreatedDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `&#x60;invalid_draft&#x60; (every problem listed) or &#x60;idempotency_key_reused&#x60;`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/stores",
    alias: "ApiV1Controller_listStores",
    requestFormat: "json",
    response: ApiV1StoresDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/whoami",
    alias: "ApiV1Controller_whoami",
    requestFormat: "json",
    response: ApiV1WhoamiDto,
    errors: [
      {
        status: 400,
        description: `Invalid query parameters, &#x60;invalid_range&#x60;, &#x60;range_too_long&#x60;, or &#x60;result_too_large&#x60; (revenue-by-day: narrow the range or pass one storeId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing, unknown, revoked or expired token (&#x60;code&#x60; says which)`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `&#x60;insufficient_scope&#x60;, &#x60;token_owner_not_admin&#x60;, or &#x60;organization_not_on_supabase&#x60; (the API serves only organizations whose data has moved to the new database)`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Organization request limit reached; see Retry-After`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Could not confirm the organization’s database; retry`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/wallets",
    alias: "WalletsController_createWallet",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateWalletDto,
      },
    ],
    response: WalletFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/wallets",
    alias: "WalletsController_getWallets",
    requestFormat: "json",
    response: z.array(WalletFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/wallets/:id",
    alias: "WalletsController_getWalletById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WalletFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/wallets/:id",
    alias: "WalletsController_updateWallet",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateWalletDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WalletFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/wallets/:id",
    alias: "WalletsController_deleteWallet",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/wallets/compare/:firestoreId",
    alias: "WalletsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/webhooks/paddle",
    alias: "WebhooksController_handlePaddleWebhook",
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/webhooks/revenuecat",
    alias: "WebhooksController_handleRevenueCatWebhook",
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/working-hours",
    alias: "WorkingHoursController_createWorkingHours",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateWorkingHoursDto,
      },
    ],
    response: WorkingHoursFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/working-hours",
    alias: "WorkingHoursController_getWorkingHours",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "employeeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(WorkingHoursFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/working-hours/:id",
    alias: "WorkingHoursController_getWorkingHoursById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WorkingHoursFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/working-hours/:id",
    alias: "WorkingHoursController_updateWorkingHours",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WorkingHoursFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/working-hours/:id",
    alias: "WorkingHoursController_deleteWorkingHours",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/working-hours/:id/payment",
    alias: "WorkingHoursController_createWorkingHoursPayment",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/api/working-hours/:id/payment/:transactionId",
    alias: "WorkingHoursController_deleteWorkingHoursPayment",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "transactionId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/working-hours/bulk",
    alias: "WorkingHoursController_createAutomaticWorkingHours",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/working-hours/compare/:firestoreId",
    alias: "WorkingHoursController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/working-hours/firestore/:firestoreId",
    alias: "WorkingHoursController_deleteByFirestoreId",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }).partial().passthrough(),
  },
  {
    method: "patch",
    path: "/api/working-hours/firestore/:firestoreId",
    alias: "WorkingHoursController_updateByFirestoreId",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WorkingHoursFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/working-hours/window",
    alias: "WorkingHoursController_getWorkingHoursWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "employeeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/write-off-reasons",
    alias: "WriteOffReasonsController_create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateWriteOffReasonDto,
      },
    ],
    response: WriteOffReasonFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/write-off-reasons",
    alias: "WriteOffReasonsController_findAll",
    requestFormat: "json",
    response: z.array(WriteOffReasonFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/write-off-reasons/:id",
    alias: "WriteOffReasonsController_findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WriteOffReasonFirestoreResponse,
  },
  {
    method: "patch",
    path: "/api/write-off-reasons/:id",
    alias: "WriteOffReasonsController_update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateWriteOffReasonDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WriteOffReasonFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/write-off-reasons/:id",
    alias: "WriteOffReasonsController_remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/api/write-off-reasons/compare/:firestoreId",
    alias: "WriteOffReasonsController_compareWithFirestore",
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/writeoffs",
    alias: "WriteoffsController_createWriteoff",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateWriteoffDto,
      },
    ],
    response: WriteoffFirestoreResponse,
  },
  {
    method: "get",
    path: "/api/writeoffs",
    alias: "WriteoffsController_getWriteoffs",
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "productId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(WriteoffFirestoreResponse),
  },
  {
    method: "get",
    path: "/api/writeoffs/:id",
    alias: "WriteoffsController_getWriteoffById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WriteoffFirestoreResponse,
  },
  {
    method: "delete",
    path: "/api/writeoffs/:id",
    alias: "WriteoffsController_deleteWriteoff",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/writeoffs/compare/:firestoreId",
    alias: "WriteoffsController_compareWithFirestore",
    description: `Fetches writeoff from both databases and compares all fields to identify sync issues`,
    requestFormat: "json",
    parameters: [
      {
        name: "firestoreId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "organizationId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "x-migration-token",
        type: "Header",
        schema: z.string().optional(),
      },
    ],
    response: ComparisonResultDto,
    errors: [
      {
        status: 401,
        description: `Unauthenticated, or invalid x-migration-token`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Authenticated, but the target is another tenant`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/writeoffs/window",
    alias: "WriteoffsController_getWriteoffsWindow",
    description: `ATL-1477 snapshot window: the rows of this list whose id is in [idFrom, idTo), at most 200, and &#x60;total&#x60; — the exact number of rows the window holds, counted in the same SQL statement. &#x60;rows.length &#x3D;&#x3D;&#x3D; total&#x60; proves the window complete; otherwise split it. Returns { total, rows, idFrom, idTo }.`,
    requestFormat: "json",
    parameters: [
      {
        name: "storeId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "startDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "endDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "productId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idFrom",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "idTo",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
